import Phaser from 'phaser';
import { loadGen3 } from '../data/dex';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { seedStore } from '../state/seed';
import { teamFromParty, trainerPokemonSet, wildPokemonAsync, SPECIES_TO_DEX } from '../battle/teamFromParty';
import { ENCOUNTER_TABLES } from '../data/encounters';
import { RoamerSystem, type Roamer } from '../render/roamers';
import {
  facingTowardTile,
  planTrainerStaging,
  planWildStaging,
  worldToScreen,
} from '../render/duelStage';
import { mountDuelPinch, type DuelPinchHandle } from '../render/duelPinch';
import {
  applyOwSkin,
  hdPreferred,
  setHdPreferred,
  speciesFromKey,
} from '../render/duelHdSkin';
import {
  mountDuelHdToggle,
  type DuelHdToggleHandle,
} from '../render/duelHdToggle';
import {
  DuelSceneFilterPipeline,
  DUEL_FILTER_PIPELINE_KEY,
} from '../render/duelSceneFilter';
import {
  mountDuelFilterCycler,
  filterAlgoPref,
  filterSmoothPref,
  type DuelFilterCyclerHandle,
} from '../render/duelFilterCycler';
import { DuelStage } from '../render/duel';
import { WorldBattleHost } from './worldBattleHost';

/** M2 gate — in-world duels replace the frozen-modal battle for roam
 *  engagements when true. Flipped ON 2026-08-03 (owner call, "let's
 *  see it") after the dogfood loop verified the full cycle: bump →
 *  startle-hop → punch-in → battle → EXP → restore. Classic runs and
 *  trainer battles are untouched either way; flip back to false to
 *  re-gate roam engagements onto the frozen-modal path. */
const IN_WORLD_DUEL_ENABLED = true;
/** HD (xBRZ-baked) battler art in duels — TESTING-PHASE taste gate
 *  (owner ask 2026-08-03: "hoping we can compare with the upscaled at
 *  4x", live at duel zoom). Gates the .hd4 sheet preloads, the intro
 *  skin apply, and the in-duel GBA⇄HD toggle pill. A DESIGN.md Zone 3
 *  doctrine exception is PENDING the owner's ruling — duel-camera
 *  scope only; flip false to kill everything at once. */
const DUEL_HD_BATTLERS_ENABLED = false; // owner ruling 2026-08-04: rendering experiments off — combat stays plain GBA (no HD swap / toggle pill) until combat is decided
/** Whole-scene GPU smoothing filter for duels — TESTING-PHASE taste gate
 *  (owner ask 2026-08-03: filter the entire 5x scene, cycle styles live to
 *  find a look). Supersedes the per-sprite HD swap: when true the filter is
 *  the mechanism (it smooths tiles + sprites + spectators uniformly), the
 *  sprite HD swap stays OFF, and the cycler pill replaces the HD pill. Flip
 *  false to fall back to the sprite-swap path (or off entirely). */
const DUEL_SCENE_FILTER_ENABLED = false; // owner ruling 2026-08-04: no whole-scene/world filter (live or baked) — good experiment, not the look
import { getDialogue } from '../data/dialogue';
import { SPRITE_MANIFEST } from '../data/spriteManifest';
import {
  dispatchScript,
  HEAL_SPOT_SCRIPT_REFS,
  type ScriptCtx,
  type ScriptHostScene,
  type NpcWalkArgs,
  type NpcWalkHandle,
} from '../scripts/scriptRunner';
import { FLAGS } from '../scripts/flagRegistry';
import { expandWaypoints, playerWalkAllowed } from '../scripts/npcWalkPath';
import {
  repairStrandedLabPostPick,
  getLabSceneState,
  setLabSceneState,
  LAB_SCENE_STATES,
} from '../scripts/labScene';
import {
  fledRivalRearmTile,
  RIVAL_LOCAL_ID,
  VAR_RIVAL_LAST_TILE,
} from '../scripts/handlers/rivalLab';
import {
  enterBracketRecall,
  exitBracketRecall,
} from '../scripts/bracketRecall';
import { findTriggerAtTile } from '../scripts/coordEvents';
import { facingFromMovementType, frameForFacing, frontTileOf, walkCycleFrame } from '../scripts/npcFacing';
import { DRAFT_POOLS } from '../data/draftPools';
import { rollOffers } from '../state/draftRoll';
import { getTrainer, type TrainerData } from '../data/trainerParties';
import type { BattleSetup } from '../battle/types';
import { audio } from '../systems/audio';
import { MAP_MUSIC } from '../data/audioMap';
import { intentRouter } from '../input/intentRouter';
import {
  mountJoystick,
  TAP_PASSTHROUGH_EVENT,
  type JoystickHandle,
  type TapPassthroughPayload,
} from '../input/joystick';
import { mountDialogueOverlay, type DialogueOverlayHandle } from '../ui/dialogueOverlay';
import { mountPartyOrbs, type PartyOrbsHandle } from '../ui/PartyOrbs';
import { mountPokeballMenu, type PokeballMenuHandle } from '../ui/pokeballMenu';
import { Z_ROUTE_PLACARD } from '../ui/zLayers';
import {
  GLASS_FILL_THIN,
  GLASS_FILL_MID,
  GLASS_FILL_THICK,
  SPECULAR_EDGE,
  SPECULAR_EDGE_EMPHASIS,
  TEXT_PRIMARY,
  ERR_RED,
} from '../styles/phaserPalette';
import { Follower, FOLLOWER_SPRITE_CREATED_EVENT } from '../render/follower';
import { getControlMode, shouldShowClassicDpad, type ControlMode } from '../state/controlMode';
import {
  PathfinderManager,
  buildPathfinderGrid,
  type PathArrivalIntent,
  type TileXY,
} from '../input/pathfinder';
import { buildMegaMap, preloadMegaMap, type World } from '../render/worldLoader';
import {
  crossIntoRegion,
  regionAt,
  type Region,
  type RegionNpc,
  type RegionSign,
  type RegionTrigger,
} from '../state/regions';
import { effectsReduced } from '../render/motion';
import {
  DOOR_FADE_RGB,
  DOOR_PHASE1_MS,
  DOOR_PHASE2_MS,
  DOOR_PHASE3_MS,
} from '../render/doorTransition';
import { applyZoomMultiplier, getZoomMultiplier } from '../state/cameraPrefs';
import { captureFrom, type SnapshotHandle } from '../render/snapshotBackdrop';
import {
  applyModalOverlay,
  type ModalOverlaySource,
} from './modalOverlayState';

interface RuntimeMap {
  id: string;
  name: string;
  width: number;
  height: number;
  tileSize: number;
  tilesetImage: string;
  atlasCols: number;
  atlasRows: number;
  metatileIds: number[];
  collision: number[];
  elevation: number[];
  /** MB_* metatile behavior per cell. 0 = normal, 2 = MB_TALL_GRASS. */
  behaviors: number[];
  /** 1 iff this cell should re-render in an above-sprite layer (cartridge
   *  front-plane drawing on BG1). Built by the converter from the metatile's
   *  layer_type ≠ COVERED AND non-empty front plane (entries 4-7 of
   *  metatiles.bin). Roof eaves, tree canopies, awnings carry 1 here. */
  aboveLayer: number[];
  warps: Array<{
    id: string;
    x: number;
    y: number;
    elevation: number;
    destinationMap: string;
    destinationWarp: number;
  }>;
  connections: Array<{ direction: ConnectionDir; map: string; offset: number }>;
  events: {
    npcs: RuntimeNpc[];
    signs: RuntimeSign[];
    triggers: RuntimeTrigger[];
  };
}

interface RuntimeNpc {
  localId?: string;
  graphicsId: string;
  x: number;
  y: number;
  // Most fields below are required for `type: "object"` events but absent
  // for `type: "clone"` entries (cartridge convention for shared NPCs:
  // tree clones, item-ball clones across maps). Until the converter
  // resolves clones into full entries, downstream code reads these
  // through optional-chains so a clone NPC can't crash the scene.
  elevation?: number;
  movementType?: string;
  movementRangeX?: number;
  movementRangeY?: number;
  trainerType?: string;
  trainerSightOrBerryTreeId?: number | null;
  scriptRef?: string;
  flag?: string;
}

interface RuntimeSign {
  type: string;
  x: number;
  y: number;
  elevation: number;
  playerFacingDir: string;
  scriptRef: string;
}

interface RuntimeTrigger {
  type: string;
  x: number;
  y: number;
  elevation: number;
  var: string;
  varValue: number;
  scriptRef: string;
}

type ConnectionDir = 'up' | 'down' | 'left' | 'right';

interface TouchButton {
  rect: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  reposition: () => void;
  setVisible: (v: boolean) => void;
}

const STEP_COOLDOWN_MS = 140;
const TRANSITION_MS = 180;

/** From pokefirered include/constants/metatile_behaviors.h. Tall
 *  grass is where wild Pokémon live — roamer patches are its
 *  connected components. */
const MB_TALL_GRASS = 0x02;

/** Behaviors the player cannot walk onto, even when the tile's collision
 *  flag is 0 (passable). The cartridge gates water + currents via behavior,
 *  not via the collision bit; we have to mirror that or the player walks
 *  on water. Surf would unlock POND_WATER / OCEAN_WATER / SHALLOW_WATER —
 *  surf is out of scope for v0.1 so all water variants stay blocked. */
const IMPASSABLE_BEHAVIORS = new Set<number>([
  0x10, // MB_POND_WATER
  0x12, // MB_DEEP_WATER
  0x13, // MB_WATERFALL
  0x15, // MB_OCEAN_WATER
  0x16, // MB_PUDDLE — visually shallow but not steppable in cartridge
  0x17, // MB_SHALLOW_WATER
  0x19, // MB_UNDERWATER_BLOCKED_ABOVE
]);
/** Ledge tiles. Walking *toward* a ledge tile in its matching direction
 *  triggers a 2-tile hop *over* it, landing on the tile beyond. The ledge
 *  tile itself has collision=1 (impassable for normal stepping); the hop
 *  bypasses that. pokefirered defines only cardinal ledges (no NE/NW/SE/SW
 *  diagonals — those were Hoenn-specific). Values from
 *  pokefirered/include/constants/metatile_behaviors.h. */
const MB_JUMP_EAST = 0x38;
const MB_JUMP_WEST = 0x39;
const MB_JUMP_NORTH = 0x3a;
const MB_JUMP_SOUTH = 0x3b;

/** Whether `behavior` is a ledge that the player can hop *toward* `dir` from
 *  the adjacent tile. Cardinal-only in FireRed/Kanto. */
function isLedgeJumpable(behavior: number, dir: ConnectionDir): boolean {
  switch (dir) {
    case 'right': return behavior === MB_JUMP_EAST;
    case 'left':  return behavior === MB_JUMP_WEST;
    case 'up':    return behavior === MB_JUMP_NORTH;
    case 'down':  return behavior === MB_JUMP_SOUTH;
  }
}

/** Warp-triggering metatile behaviors. From
 *  pokefirered/include/constants/metatile_behaviors.h. Cartridge fires a
 *  warp only when the player steps onto a tile whose behavior is one of
 *  these; tiles listed in `warps[]` with `MB_NORMAL` (0) are leniency /
 *  spawn metadata that the engine doesn't actually trigger on. Arrow
 *  warps additionally require the step direction to match the arrow. */
const MB_CAVE_DOOR = 0x60;
const MB_LADDER = 0x61;
const MB_EAST_ARROW_WARP = 0x62;
const MB_WEST_ARROW_WARP = 0x63;
const MB_NORTH_ARROW_WARP = 0x64;
const MB_SOUTH_ARROW_WARP = 0x65;
const MB_FALL_WARP = 0x66;
const MB_REGULAR_WARP = 0x67;
const MB_WARP_DOOR = 0x69;
const MB_UP_ESCALATOR = 0x6a;
const MB_DOWN_ESCALATOR = 0x6b;
const MB_UP_RIGHT_STAIR_WARP = 0x6c;
const MB_UP_LEFT_STAIR_WARP = 0x6d;
const MB_DOWN_RIGHT_STAIR_WARP = 0x6e;
const MB_DOWN_LEFT_STAIR_WARP = 0x6f;

/** Counter desks in Pokémon Centers and Marts. The counter row is collision=1
 *  (the player can't step on it), and the NPC stands one tile beyond it.
 *  Cartridge semantic: pressing A while facing a counter looks PAST the
 *  counter to the NPC behind. Without this skip, the nurse / mart clerk is
 *  unreachable. */
const MB_COUNTER = 0x80;

/** Whether `behavior` is one of the 4 directional arrow-warp tiles. Arrow
 *  warps fire on step *intent* (press the arrow direction while standing
 *  on the tile), NOT on landing — walking onto a rug doesn't exit, you
 *  walk OFF it in the arrow direction to exit. Non-arrow warps (stairs,
 *  escalators, regular warps, doors) keep firing on landing in any
 *  direction; the gating happens in checkWarp's `context` arg. */
function isArrowWarp(behavior: number): boolean {
  return (
    behavior === MB_SOUTH_ARROW_WARP ||
    behavior === MB_NORTH_ARROW_WARP ||
    behavior === MB_EAST_ARROW_WARP ||
    behavior === MB_WEST_ARROW_WARP
  );
}

/** Whether stepping onto a tile with `behavior` while facing `dir` should
 *  fire its warp (caller checks that the tile is in `warps[]` separately).
 *  Cartridge semantics: arrow warps fire only when stepping in the arrow's
 *  direction; non-directional warps (regular / cave door / warp door /
 *  stairs / escalators / ladder / fall) fire on any step; tiles with
 *  MB_NORMAL or any non-warp behavior do not fire — they're leniency or
 *  spawn metadata even when listed in `warps[]`.
 *
 *  Arrow warps additionally restricted by checkWarp to intent context
 *  (pre-step) only — see isArrowWarp + checkWarp(context) below. */
function behaviorFiresWarp(behavior: number, dir: ConnectionDir): boolean {
  switch (behavior) {
    case MB_SOUTH_ARROW_WARP: return dir === 'down';
    case MB_NORTH_ARROW_WARP: return dir === 'up';
    case MB_EAST_ARROW_WARP:  return dir === 'right';
    case MB_WEST_ARROW_WARP:  return dir === 'left';
    case MB_CAVE_DOOR:
    case MB_LADDER:
    case MB_FALL_WARP:
    case MB_REGULAR_WARP:
    case MB_WARP_DOOR:
    case MB_UP_ESCALATOR:
    case MB_DOWN_ESCALATOR:
    case MB_UP_RIGHT_STAIR_WARP:
    case MB_UP_LEFT_STAIR_WARP:
    case MB_DOWN_RIGHT_STAIR_WARP:
    case MB_DOWN_LEFT_STAIR_WARP:
      return true;
    default: return false;
  }
}

/** Hop animation tuning. Total hop duration is 2x a normal step (covers two
 *  tiles). The peak of the sin-arc lifts the sprite this many world pixels
 *  above the straight-line lerp midpoint. */
const HOP_DURATION_MS = 280;
const HOP_ARC_PIXELS = 14;
/** Trainer-sighted '!' emote hold after the pop-in — "there's a pause
 *  for a second, then they run" (playtest round 2). Cartridge holds
 *  frame 2 for 52 GBA frames (~867ms); 500ms reads classic without
 *  dragging our snappier pace. */
const EMOTE_HOLD_MS = 500;
const EMOTE_HOLD_REDUCED_MS = 250;

// trainerFacingFromMovementType moved to scripts/npcFacing.ts +
// renamed facingFromMovementType (used by trainer LOS + C3 sprite frame
// + C6 approach-from-front). Same logic; shared module.

/** PR 12 (H9): is this NPC currently filtered from rendering / interaction?
 *  An NPC carrying a FLAG_HIDE_* flag is hidden iff that flag is currently
 *  SET in runState. Five sites query this predicate (rebuildNpcSprites,
 *  trainer LOS scan, tap routing, the in-front-interactable shortcut, the
 *  adjacent-interactable hit-test) — keep them in lockstep via this helper.
 *  Pre-PR-12 callers passed any FLAG_HIDE_* through as "always hidden"; that
 *  posture was too pessimistic (the 3 starter balls + Oak + rival were
 *  permanently invisible). The conditional read now honors INITIAL_HIDDEN_
 *  FLAGS + handler-driven set/clear. */
function npcIsHidden(npc: { flag?: string }): boolean {
  if (!npc.flag?.startsWith('FLAG_HIDE_')) return false;
  return runState.hasFlag(npc.flag);
}

/**
 * Derive a fresh @pkmn/sim PRNGSeed string from the seeded `battle` domain.
 * The gen5 PRNG is a 4×16-bit state ('gen5,a,b,c,d'); we draw 4 uint16s and
 * format. Each call advances the battle stream by 4 — three calls per battle
 * (sim seed + p1 ai seed + p2 ai seed) = 12 uint16s total. Pulling all three
 * from the same domain keeps the per-battle state-advance count constant
 * regardless of which side is fighting, which the v0.2 replay format relies
 * on for reproducibility.
 */
function rollBattleSeed(): `gen5,${number},${number},${number},${number}` {
  const a = Math.floor(seedStore.random('battle') * 0x10000);
  const b = Math.floor(seedStore.random('battle') * 0x10000);
  const c = Math.floor(seedStore.random('battle') * 0x10000);
  const d = Math.floor(seedStore.random('battle') * 0x10000);
  return `gen5,${a},${b},${c},${d}`;
}

// Maps available in v0.1. The game preloads them all so edge + warp
// transitions are instant. Outdoor: Pallet → Route 1 → Viridian → Route 2
// → Pewter. Interior: Player's House 1F (game boots here), Oak's Lab,
// the two Viridian Forest gate buildings, Viridian Forest itself, Pewter
// Gym, Pewter + Viridian Pokémon Centers (1F).
const V01_MAPS = [
  'PalletTown',
  'PalletTown_PlayersHouse_1F',
  'PalletTown_PlayersHouse_2F',
  'PalletTown_RivalsHouse',
  'PalletTown_ProfessorOaksLab',
  'Route1',
  'ViridianCity',
  'ViridianCity_PokemonCenter_1F',
  'ViridianCity_Mart',
  'Route2',
  'Route2_House',
  'Route2_EastBuilding',
  'Route2_ViridianForest_SouthEntrance',
  'Route2_ViridianForest_NorthEntrance',
  'ViridianForest',
  'PewterCity',
  'PewterCity_Gym',
  'PewterCity_Mart',
  'PewterCity_PokemonCenter_1F',
] as const;
type MapName = (typeof V01_MAPS)[number];

// pokefirered MAP_* IDs back to our converter's file basenames.
const MAP_ID_TO_NAME: Record<string, MapName> = {
  MAP_PALLET_TOWN: 'PalletTown',
  MAP_PALLET_TOWN_PLAYERS_HOUSE_1F: 'PalletTown_PlayersHouse_1F',
  MAP_PALLET_TOWN_PLAYERS_HOUSE_2F: 'PalletTown_PlayersHouse_2F',
  MAP_PALLET_TOWN_RIVALS_HOUSE: 'PalletTown_RivalsHouse',
  MAP_PALLET_TOWN_PROFESSOR_OAKS_LAB: 'PalletTown_ProfessorOaksLab',
  MAP_ROUTE1: 'Route1',
  MAP_VIRIDIAN_CITY: 'ViridianCity',
  MAP_VIRIDIAN_CITY_POKEMON_CENTER_1F: 'ViridianCity_PokemonCenter_1F',
  MAP_VIRIDIAN_CITY_MART: 'ViridianCity_Mart',
  MAP_ROUTE2: 'Route2',
  MAP_ROUTE2_HOUSE: 'Route2_House',
  MAP_ROUTE2_EAST_BUILDING: 'Route2_EastBuilding',
  MAP_ROUTE2_VIRIDIAN_FOREST_SOUTH_ENTRANCE: 'Route2_ViridianForest_SouthEntrance',
  MAP_ROUTE2_VIRIDIAN_FOREST_NORTH_ENTRANCE: 'Route2_ViridianForest_NorthEntrance',
  MAP_VIRIDIAN_FOREST: 'ViridianForest',
  MAP_PEWTER_CITY: 'PewterCity',
  MAP_PEWTER_CITY_GYM: 'PewterCity_Gym',
  MAP_PEWTER_CITY_MART: 'PewterCity_Mart',
  MAP_PEWTER_CITY_POKEMON_CENTER_1F: 'PewterCity_PokemonCenter_1F',
};

/** Starter draft uses the same rollOffers path as zone drafts now —
 *  Bulb/Char/Squirt live in DRAFT_POOLS under MAP_PALLET_TOWN_PROFESSOR_OAKS_LAB
 *  with equal weights + a fixed L5 range, and each pick rolls its own
 *  nature/IVs/ability (eng review Code Quality #3). The old hardcoded
 *  STARTER_DRAFT_OPTIONS table is gone. */

/** Lab pedestal left→right order (cartridge: x=8 Bulb, x=9 Squirt,
 *  x=10 Char per PalletTown_ProfessorOaksLab.runtime.json). launchStarter-
 *  Draft sorts the rolled offers into this order before mounting so the
 *  modal card positions match the pedestals visually. */
const STARTER_PEDESTAL_ORDER: readonly string[] = ['bulbasaur', 'squirtle', 'charmander'];

/** Derived from SPRITE_MANIFEST (extract-sprites-cli output). Adding a new
 *  sprite = 1 SPECS row in extract-sprites-cli + re-run extract; the manifest
 *  regenerates and both tables below pick it up automatically.
 *
 *  OBJ_EVENT_GFX_* → sprite-sheet key. Anything not present falls back to the
 *  olive placeholder rect in rebuildNpcSprites (OBJ_EVENT_GFX_CUT_TREE is
 *  HM-Cut scope). */
const GFX_TO_SPRITE: Record<string, string> = Object.fromEntries(
  Object.entries(SPRITE_MANIFEST).map(([key, m]) => [m.gfxSymbol, key]),
);

/** OBJ_EVENT_GFX_VAR_0 is runtime-resolved in pokefirered (each map's
 *  on_load sets VAR_OBJ_GFX_ID_0). We have no script VM for those
 *  setvars, so the resolution is baked per localId — the converter
 *  carries localId through, and v0.1's scope has exactly one visible
 *  VAR_0 NPC. Cartridge: ViridianCity's on_load resolves the tutorial
 *  man to OBJ_EVENT_GFX_OLD_MAN_1 post-coffee. */
const VAR_0_SPRITES: Record<string, string> = {
  LOCALID_TUTORIAL_MAN: 'old_man_1',
};

/** Per-spritesheet frame dimensions for preload. Excludes red_normal — the
 *  player's sheet is loaded explicitly below with documentation around the
 *  9-frame [down/up/side] convention. */
const NPC_SPRITESHEETS: Array<{ key: string; frameWidth: number; frameHeight: number }> =
  Object.entries(SPRITE_MANIFEST)
    .filter(([key]) => key !== 'red_normal')
    .map(([key, m]) => ({ key, frameWidth: m.frameWidth, frameHeight: m.frameHeight }));

/** Sprites that occupy a single 16×16 tile (vs 16×32 NPCs whose head
 *  overlaps the tile above). 16×16 sprites use a true centered origin so
 *  they sit in their own tile rather than half-floating above the next. */
const STATIC_TILE_SPRITES = new Set<string>(
  Object.entries(SPRITE_MANIFEST)
    .filter(([, m]) => m.frameHeight === 16)
    .map(([key]) => key),
);

/** Species whose follower BW filmstrips are preloaded in WorldScene.preload.
 *  Mirrors the FRAMESHEET_REQUIRED set in packages/sprite-fetch's all-frames
 *  bake — the 12 v0.1-reachable species (starters + Route 1 / Forest /
 *  Brock's ace). Any species not in this list falls through to the
 *  Follower wrapper's lazy-load path (Phaser load.start() on demand). */
const FOLLOWER_PRELOAD_SPECIES: readonly string[] = [
  'bulbasaur', 'charmander', 'squirtle',
  'caterpie', 'metapod', 'weedle', 'kakuna',
  'pidgey', 'rattata', 'spearow', 'pikachu', 'onix',
  // In-world trainer duels render opponents from OW sheets — every
  // trainer-party species needs one preloaded (geodude: Pewter gym).
  'geodude',
];

/** Tile size in pixels. Gen 3 standard. Passed to Follower at construction;
 *  per-map tileSize lives on mapData but never varies in v0.1, so a single
 *  constant keeps the Follower API simple. */
const TILE_SIZE_PX = 16;

/**
 * Build a `RuntimeMap`-shaped shim for exterior (mega-map) mode.
 *
 * The 74 `this.mapData.*` references in WorldScene continue to work
 * transparently in exterior mode via this shim:
 *   - World-coord fields (width, height, tileSize, metatileIds, collision,
 *     behaviors, layerTypes) delegate to `world` — values are GLOBAL.
 *   - Region-coord fields (id, name, warps, events.{npcs,signs,triggers})
 *     come from `region`, with NPC/sign/trigger/warp coords pre-translated
 *     to global tile coords at converter time. To support seamless
 *     boundary crosses, the events block is the UNION of every region's
 *     events — Phaser frustum-culls off-screen sprites for free, and a
 *     player crossing into a neighbor region sees that region's NPCs
 *     immediately (without any rebuild).
 *   - connections is `[]` (the mega-map has no per-map edges; the
 *     existing edge-cross / attemptConnectionTransition path naturally
 *     short-circuits with no connections to test).
 *   - elevation is left empty — call sites use a `?? 0` fallback.
 *
 * Called once on enterExteriorRegion AND on every boundary cross (to
 * update `mapData.id` / `mapData.name` to the new region). The world
 * grid + events union are stable across crosses; only the region-coord
 * fields shift.
 */
/** Display-format a short region name for the route placard:
 *    "Route1" → "Route 1", "PalletTown" → "Pallet Town", "ViridianForest"
 *  → "Viridian Forest". Splits on CamelCase + before digits. Leaves
 *  unrecognized formats alone (best-effort). */
function formatRegionName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .trim();
}

function synthesizeExteriorMapData(world: World, region: Region): RuntimeMap {
  const allNpcs: RegionNpc[] = [];
  const allSigns: RegionSign[] = [];
  const allTriggers: RegionTrigger[] = [];
  for (const r of world.regions) {
    for (const n of r.events.npcs) allNpcs.push(n);
    for (const s of r.events.signs) allSigns.push(s);
    for (const t of r.events.triggers) allTriggers.push(t);
  }
  return {
    id: region.id,
    name: region.name,
    width: world.worldWidth,
    height: world.worldHeight,
    tileSize: world.tileSize,
    tilesetImage: '', // mega-map tilesets bound via Phaser keys, not asset URLs
    atlasCols: 0,
    atlasRows: 0,
    metatileIds: world.metatileIds,
    collision: world.collision,
    elevation: [], // unused in v0.1; consumer uses `?? 0` fallback
    behaviors: world.behaviors,
    aboveLayer: [], // mega-map renders above via its own metatileIdsAbove
    warps: region.warps.map((w) => ({
      id: w.id,
      x: w.globalX,
      y: w.globalY,
      elevation: w.elevation,
      destinationMap: w.destinationMap,
      destinationWarp: w.destinationWarp,
    })),
    connections: [], // mega-map has no per-map edges
    events: {
      npcs: allNpcs,
      signs: allSigns,
      triggers: allTriggers,
    },
  };
}

export class WorldScene extends Phaser.Scene {
  private mapData!: RuntimeMap;
  /** Current map's converter basename (e.g. 'ViridianForest'). Same as
   *  `mapData.name` for interiors; mega-map exteriors maintain it via
   *  `currentRegion?.name` in the exterior-mode branch. `mapData.id` is
   *  the cartridge `MAP_*` long form — no longer used at runtime now that
   *  data tables + runState keys are rekeyed to the short name. */
  private currentMapName!: MapName;
  private layer!: Phaser.Tilemaps.TilemapLayer;
  /** Re-renders metatiles whose runtime.json `aboveLayer` flag is 1 (cartridge
   *  front-plane content on BG1: roof eaves, tree canopies, awnings) at depth
   *  6 — above the player (depth 5) and NPCs (depth 4). The full atlas tile
   *  is drawn in both layers; the discriminator on which cells participate
   *  is computed by the converter using the metatile's layer_type +
   *  has-front-content. Cleared with the tilemap on next loadMap. */
  private aboveLayer?: Phaser.Tilemaps.TilemapLayer;
  private tilemap!: Phaser.Tilemaps.Tilemap;
  /** Mega-map (seamless-world). Loaded synchronously in create() via
   *  preload + buildMegaMap; available for the exterior path. */
  private world: World | null = null;
  /** Active rendering mode. `'interior'` = per-map tilemap (mapData ←
   *  the loaded interior runtime.json). `'exterior'` = mega-map tilemap
   *  (mapData ← synthesizeExteriorMapData(world, currentRegion), a shim
   *  that delegates collision/behaviors/width/height to world and id/name/
   *  warps/events to currentRegion). The 74 mapData refs in this file
   *  continue to work in both modes via this shim — see Step 3b commit. */
  private mode: 'interior' | 'exterior' = 'interior';
  /** Region the player is currently inside (exterior mode only). Updated
   *  on every boundary cross detected by regionAt(world, gx, gy). */
  private currentRegion: Region | null = null;
  /** Route-name pill that fades in TRAILING every exterior boundary
   *  cross. DOM overlay parented to #game, anchored top-left 16px inset.
   *  Migrated from Phaser Graphics + Phaser.Text to DOM so it picks up
   *  real `backdrop-filter: blur(...)` glass per DESIGN.md (Phaser can't
   *  blur the underlying world). */
  private routePlacard: HTMLDivElement | null = null;
  /** Timer for the placard's fade-out + DOM unmount. Held so rapid
   *  region re-crosses can cancel the pending teardown when a new
   *  placard supersedes the current one. */
  private routePlacardTimer: ReturnType<typeof setTimeout> | null = null;
  private player!: Phaser.GameObjects.Sprite;
  /** Step counter alternates left-foot / right-foot frames during walking. */
  private stepCount = 0;
  /**
   * NPC sprites mounted at rebuild time, keyed by `localId` so cinematic
   * handlers can resolve a specific NPC for frame-toggle / facing /
   * highlight operations. Per PR A1 eng-review decision 2: changed from
   * `Sprite[]` to a Map. NPCs without an explicit `localId` (cartridge
   * `type: "clone"` entries) get a synthetic key derived from their
   * (x, y) tile position so they're still individually addressable
   * within their map (a clone NPC's tile is unique within its map).
   *
   * Iteration order is insertion order (Map preserves it) so the
   * cleanup loops in tilemapSwap + rebuildNpcSprites destroy sprites
   * in mount order — same as the prior Array semantics.
   */
  private npcSprites: Map<string, Phaser.GameObjects.Sprite | Phaser.GameObjects.Rectangle> = new Map();
  /**
   * Sprites created by cinematic handlers (npcWalkAlongPath without
   * existingSprite — Oak's run-in, the rival walker). NOT in npcSprites[]
   * so rebuildNpcSprites can't yank them mid-walk. Tracked here so
   * runHandlerBracket's finally can destroy any survivors if a handler
   * errors mid-cinematic or forgets to clean up. Per PR A5 / eng-review
   * decision 5 (hypothesis-D structural fix for the mini-rival sprite
   * leak regression). Sprites auto-remove from this set via Phaser's
   * DESTROY event so an explicit handler-side destroy() doesn't leave
   * a stale reference here.
   *
   * Lifecycle:
   *
   *     handler starts            bracket snapshots set
   *           │                          │
   *           ▼                          ▼
   *   ┌────────────────────────────────────────────┐
   *   │  npcWalkAlongPath creates sprite →         │
   *   │    handlerOwnedSprites.add(sprite)         │
   *   │    sprite.once(DESTROY) → set.delete       │
   *   │                                            │
   *   │  handler.destroy()  → DESTROY fires →      │
   *   │    set.delete (auto)                       │
   *   │                                            │
   *   │  handler errors mid-walk → sprite leaks    │
   *   │    bracket finally → destroys survivors    │
   *   └────────────────────────────────────────────┘
   */
  private handlerOwnedSprites: Set<Phaser.GameObjects.Sprite> = new Set();
  private collisionOverlay?: Phaser.GameObjects.Container;
  private statusText!: Phaser.GameObjects.Text;
  private helpText!: Phaser.GameObjects.Text;
  /** Second camera kept at zoom 1 so chrome (help/status/dialogue) renders at
   *  viewport pixels regardless of the world camera's GBA-like zoom. */
  private uiCamera!: Phaser.Cameras.Scene2D.Camera;
  /** Touch controls: D-pad + A/B buttons. Always visible; same UI for desktop
   *  mouse + mobile touch. Children re-positioned on resize, not destroyed. */
  private touchControls!: Phaser.GameObjects.Container;
  private dpadButtons: Record<ConnectionDir, TouchButton> = {} as Record<ConnectionDir, TouchButton>;
  private aButton!: TouchButton;
  private bButton!: TouchButton;
  /** Active touch-control mode. Read from localStorage on create();
   *  flipped at runtime by setControlMode() when the user changes the
   *  inline radio in MenuScene (Step 4 of the touch-controls plan). */
  private controlMode: ControlMode = 'direct';
  /** Live joystick handle when controlMode === 'direct'. Null in classic
   *  + command modes. setControlMode toggles this. */
  private joystickHandle: JoystickHandle | null = null;
  /** Diegetic glass-orb party strip (step 3 of orb chrome). Mounted on
   *  create(); stays alive across SLEEP/WAKE via display flip inside
   *  the handle's own listeners. Always present in all three control
   *  modes — the strip is chrome, not input. */
  private partyOrbs: PartyOrbsHandle | null = null;
  /** Pokéball glyph + popover at top-right (step 6 of orb chrome).
   *  Coexists with the legacy ☰ button until step 7c deletes the latter. */
  private pokeballMenu: PokeballMenuHandle | null = null;
  /** COMMAND-mode pathfinder (Step 7). Per-map (grid baked at
   *  loadMap). Receives tap targets via handleTap; feeds tile-by-
   *  tile direction into intentRouter under the 'tap-step' source. */
  private pathfinder?: PathfinderManager;
  /** Phaser scene clock at the most recent WAKE. The pointerup handler
   *  rejects events whose pointerdown predates this so a tap that
   *  closed an overlay scene (MenuScene backdrop, DraftScene, etc.) and
   *  fired its pointerup after WorldScene woke doesn't get routed to
   *  handleTap as a COMMAND walk. */
  private wakeAt = 0;

  // Boot location: Player's House 1F, just below the staircase from 2F
  // (we skip 2F for v0.1 — player would normally walk down those stairs).
  // Walking south takes them past Mom, out the front door to Pallet Town.
  private playerTileX = 10;
  private playerTileY = 3;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private cKey!: Phaser.Input.Keyboard.Key;
  private spaceKey!: Phaser.Input.Keyboard.Key;
  private enterKey!: Phaser.Input.Keyboard.Key;
  private moving = false;
  /** Manual step-tween state. Replaced Phaser tweens because tweens.add()
   *  queues into the next tween-manager preUpdate, leaving a 1-2 frame gap
   *  between the previous step's onComplete and the next step's first tick.
   *  That gap was the user-visible "every step has a noticeable stop". With a
   *  manual lerp, when a step completes mid-update we kick off the next step
   *  in the *same* tick — zero gap, true smooth-walk cadence. */
  private stepStartX = 0;
  private stepStartY = 0;
  private stepTargetX = 0;
  private stepTargetY = 0;
  private stepElapsedMs = 0;
  /** True while the player is mid-ledge-hop. Hops use a longer duration
   *  (HOP_DURATION_MS, ~2x a normal step) and overlay a sin-arc onto the
   *  player's y so the leap visibly bounces. */
  private hopActive = false;
  private transitioning = false;
  /** Tile-index → blocked-by-NPC. Rebuilt on every loadMap so canStep can
   *  check NPC occupancy in O(1). Without this NPCs render but the player
   *  walks straight through them. */
  private npcTiles = new Set<number>();
  /** Guard against re-entry while wildPokemonAsync awaits the @pkmn/data
   *  lazy chunk. The first encounter after page load can take a few hundred
   *  ms; without this, repeated contact could stack engagement calls
   *  before the battle actually launches. */
  private encounterPending = false;
  /** Direction the player most-recently tried to move; classic Pokémon convention is to start facing south. */
  private facing: ConnectionDir = 'down';

  /** Dialogue overlay — DOM glass panel (Future Cartridge PR 2). The
   *  Phaser-side dialogue rendering (Rectangle + BitmapText container
   *  + ▼/× glyph Text objects + bob tween) retired here; the handle
   *  owns the DOM child of #game and tracks the open/opening/closing
   *  state machine via dialogueOverlayLogic. */
  private dialogueOverlayHandle?: DialogueOverlayHandle;
  /** True while a dialogue is opening / open / closing. Read by every
   *  movement + interaction gate in this scene; derived from the
   *  overlay handle so there's a single source of truth. */
  private get dialogueOpen(): boolean {
    return this.dialogueOverlayHandle?.isOpen() ?? false;
  }
  /** Timestamp of the most recent showDialogue. Read by handleTap to
   *  reject close-by-outside-tap events that landed within ~350 ms of
   *  show — those are almost always the tail of the gesture that
   *  triggered the open (iOS Safari ~300 ms synthetic click delay). */
  private dialogueOpenedAt = 0;
  /** Sources that currently have a modal overlay open ON TOP of the
   *  world (transparent overlays — WorldScene stays awake). When non-
   *  empty, orb chrome dims, joystick zone disables, dialogue DOM
   *  suppresses, and tryProcessMovement gates so the player can't walk
   *  while inspecting. PR 3a generalizes the prior `partySheetOpen`
   *  boolean — PR 3b activates the other sources (settings/bag/pokedex)
   *  via the same setModalOverlayOpen entry point. Callers
   *  (PartySheetScene, pokeballMenu) toggle membership directly;
   *  WorldScene itself never toggles. */
  private openModalSources: ReadonlySet<ModalOverlaySource> = new Set();
  /** True while an orb drag-to-reorder gesture is in progress. Joystick
   *  zone pointer-events: none for the duration so a long drag that
   *  crosses into the joystick zone doesn't double-fire both the drag
   *  handler and the joystick anchor. Orb chrome stays interactive so
   *  the drag itself completes. PartyOrbs calls setDragInProgress on
   *  long-press start / drag end. */
  private dragInProgress = false;
  /** True while launchFrozenModal is between input-lock and scene.launch.
   *  Joins the joystick suppress chain so a stray tap during the ≤100ms
   *  captureFrom window can't move the player or fire a second encounter.
   *  Strategy-locked outside-voice §3 / Glass-Over-World eng review. */
  private isLaunchingModal = false;
  /** Lead-Pokémon follower (HGSS-style — walks one tile behind the
   *  player). Pure-logic state in `FollowerState`; Phaser sprite + tweens
   *  in the `Follower` wrapper. Created once in create(); persists
   *  through SLEEP/WAKE cycles. */
  private follower?: Follower;
  /** The world's visible wild spawns. Always constructed; populated
   *  per region once the player has a starter. */
  private roamers: RoamerSystem | null = null;
  /** M2 in-world duel: true while a battle is staged over the AWAKE
   *  world. The frozen-modal path got total input lockout from
   *  scene.sleep for free; a duel needs the world live but the
   *  player's verbs routed to battle chrome only — this flag is that
   *  regime's single source of truth. Gates movement, world taps, and
   *  interact; roamer ticking freezes via encounterPending, which is
   *  always set alongside this. */
  private duelActive = false;
  /** Live staging director while an in-world duel runs; null otherwise. */
  private duelStage: DuelStage | null = null;
  /** Attached whole-scene smoothing PostFX (null = off / detached). Its zoom
   *  + native-grid phase uniforms are pushed each frame from the live camera
   *  in update(). Scene-lifetime since 2026-08-04, not duel-scoped. */
  private duelFilterPipe: DuelSceneFilterPipeline | null = null;
  /** The live scaler pills. Mounted at create() + WAKE, destroyed on SLEEP +
   *  SHUTDOWN — DOM parented to #game, so it must never outlive the scene's
   *  visible state (same contract as the route placard). */
  private filterCycler: DuelFilterCyclerHandle | null = null;
  /** Canvas `image-rendering` as Phaser left it at boot, cached while the
   *  smooth-delivery toggle is on so teardown restores it verbatim. */
  private duelPrevImageRendering: string | null = null;
  /** Region name whose next populate must NOT bump the visit counter —
   *  set on boot-resume so a reload re-derives the same cast instead
   *  of re-rolling it (the reload-scum guard). Cleared on first use. */
  private roamerResumeRegion: string | null = null;
  /** Species of the current lead, tracked so onPartyChanged fires
   *  follower.onLeadChanged only on actual lead changes (not on HP /
   *  level / move mutations that also notify onPartyChanged). */
  private prevLeadSpecies: string | null = null;
  /** Species of a pending starter-pick spawn — set by the
   *  onPartyChanged subscriber when the party transitions from empty
   *  to non-empty while WorldScene is asleep (DraftScene up). The
   *  WAKE handler consumes this and triggers the Pokéball-pop
   *  animation so the user sees it post-DraftScene close. */
  private pendingFirstSpawnSpecies: string | null = null;
  /** Unsubscribe handle for the runState.onPartyChanged listener; held
   *  so future scene-destroy paths can clean up (Phaser singleton scene
   *  so unused in v0.1, but cheap). */
  private unsubscribePartyChanged?: () => void;
  /** Phase 12 M3: scriptRef of the NPC/sign that opened the current
   *  dialogue. Used by closeDialogue to dispatch post-dialogue actions
   *  (Pokémon Center heal, future item gives, etc.). null when no dialogue
   *  is open or when the dialogue was opened from a code path that didn't
   *  pass a scriptRef (warp lock-out messages, e.g.). */
  private dialogueScriptRef: string | null = null;

  constructor() {
    super('WorldScene');
  }

  preload(): void {
    for (const mapName of V01_MAPS) {
      this.load.json(`map:${mapName}`, `assets/maps/${mapName}.runtime.json`);
      this.load.image(`atlas:${mapName}`, `assets/tilesets/${mapName}.atlas.png`);
      // Front-only atlas — same layout, but each cell contains only the
      // metatile's front plane (subtile entries 4-7) with the rest
      // transparent. The above-sprite layer renders from this so back-
      // plane content (grass ground, tree-trunk shadows) doesn't double-
      // render above the sprite.
      this.load.image(`atlas-above:${mapName}`, `assets/tilesets/${mapName}.atlas-above.png`);
    }
    // Mega-map (seamless-world Step 3b): JSON + shared ground/above
    // atlases. Phaser blocks create() until preload completes, so create()
    // can synchronously build the world via buildMegaMap().
    preloadMegaMap(this);
    // m5x7 BitmapFont retired in the Future Cartridge migration (TODOs
    // #24 PR 6). The atlas + bake script stay around for 30 days as
    // regret insurance (TODOs #29) — drop them in PR 6b once the
    // friend-group playtests under the new aesthetic confirm no regret.
    // Player walking sprite: 144×32 strip, 9 frames @ 16×32. Frame layout
    // is the pokeemerald gObjectEventImageAnimTable_StandardFour
    // convention: 0 stand S · 1 stand N · 2 stand W · 3-4 walk S ·
    // 5-6 walk N · 7-8 walk W (right = flipX of west). Authoritative
    // mapping lives in setPlayerSpriteFrame's doc comment.
    this.load.spritesheet('red_normal', 'assets/sprites/red_normal.png', {
      frameWidth: 16,
      frameHeight: 32,
    });
    for (const s of NPC_SPRITESHEETS) {
      this.load.spritesheet(s.key, `assets/sprites/${s.key}.png`, {
        frameWidth: s.frameWidth,
        frameHeight: s.frameHeight,
      });
    }
    // Trainer-sighted '!' emote — cartridge sGfx_Emoticons top row
    // (extract-emote-cli). 3 frames: two-step pop-in, then hold.
    this.load.spritesheet('emote_exclamation', 'assets/sprites/emote_exclamation.png', {
      frameWidth: 16,
      frameHeight: 16,
    });
    // Follower overworld sheets — HGSS-style 9-frame walk cycles for
    // the 12 v0.1 species. 288×32 strips, 32×32 per frame, TaTaTaZJJ
    // layout: idle cluster 0/1/2 = down/up/LEFT, walk pairs 3-4 /
    // 5-6 / 7-8, flipX for RIGHT (side art faces left — the inverse
    // of the player sheet's west-facing convention). Authoritative
    // tables: follower.ts OW_IDLE_FRAME / OW_WALK_FRAMES. Loaded as
    // Phaser spritesheets so Follower + roamers can setFrame()
    // directly without runtime slicing.
    for (const species of FOLLOWER_PRELOAD_SPECIES) {
      const dexId = SPECIES_TO_DEX[species];
      if (!dexId) continue;
      this.load.spritesheet(
        `follower-ow:${species}`,
        `assets/pokemon/follower/${dexId}.png`,
        { frameWidth: 32, frameHeight: 32 },
      );
    }
    if (DUEL_HD_BATTLERS_ENABLED) {
      // xBRZ-4× duel-battler variants (sprite-fetch --mode=hd): same
      // 9-frame layout at 128px frames, worn only under the duel
      // camera at scale 1/4. ~7 KB each — preloading the roster is
      // cheaper than an async race at duel start.
      for (const species of FOLLOWER_PRELOAD_SPECIES) {
        const dexId = SPECIES_TO_DEX[species];
        if (!dexId) continue;
        this.load.spritesheet(
          `follower-ow-hd:${species}`,
          `assets/pokemon/follower/${dexId}.hd4.png`,
          { frameWidth: 128, frameHeight: 128 },
        );
      }
    }
    audio.preloadAllSfx(this.load);
  }

  create(): void {
    // Player sprite. Origin (0.5, 0.75) so the sprite's feet sit at the tile's
    // center — the character's lower 16×16 occupies its current tile, the
    // upper 16×16 (head/torso) overlaps the tile above. Camera startFollow
    // tracks this anchor point, which keeps the foot centered in the viewport.
    this.player = this.add.sprite(0, 0, 'red_normal', 0).setOrigin(0.5, 0.75).setDepth(5);

    // Follower system (M1 of diegetic combat). Instantiated BEFORE the
    // first loadMap so onMapLoad's call site has a target; spawn() fires
    // explicitly after the resume-on-reload loadMap below once we know
    // the lead species. Persists across SLEEP/WAKE — its FollowerState
    // preserves species across battle scenes.
    this.follower = new Follower(this, TILE_SIZE_PX);

    // Visible wild spawns. Constructed unconditionally (cheap when
    // empty); populate is gated on hasStarter + exterior mode in
    // maybePopulateRoamers. Host
    // callbacks read live scene state at call time — the mapData shim
    // swap on region cross is picked up for free.
    this.roamers = new RoamerSystem(this, {
      gridWidth: () => this.mapData.width,
      tileSize: () => this.mapData.tileSize,
      isGrass: (x, y) =>
        (this.mapData.behaviors[y * this.mapData.width + x] ?? 0) ===
        MB_TALL_GRASS,
      isBlockedForRoamer: (x, y) => {
        if (!this.canStep(x, y)) return true;
        if (x === this.playerTileX && y === this.playerTileY) return true;
        // Both the follower's committed tile AND its in-flight tween
        // destination — the committed tile only advances at tween end,
        // so without targetTile a roamer could commit into the tile
        // the follower is walking toward and stack on it (adversarial
        // review bug 4).
        const f = this.follower?.snapshot;
        if (!f?.visible) return false;
        if (f.tileX === x && f.tileY === y) return true;
        return Boolean(f.targetTile && f.targetTile.x === x && f.targetTile.y === y);
      },
    });

    this.cursors = this.input.keyboard!.createCursorKeys();
    this.cKey = this.input.keyboard!.addKey('C');
    this.spaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.enterKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);

    this.dialogueOverlayHandle = mountDialogueOverlay(this);
    this.dialogueOverlayHandle.onClosed(() => this.onDialogueClosed());
    this.createTouchControls();
    // Touch controls layer: hide classic D-pad + A/B in direct/command
    // modes (default direct). MENU stays visible across all modes.
    this.controlMode = getControlMode();
    this.applyControlModeVisibility();
    if (this.controlMode === 'direct') {
      this.joystickHandle = mountJoystick(this);
    }
    // Party-orb strip (orb chrome step 3). Mounts in all control modes —
    // the strip is diegetic chrome, not control input. Self-manages
    // its visibility via SLEEP/WAKE listeners; no manual hide/show
    // needed from WorldScene.
    this.partyOrbs = mountPartyOrbs(this);
    // Pokéball glyph + popover (orb chrome step 6). The popover is the
    // gateway to Pokédex / Bag (disabled) / Settings. ☰ button stays
    // alive in parallel until step 7c closes the dual-surface window.
    this.pokeballMenu = mountPokeballMenu(this);

    // Step 5: tap-on-thing. A scene-level pointerup handler catches taps
    // anywhere on the canvas that didn't already hit an interactive
    // (D-pad, MENU, dialogue bg, dialogue ×). Phaser passes both the
    // pointer and a `currentlyOver` array of game objects; an empty
    // array means the tap landed on empty world.
    this.input.on(
      'pointerup',
      (pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) => {
        if (currentlyOver.length > 0) return;
        // Reject pointerups whose pointerdown predates the most recent
        // WAKE — those are the tail of a gesture that closed an overlay
        // scene (e.g. MenuScene backdrop), and routing them to handleTap
        // would COMMAND-walk to the menu-close position.
        if (pointer.downTime < this.wakeAt) return;
        const duration = pointer.upTime - pointer.downTime;
        const distance = Phaser.Math.Distance.Between(
          pointer.downX, pointer.downY, pointer.upX, pointer.upY,
        );
        // Generous gates: 250 ms / 8 px catch real taps reliably on
        // touch + mouse without false-positive drags. The plan locks
        // these as the click signature for tap-passthrough too.
        if (duration > 250 || distance > 8) return;
        this.handleTap({
          screenX: pointer.x,
          screenY: pointer.y,
          worldX: pointer.worldX,
          worldY: pointer.worldY,
        });
      },
    );

    // Tap-passthrough from the joystick zone (DIRECT mode). When the
    // capture-phase click detector inside joystick.ts identifies a tap
    // (not a drag), it emits this scene event carrying the original
    // pointer's screen coords. We project to world coords via the
    // main camera and reuse handleTap so the routing logic stays in
    // one place.
    this.events.on(TAP_PASSTHROUGH_EVENT, (payload: TapPassthroughPayload) => {
      const world = this.cameras.main.getWorldPoint(payload.clientX, payload.clientY);
      this.handleTap({
        screenX: payload.clientX,
        screenY: payload.clientY,
        worldX: world.x,
        worldY: world.y,
      });
    });

    // Dev-only HUD: dev hint (controls reminder) + tile-state readout. Created
    // but kept hidden so the mobile polish target stays clean. Toggle via
    // window.__pokeShowDev = true; game.scene.getScene('WorldScene').helpText.setVisible(true)
    // when debugging encounter / collision behavior locally.
    this.helpText = this.add
      .text(
        8,
        8,
        `phase 9 · grass = wild encounter · arrows/D-pad · A: interact · V: force battle · C: debug`,
        {
          // Dev-only — text-primary on opaque dark glass for legibility
          // over moving world content. system-ui font keeps it dev-y.
          color: TEXT_PRIMARY.rgba,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '12px',
          backgroundColor: 'rgba(20, 22, 28, 0.85)',
          padding: { left: 6, right: 6, top: 3, bottom: 3 },
        })
      .setScrollFactor(0)
      .setDepth(10)
      .setVisible(false);

    // Dev-only HUD readout (tile coord + collision + elevation). Hidden
    // by default — flipped on for local debugging via the C key toggle.
    // Migrated from m5x7 BitmapText to Phaser.Text in PR 6 of the
    // Future Cartridge migration; system-ui keeps it dev-feeling and
    // avoids loading a font just for a hidden overlay.
    this.statusText = this.add
      .text(8, 32, '', {
        color: '#FFFFFF',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '11px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        padding: { left: 6, right: 6, top: 3, bottom: 3 },
      })
      .setScrollFactor(0)
      .setDepth(10)
      .setVisible(false);

    this.scale.on('resize', () => this.repositionAll());

    // Two cameras: main = world (zoomed, follows player); ui = chrome (zoom 1,
    // static). Each camera ignores the layer the other one owns. Without this,
    // setScrollFactor(0) UI inherits the world camera's zoom and renders at 3x.
    this.cameras.main.startFollow(this.player, true);
    this.cameras.main.setRoundPixels(true);
    this.cameras.main.ignore([
      this.helpText,
      this.statusText,
      this.touchControls,
    ]);

    this.uiCamera = this.cameras.add(0, 0, this.scale.width, this.scale.height);
    this.uiCamera.setZoom(1);
    this.uiCamera.setScroll(0, 0);

    // Build the mega-map from preloaded assets (seamless-world Step 3b).
    // Phaser blocked create() until preload completed, so the JSON +
    // atlases are guaranteed in cache. Failure throws and the boot path
    // falls back to the interior-only flow — the existing per-map
    // exterior runtime.json files still ship, so a missing mega-map
    // doesn't break v0.1 functionality (just disables seamless cross).
    try {
      this.world = buildMegaMap(this);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[world] buildMegaMap failed; falling back to per-map exterior path', err);
      this.world = null;
    }

    // Resume-on-reload: if the save carried a position, drop the player
    // back where they were (same map, tile, facing). Defaults to the
    // bedroom for fresh runs OR for v5-and-earlier saves whose v5→v6
    // migration set position=null. Dispatch: exterior region in the
    // mega-map → enterExteriorRegion; everything else → loadMap (per-map).
    //
    // Bounds-defense: if the resumed tile is out of the named region's
    // [0, w) × [0, h) box, the save was written by the pre-fix
    // persistPlayerPosition that stored GLOBAL coords as if they were
    // local. Falling through to bedroom is the safest recovery — the
    // alternative is the user landing in the off-region "border woods"
    // void with their avatar covered by above-layer trees. Interior
    // maps aren't in world.byName so the bounds check skips for them.
    const resumed = runState.getPosition();
    let useResume = false;
    if (resumed && (V01_MAPS as readonly string[]).includes(resumed.mapName)) {
      const region = this.world?.byName.get(resumed.mapName as MapName);
      if (!region) {
        // Interior map (not in mega-map) — no bounds context, trust the save.
        useResume = true;
      } else if (
        resumed.tileX >= 0 && resumed.tileX < region.w &&
        resumed.tileY >= 0 && resumed.tileY < region.h
      ) {
        useResume = true;
      } else {
        console.warn(
          `[resume] saved tile (${resumed.tileX}, ${resumed.tileY}) out of bounds ` +
          `for region ${resumed.mapName} (${region.w}×${region.h}) — corrupt save, ` +
          `booting to bedroom`,
        );
      }
    }
    if (useResume && resumed) {
      // Roam: mark this as a resume entry so the populate path reuses
      // the persisted visit count instead of bumping it — a reload
      // must re-derive the SAME cast, not re-roll it.
      this.roamerResumeRegion = resumed.mapName;
      this.dispatchEnterMap(resumed.mapName as MapName, resumed.tileX, resumed.tileY);
      this.facing = resumed.facing;
      this.player.setFlipX(this.facing === 'right');
      this.setPlayerSpriteFrame('idle');
    } else {
      this.loadMap('PalletTown_PlayersHouse_1F', 10, 3);
    }
    this.repositionTouchControls();

    // Place the follower with the current lead species (if any). loadMap
    // above already ran (with no animation now); place() positions the
    // sprite at the derived home tile without a Pokéball-pop. HGSS:
    // boot-resume = "already there beside you," no re-summoning.
    // prevLeadSpecies is the dedupe key for the onPartyChanged
    // subscription wired below — HP / level / move changes notify
    // onPartyChanged but don't change the lead.
    const initialLead = runState.getParty()[0];
    if (initialLead && this.follower) {
      this.follower.place(
        { x: this.playerTileX, y: this.playerTileY },
        this.facing,
        initialLead.species,
        this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
      );
      this.prevLeadSpecies = initialLead.species;
    }

    // Lead-change reactivity. onPartyChanged fires for any party
    // mutation (HP / level / moves / order / removal); we filter to
    // species changes only. Empty-party (post-Reset-Run) hides the
    // follower; subsequent draft picks rebuild it via follower.spawn
    // called from the lead-set path.
    this.unsubscribePartyChanged = runState.onPartyChanged(() => {
      const newLead = runState.getParty()[0]?.species ?? null;
      if (newLead === this.prevLeadSpecies) return;
      const wasEmpty = this.prevLeadSpecies === null;
      this.prevLeadSpecies = newLead;
      if (!newLead) {
        // Party emptied (Reset Run from settings). Hide until next draft
        // pick repopulates it via the lead-set path.
        this.follower?.onSleep();
        return;
      }
      if (wasEmpty) {
        // First lead in this run — STARTER PICK. HGSS: this is the
        // moment the Pokéball-pop animation plays. If the scene is
        // asleep (DraftScene up — typical starter-pick path), defer
        // the spawn to the WAKE handler so the animation is visible.
        // If awake (rare — e.g., direct addToParty from a transparent
        // overlay), fire immediately.
        if (this.scene.isActive()) {
          this.follower?.spawn(
            { x: this.playerTileX, y: this.playerTileY },
            this.facing,
            newLead,
            this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
            this.firstSpawnPreferredTile(),
          );
        } else {
          this.pendingFirstSpawnSpecies = newLead;
        }
        return;
      }
      // Lead swap via orb bar — current follower already on screen;
      // play the send-out at its current tile (texture swaps in-place).
      this.follower?.onLeadChanged(newLead);
    });

    // Sleep/wake input cleanup. When a wild encounter fires while the player
    // is holding a direction (key or D-pad), WorldScene goes to sleep
    // mid-press. Phaser pauses input on sleeping scenes, so the matching
    // keyup / pointerup never lands — `cursors.left.isDown` and the
    // intentRouter's classic-dpad source both stay true. On wake, update()
    // would read the stale "down" and the player auto-walks until the user
    // presses + releases that direction to clear it. Reset on wake.
    // Pathfinder resets too so a half-walked path doesn't try to
    // resume into stale geometry after a battle.
    this.events.on(Phaser.Scenes.Events.SLEEP, () => {
      this.pathfinder?.reset();
      // Follower: snap to home tile + hide. Cancels any walk tween and
      // any in-flight send-out (scene is tearing down — visual
      // continuity doesn't matter). Species preserved for the WAKE
      // re-derive.
      this.follower?.onSleep();
      // Route placard is a DOM overlay parented to #game, not a Phaser
      // child — destroyed here on SLEEP so it doesn't outlive the
      // scene's visible state. (Phaser auto-destroys Phaser-side
      // GameObjects on SHUTDOWN; DOM overlays must clean themselves up.)
      this.disposeRoutePlacard();
      // Same DOM-outlives-visibility contract as the placard above: the
      // scaler pills are parented to #game, so a modal/Pokédex taking over
      // must not leave them floating over it. WAKE re-mounts from the stored
      // prefs, so nothing is lost. This also restores `image-rendering`, so
      // an overlay scene can never inherit a smoothed canvas.
      this.unmountDuelFilter();
    });
    this.events.on(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.disposeRoutePlacard();
      this.unmountDuelFilter();
      this.detachDuelFilter();
      // Mid-duel shutdown: release the battler + camera state without
      // tweens — Phaser tears down scene objects, but the DuelStage
      // ref must not survive into a future create().
      this.duelStage?.dispose();
      this.duelStage = null;
    });

    // Follower's OW sprite is materialized lazily on the first
    // spawn/onMapLoad/onWake/onLeadChanged after the species is known.
    // syncUiCameraIgnores already ran during loadMap above, so the new
    // sprite isn't in the UI camera's ignore list — without this
    // listener it double-renders as a tiny viewport-anchored copy until
    // the next loadMap re-syncs. Re-sync as soon as the sprite is
    // created so the UI camera ignores it from frame 0.
    this.events.on(FOLLOWER_SPRITE_CREATED_EVENT, () => this.syncUiCameraIgnores());

    this.events.on(Phaser.Scenes.Events.WAKE, () => {
      this.wakeAt = this.time.now;
      // Re-mount the scaler pills SLEEP tore down (idempotent, so a wake
      // without a matching sleep can't stack duplicates).
      this.mountDuelFilter();
      intentRouter.clearAll();
      this.input.keyboard?.resetKeys();
      // resetKeys clears all registered key states (cursors + cKey/
      // spaceKey/enterKey). Movement intent on the next update() reads fresh.

      // Phase 15a: when waking from a battle, the BattleScene played
      // battle/victory music that's still going. Restart the map's own
      // theme so the overworld doesn't echo a victory fanfare while the
      // player walks. playMusic is a no-op if the same key is already
      // current, so this is cheap on non-battle wakes too.
      const musicKey = MAP_MUSIC[this.currentMapName];
      if (musicKey) void audio.playMusic(this, musicKey);

      // Round-5: if the player FLED the rival battle, battleRules
      // cleared his hide flag but the map-authored npc entry still
      // points at his home tile next to Oak — reposition the entry to
      // his battle-era tile BEFORE the rebuild below mounts him, so he
      // re-arms where the player left him mid-challenge.
      this.repositionFledRivalNpc();

      // PR 15 (H7b): refresh NPC visibility on every wake. Flag state can
      // change while the scene is asleep (DraftScene.commit setting ball-
      // hide flags, BattleScene's rival defeat path setting RIVAL flags,
      // future post-battle hooks). Cheap (~3-10 sprite re-mounts); the
      // alternative was a per-call-site refresh on every flag mutation.
      this.refreshNpcVisibility();

      // PR A3 + A4: WAKE-handler cinematic dispatch gates for the Oak's
      // Lab cinematic sequence. Both fire from the same WAKE pass on
      // the lab map but are mutually exclusive in production:
      //
      //   * labPostPick (A3) — fires after DraftScene closes; gate
      //     requires STARTER_PICKED + state==2 + LAB_POST_PICK_DONE
      //     unset. Latch is set inside the handler at entry
      //     (eng-review decision 3).
      //
      //   * rivalPostBattle (A4) — fires after BattleScene exits with
      //     RIVAL_BATTLE_RESOLVED set (BattleScene flips it on p1
      //     victory over a rival ref). Latch RIVAL_POST_BATTLE_DONE
      //     prevents re-dispatch during the cinematic.
      const onOakLab = this.mapData?.name === 'PalletTown_ProfessorOaksLab';
      if (
        onOakLab &&
        runState.hasFlag(FLAGS.STARTER_PICKED) &&
        runState.getVar('VAR_MAP_SCENE_PALLET_TOWN_PROFESSOR_OAKS_LAB') === '2' &&
        !runState.hasFlag(FLAGS.LAB_POST_PICK_DONE)
      ) {
        dispatchScript('__lab_post_pick__', this.scriptCtx());
      } else if (
        onOakLab &&
        runState.hasFlag(FLAGS.RIVAL_BATTLE_RESOLVED) &&
        !runState.hasFlag(FLAGS.RIVAL_POST_BATTLE_DONE)
      ) {
        dispatchScript('__rival_post_battle__', this.scriptCtx());
      }

      // Follower: re-derive position from player tile + facing. NO
      // send-out animation on WAKE (HGSS: the follower is already
      // beside you when the battle scene fades out).
      if (this.follower && runState.hasPartyMember()) {
        this.follower.onWake(
          { x: this.playerTileX, y: this.playerTileY },
          this.facing,
          this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
        );
      }
      // If a starter-pick fired while the scene was asleep (DraftScene
      // typical path), the subscriber deferred the Pokéball-pop spawn
      // to this WAKE handler. Trigger it now so the animation is
      // visible after the DraftScene closes.
      if (this.pendingFirstSpawnSpecies && this.follower) {
        const species = this.pendingFirstSpawnSpecies;
        this.pendingFirstSpawnSpecies = null;
        this.follower.spawn(
          { x: this.playerTileX, y: this.playerTileY },
          this.facing,
          species,
          this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
          this.firstSpawnPreferredTile(),
        );
      }
    });

    // Whole-game scaler cycler (owner ask 2026-08-04). Mounted here rather
    // than inside the duel flow so the pills are live in the overworld too —
    // the filter is a look for the WHOLE game now, and judging it only at the
    // duel's Z=6 was hiding how each algorithm behaves at the Z=2 the player
    // actually spends most of their time in.
    if (DUEL_SCENE_FILTER_ENABLED) this.mountDuelFilter();

    // Phase 7 M1 sanity check (deferred): the @pkmn/data + @pkmn/dex bundle is
    // ~5 MB raw (the full learnsets table), so we dynamic-import it AFTER the
    // initial paint. Phase 8a will move this call into the battle-scene boot.
    // Logging here just proves the lazy chunk loads without bloating the
    // critical-path bundle.
    void loadGen3().then((gen3) => {
      const poochyena = gen3.species.get('poochyena');
      if (poochyena) {
        console.log(
          `[pkmn] gen-3 ${poochyena.name}: types=${poochyena.types.join('/')} ` +
            `baseStats hp=${poochyena.baseStats.hp} atk=${poochyena.baseStats.atk}`,
        );
      }
    });
  }

  override update(_time: number, delta: number): void {
    if (Phaser.Input.Keyboard.JustDown(this.cKey)) this.toggleCollisionOverlay();

    // Drive the pathfinder before any movement reads so a path that
    // resolves this frame can push its first intent before
    // tryProcessMovement consumes it.
    this.pathfinder?.tick(delta);

    // Roam wanderers tick before the input funnel. Freeze gates live
    // here (not inside the system) so the contract is visible at the
    // call site: dialogue, transitions, and a pending battle launch
    // all freeze the grass; scene SLEEP during battles/modals stops
    // update() entirely, freezing it for free.
    if (!this.transitioning && !this.dialogueOpen && !this.encounterPending) {
      this.roamers?.update(delta);
    }

    // Whole-scene filter: push the live zoom + native-grid phase so the
    // shader recovers source texels from the framebuffer. worldView moves
    // with walking AND with the duel punch/pinch; phase is where the native
    // grid's texel boundaries fall (mod Z) in framebuffer px. Runs before the
    // duel early-return below because it must update DURING the duel — and
    // now also every overworld frame, where the camera scrolls continuously
    // and the phase changes on every step.
    if (this.filterCycler || this.duelFilterPipe) {
      const cam = this.cameras.main;
      const z = cam.zoom;
      // Zoom goes to the PILL even when no pipeline is attached: at algo 0
      // duelFilterPipe is null, and gating the push on it would leave the
      // readout showing a stale zoom for the first frame after the owner
      // cycles ON — exactly the moment they are reading it.
      this.filterCycler?.setZoom(z);
      if (this.duelFilterPipe) {
        const mod = (a: number, m: number): number => ((a % m) + m) % m;
        this.duelFilterPipe.zoom = z;
        this.duelFilterPipe.phaseX = mod(-cam.worldView.x * z, z);
        this.duelFilterPipe.phaseY = mod(-cam.worldView.y * z, z);
      }
    }

    // M2 duel regime: while a battle is staged over the awake world,
    // every world-side verb below (interact, movement) is the duel's,
    // not the player-avatar's. Battle chrome is DOM with its own
    // listeners; keyboard 1-4 belongs to the battle host.
    if (this.duelActive) return;

    if (this.transitioning) return;

    const interactPressed =
      Phaser.Input.Keyboard.JustDown(this.spaceKey) ||
      Phaser.Input.Keyboard.JustDown(this.enterKey);

    if (interactPressed) {
      this.handleInteract();
      return;
    }

    // Dialogue locks movement (A button + keyboard interact go through
    // handleInteract above — both close the dialogue when one's open).
    if (this.dialogueOpen) return;

    // Drive the in-flight lerp first. If a step finishes mid-tick, advanceStep
    // returns the leftover delta — we hand it straight to the next step (if
    // input is held) so the player walks at constant velocity across tile
    // boundaries instead of pausing for a frame. We loop because a single
    // long delta could in theory cover multiple tiles.
    let remaining = delta;
    while (this.moving && remaining > 0) {
      remaining = this.advanceStep(remaining);
      if (this.moving) break; // step still in progress
      // Step just ended — try to chain. tryProcessMovement decides whether to
      // start another step (input held + can step), set idle (input released
      // / blocked), or kick a connection transition (off-map).
      this.tryProcessMovement();
      // If side effects (encounter, transition) took control of the scene,
      // bail out so we don't keep advancing into a sleeping scene.
      if (this.transitioning || this.encounterPending || this.dialogueOpen) return;
    }
    if (this.moving) return;

    // No step in flight — read input fresh and decide what to do.
    this.tryProcessMovement();
  }

  /**
   * Seamless-world dispatcher (Step 3b). Routes a map-name + tile target
   * to the correct rendering path:
   *   - If `name` matches a region in the mega-map (e.g. "Route1") AND
   *     the world loaded successfully, enter exterior mode — render the
   *     mega-map, position player at the region's global coords.
   *   - Else (interior map name OR mega-map unavailable), enter interior
   *     mode via the existing per-map loadMap flow.
   *
   * Used by the resume-on-reload boot path + by transitionToMap's
   * post-warp dispatch.
   */
  private dispatchEnterMap(name: MapName, localX: number, localY: number): void {
    if (this.world && this.world.byName.has(name)) {
      this.enterExteriorRegion(name, localX, localY);
    } else {
      this.loadMap(name, localX, localY);
    }
  }

  /**
   * Switch to exterior (mega-map) mode and position the player at the
   * given local coords inside the named region. Mirrors loadMap's
   * obligations but renders the mega-map instead of a per-map tilemap.
   *
   * Effects:
   *   - mode ← 'exterior', currentRegion ← world.byName.get(name)
   *   - Hide any existing per-map tilemap; show mega-map layers
   *   - playerTileX/Y ← global coords (region.x + localX, region.y + localY)
   *   - Camera bounds ← whole world (so the player can walk across
   *     former boundaries with no clamp)
   *   - mapData ← synthesized shim that delegates to world + currentRegion
   *   - npcSprites + npcTiles ← built from ALL regions' events (global
   *     coords). Phaser frustum-culling handles off-screen sprites for free.
   *   - currentMapName ← short region name (matches position.mapName)
   *   - onMapEntered() fires per-region music + zone-draft hooks
   */
  private enterExteriorRegion(name: MapName, localX: number, localY: number): void {
    if (!this.world) {
      throw new Error('enterExteriorRegion called without a loaded world');
    }
    const region = this.world.byName.get(name);
    if (!region) {
      throw new Error(`enterExteriorRegion: region "${name}" not in world`);
    }

    // Tear down the prior per-map tilemap (if any). Mega-map layers were
    // built once at create-time and persist across mode switches.
    if (this.mode === 'interior' && this.tilemap) {
      this.tilemap.destroy();
    }

    this.mode = 'exterior';
    this.currentRegion = region;
    this.currentMapName = name;
    this.mapData = synthesizeExteriorMapData(this.world, region);

    // Show mega-map layers (Step 2 left them invisible).
    this.world.groundLayer.setVisible(true);
    this.world.aboveLayer.setVisible(true);
    this.tilemap = this.world.tilemap;
    this.layer = this.world.groundLayer;
    this.aboveLayer = this.world.aboveLayer;

    // Camera bounds + bg via applyCameraBounds — exterior path clamps at
    // mega-map edges (cartridge edge behavior). Player can walk across
    // former per-map boundaries with no per-map clamp — that IS the
    // seamless effect.
    this.applyCameraBounds();

    // Position player at global coords. Local input is region-relative
    // (matches the convention used by warps + boot resume).
    const globalX = region.x + localX;
    const globalY = region.y + localY;
    this.playerTileX = globalX;
    this.playerTileY = globalY;
    const tileSize = this.world.tileSize;
    this.player.setPosition((globalX + 0.5) * tileSize, (globalY + 0.5) * tileSize);

    // Build NPC sprites + occupancy set from EVERY exterior region's
    // events (already in global coords on the synthesized mapData). Phaser
    // frustum-culling skips off-screen sprites for free; v0.1's ~15 NPCs
    // are far below any performance concern.
    this.rebuildNpcSprites();

    // Snap the camera to the player (setBounds alone leaves scroll at
    // the previous value — camera follow only updates on the NEXT frame,
    // and even then can stall during the in-flight doorTransition fade).
    // centerOn jumps the camera immediately so the fade-in reveals the
    // correct region. syncUiCameraIgnores re-binds the world-camera
    // ignore list to the new npcSprites + layers, and repositionAll
    // re-computes zoom for the new (whole-world) map dimensions.
    this.cameras.main.centerOn(this.player.x, this.player.y);
    // Roam: populate this region's cast (gated inside — mode, starter,
    // table, live-group continuity). Before syncUiCameraIgnores so the
    // new sprites land on the UI camera's ignore list this pass.
    this.maybePopulateRoamers();

    this.syncUiCameraIgnores();
    this.repositionAll();

    // Mirror the per-map music switch from loadMap — playMusic is a
    // no-op when the same key is already current.
    const musicKey = MAP_MUSIC[name];
    if (musicKey) void audio.playMusic(this, musicKey);

    this.onMapEntered();
  }

  /**
   * Boundary-cross detector (seamless-world Step 3b). Called from
   * advanceStep after every step completes. No-op in interior mode and
   * when the player stays inside the cached region (regionAt's lastHit
   * cache makes this O(1) in the common case).
   *
   * On cross: notify the regions module (Step 4 will add music crossfade
   * + route placard inside crossIntoRegion's body), update the synthesized
   * mapData + currentMapName so encounter / draft / scriptRef lookups
   * consult the new region, hard-cut music, then fire per-region hooks.
   *
   * The pre-starter Route 1 gate + pending-draft restore + zone-draft
   * trigger all live in onMapEntered; firing it again on cross keeps
   * those gates working at boundaries.
   */
  private detectRegionCross(): void {
    if (this.mode !== 'exterior' || !this.world) return;
    const next = regionAt(this.world, this.playerTileX, this.playerTileY);
    if (!next || next === this.currentRegion) return;
    const prev = this.currentRegion;
    crossIntoRegion(this.world, next, prev);
    this.currentRegion = next;
    this.mapData = synthesizeExteriorMapData(this.world, next);
    this.currentMapName = next.name as MapName;
    // Music crossfade — playMusic is a no-op when the destination matches
    // the currently-playing key (silent crosses between adjacent regions
    // sharing a BGM key — P3 specifies this). Default fadeMs=800 matches
    // the design doc's equal-power crossfade window.
    const musicKey = MAP_MUSIC[next.name];
    if (musicKey) void audio.playMusic(this, musicKey);
    // Trailing route placard — fades in AFTER the cross (never leads).
    this.showRoutePlacard(formatRegionName(next.name));
    // Roam: crossing into a region populates its cast (no-op when a
    // live group survives from an earlier visit — border-hop
    // continuity). New sprites need a fresh UI-camera ignore pass.
    this.maybePopulateRoamers();
    this.syncUiCameraIgnores();
    this.onMapEntered();
  }

  /**
   * Display the route-name pill at the UI camera's top-left for ~2 s.
   * Fade in 200 ms / dwell 1800 ms / fade out 200 ms. Under reduced-motion
   * preference: instant on/off, dwell unchanged (dwell is the reading
   * window, not animation). Idempotent under rapid re-fire — calling it
   * again before the previous placard finishes cancels the old one and
   * supersedes it cleanly.
   */
  private showRoutePlacard(text: string): void {
    // Cancel + tear down any prior placard cleanly so rapid region
    // re-crosses don't leak orphan DOM elements. Both the active
    // element and any pending fade-out timer get cleared.
    this.disposeRoutePlacard();

    const parent = document.getElementById('game') ?? document.body;
    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.textContent = text;
    el.style.position = 'absolute';
    el.style.top = '16px';
    el.style.left = '16px';
    el.style.zIndex = String(Z_ROUTE_PLACARD);
    el.style.padding = '8px 12px';
    el.style.borderRadius = '4px';
    el.style.background = 'var(--glass-fill-mid)';
    el.style.backdropFilter = 'blur(var(--glass-blur)) saturate(var(--glass-saturate))';
    (el.style as unknown as { webkitBackdropFilter: string }).webkitBackdropFilter =
      'blur(var(--glass-blur)) saturate(var(--glass-saturate))';
    el.style.boxShadow = [
      'inset 0 1px 0 var(--specular-edge)',
      'var(--depth-shadow)',
    ].join(', ');
    el.style.color = 'var(--text-secondary)';
    el.style.fontFamily =
      "'DM Sans Variable', 'DM Sans', system-ui, -apple-system, sans-serif";
    el.style.fontSize = '13px';
    el.style.fontWeight = '600';
    el.style.letterSpacing = '0.04em';
    el.style.textTransform = 'uppercase';
    el.style.pointerEvents = 'none';
    el.style.userSelect = 'none';
    (el.style as unknown as { webkitUserSelect: string }).webkitUserSelect = 'none';
    el.style.willChange = 'opacity';

    const reduced = effectsReduced();
    const fadeIn = reduced ? 0 : 200;
    const dwell = 1800;
    const fadeOut = reduced ? 0 : 200;

    if (reduced) {
      el.style.opacity = '1';
    } else {
      el.style.opacity = '0';
      el.style.transition = `opacity ${fadeIn}ms cubic-bezier(0.33, 1, 0.68, 1)`;
    }

    parent.appendChild(el);
    this.routePlacard = el;

    if (!reduced) {
      // Next-frame opacity flip so the transition actually animates
      // (matches the pokeballMenu popover entry pattern).
      requestAnimationFrame(() => {
        if (this.routePlacard !== el) return; // superseded
        el.style.opacity = '1';
      });
    }

    const scheduleFadeOut = (): void => {
      if (this.routePlacard !== el) return; // superseded
      if (!reduced) {
        el.style.transition = `opacity ${fadeOut}ms cubic-bezier(0.32, 0, 0.67, 0)`;
      }
      el.style.opacity = '0';
      this.routePlacardTimer = setTimeout(() => {
        this.routePlacardTimer = null;
        // Defensive: another cross may have superseded; only tear down
        // the element if it's still the active one.
        if (this.routePlacard !== el) {
          if (el.parentNode) el.parentNode.removeChild(el);
          return;
        }
        if (el.parentNode) el.parentNode.removeChild(el);
        this.routePlacard = null;
      }, fadeOut);
    };

    this.routePlacardTimer = setTimeout(scheduleFadeOut, fadeIn + dwell);
  }

  /** Tear down the active placard + cancel any pending fade-out timer.
   *  Called by showRoutePlacard (rapid re-fire) and by SHUTDOWN. */
  private disposeRoutePlacard(): void {
    if (this.routePlacardTimer !== null) {
      clearTimeout(this.routePlacardTimer);
      this.routePlacardTimer = null;
    }
    if (this.routePlacard) {
      const el = this.routePlacard;
      this.routePlacard = null;
      if (el.parentNode) el.parentNode.removeChild(el);
    }
  }

  /**
   * Read movement intent (cursors + D-pad) and either start a step, set
   * the idle frame, or trigger an edge-connection transition. Called from
   * update() each tick (when not stepping) AND immediately after a step
   * completes to chain the next one within the same Phaser tick.
   */
  private tryProcessMovement(): void {
    if (this.moving || this.transitioning || this.dialogueOpen || this.encounterPending) return;
    if (this.openModalSources.size > 0) return;
    // Follower send-out lock — the ~1s Pokéball-pop animation can't be
    // interrupted by player input. Matches cartridge HGSS behavior:
    // first map entry / lead change pauses the player while the new
    // Pokémon emerges.
    if (this.follower?.sendOutInProgress) return;

    // Phaser's CursorKeys are still the authoritative keyboard source;
    // we just route their per-frame down state into the shared intent
    // funnel before reading. setIntent is idempotent on repeats so
    // polling each frame is fine.
    this.syncKeyboardIntent();

    // Manual-input takeover: if the user grabs the joystick, D-pad, or
    // keyboard while a tap-step pathfind is in flight, cancel the path
    // so the manual source has uncontested control. Bug 3's "tap-on-
    // far-NPC" flow puts DIRECT mode users on the same pathfind path
    // as COMMAND, and they expect the joystick to immediately override.
    if (this.pathfinder) {
      const pfState = this.pathfinder.getState().kind;
      if (pfState === 'walking' || pfState === 'planning') {
        if (
          intentRouter.isSourceActive('joystick') ||
          intentRouter.isSourceActive('classic-dpad') ||
          intentRouter.isSourceActive('keyboard')
        ) {
          this.pathfinder.cancel();
        }
      }
    }

    let dx = 0;
    let dy = 0;
    // Arrow keys + D-pad + (later) joystick + tap-to-walk all OR through
    // intentRouter.dpadDown. Left/right takes priority over up/down to
    // match the original arrow-key precedence.
    if (intentRouter.dpadDown.left) dx = -1;
    else if (intentRouter.dpadDown.right) dx = 1;
    else if (intentRouter.dpadDown.up) dy = -1;
    else if (intentRouter.dpadDown.down) dy = 1;

    if (dx === 0 && dy === 0) {
      this.setPlayerSpriteFrame('idle');
      return;
    }

    // Track facing on intent — classic Pokémon turns the player even on a blocked step.
    const prevFacing = this.facing;
    this.facing = this.directionFor(dx, dy);
    // Sync the follower's facing so its sprite frame matches even on
    // turn-in-place (player faces wall, no step fires). onPlayerStepStart
    // would also update facing on a real step, but that path is gated
    // on canStep below; the explicit hook here covers the no-step case.
    if (this.facing !== prevFacing) {
      this.follower?.onPlayerFacingChange(this.facing);
    }
    // Resume-on-reload: facing change without a tile change still wants
    // to be persisted (player turned into a wall, then closes the tab —
    // they'd expect to spawn facing the same direction on reload).
    if (this.currentMapName) {
      this.persistPlayerPosition();
    }

    // Arrow-warp step intent: pressing the arrow direction while standing
    // ON an arrow-warp tile fires the warp directly. Without this, the
    // user can side-step onto a doormat and then be unable to walk south
    // off it (the tile south of the rug is collision=1, so the normal
    // step would be blocked and the post-step warp check never runs).
    // Mirrors cartridge: the rug is a threshold, "walk off the rug in
    // the arrow direction" exits.
    if (this.checkWarp('intent')) return;

    const nx = this.playerTileX + dx;
    const ny = this.playerTileY + dy;

    if (nx < 0 || ny < 0 || nx >= this.mapData.width || ny >= this.mapData.height) {
      const conn = this.mapData.connections.find((c) => c.direction === this.facing);
      if (conn) this.attemptConnectionTransition(conn, nx, ny);
      else this.setPlayerSpriteFrame('idle');
      return;
    }

    // Ledge check: if the tile in front is a ledge whose direction matches
    // our facing, hop *over* it onto the tile beyond. Ledges always have
    // collision=1 in the map data (so the normal canStep below would block
    // them) — the hop bypasses that. Diagonal corners (SE etc.) accept
    // either of their two cardinal directions.
    const targetBehavior = this.mapData.behaviors[ny * this.mapData.width + nx] ?? 0;
    if (isLedgeJumpable(targetBehavior, this.facing)) {
      const landX = nx + dx;
      const landY = ny + dy;
      const inBounds =
        landX >= 0 && landY >= 0 &&
        landX < this.mapData.width && landY < this.mapData.height;
      if (inBounds) {
        // Roam: hopping a ledge INTO a roamer's tile engages it —
        // symmetric with the walk-bump below, and it beats landing
        // inside the sprite. Blocking query covers a mid-step
        // roamer's source tile too.
        const hopRoamer = this.roamers?.roamerBlockingAt(landX, landY);
        if (hopRoamer) {
          if (this.controlMode === 'command') {
            this.pathfinder?.cancel();
            this.setPlayerSpriteFrame('idle');
            return;
          }
          void this.engageRoamer(hopRoamer);
          return;
        }
        if (this.canStep(landX, landY)) {
          this.hopTo(landX, landY);
          return;
        }
      }
      // Landing tile blocked or off-map — fall through to the normal
      // collision path, which will refuse the step.
    }

    // Roam: bumping into a visible wild. In DIRECT/Classic the bump is
    // the player's own steering — engagement (player-initiated only;
    // roamers never step onto the player, so this intercept is the
    // ONLY walk-contact path and the player stays on their tile,
    // cartridge-style). In COMMAND the step came from a planned path,
    // so a roamer that ambled onto the corridor mid-walk is NOT a
    // chosen contact — halt-and-replan like any moved NPC (adversarial
    // review bug 2); deliberate COMMAND engagement goes through the
    // tap → approach → interact flow instead.
    const bumped = this.roamers?.roamerBlockingAt(nx, ny);
    if (bumped) {
      if (this.controlMode === 'command') {
        this.pathfinder?.cancel();
        this.setPlayerSpriteFrame('idle');
        return;
      }
      void this.engageRoamer(bumped);
      return;
    }

    if (this.canStep(nx, ny)) {
      this.stepTo(nx, ny);
    } else {
      // Wall in front — turn to face it but stay idle.
      this.setPlayerSpriteFrame('idle');
    }
  }

  /** Push the current Phaser cursor state into intentRouter under the
   *  'keyboard' source. Called once per movement check. Step 2 of the
   *  touch-controls plan funnels every input source through one OR view;
   *  this is the keyboard end of that funnel. */
  private syncKeyboardIntent(): void {
    intentRouter.setIntent('left', this.cursors.left.isDown, 'keyboard');
    intentRouter.setIntent('right', this.cursors.right.isDown, 'keyboard');
    intentRouter.setIntent('up', this.cursors.up.isDown, 'keyboard');
    intentRouter.setIntent('down', this.cursors.down.isDown, 'keyboard');
  }

  /**
   * Begin a tile step to (nx, ny). The logical tile position advances
   * immediately (so canStep against the new state works for the *next*
   * step), and the visual lerp + side effects are advanced inside update()
   * over STEP_COOLDOWN_MS. Side effects (status, triggers, encounter)
   * fire when the lerp completes — same semantics as the old onComplete.
   */
  private stepTo(nx: number, ny: number): void {
    // Follower hook BEFORE updating playerTileX/Y so the wrapper sees
    // the player's previous tile as oldTile — that's the follower's
    // target (cartridge HGSS: follower walks to the tile the player
    // just vacated).
    this.follower?.onPlayerStepStart(
      { x: this.playerTileX, y: this.playerTileY },
      { x: nx, y: ny },
      this.facing,
      this.time.now,
    );

    this.playerTileX = nx;
    this.playerTileY = ny;
    this.persistPlayerPosition();
    this.moving = true;
    this.stepCount++;
    this.setPlayerSpriteFrame('walk');
    const t = this.mapData.tileSize;
    this.stepStartX = this.player.x;
    this.stepStartY = this.player.y;
    this.stepTargetX = (nx + 0.5) * t;
    this.stepTargetY = (ny + 0.5) * t;
    this.stepElapsedMs = 0;
  }

  /**
   * Advance the in-flight tile step (or ledge hop) by `delta` ms. Returns
   * the leftover delta if the step finished mid-frame (so a chained next
   * step can consume it instead of waiting a tick — eliminates the
   * one-frame stall that used to manifest as "every step has a noticeable
   * stop"). Hops use HOP_DURATION_MS instead of STEP_COOLDOWN_MS and
   * overlay a sin-arc on y for the leap bounce.
   */
  private advanceStep(delta: number): number {
    this.stepElapsedMs += delta;
    const duration = this.hopActive ? HOP_DURATION_MS : STEP_COOLDOWN_MS;
    if (this.stepElapsedMs < duration) {
      const t = this.stepElapsedMs / duration;
      const x = this.stepStartX + (this.stepTargetX - this.stepStartX) * t;
      let y = this.stepStartY + (this.stepTargetY - this.stepStartY) * t;
      if (this.hopActive) {
        // Bounce arc — peaks at midpoint, returns to baseline at the end.
        // Subtracted because Phaser y grows downward.
        y -= Math.sin(t * Math.PI) * HOP_ARC_PIXELS;
      }
      this.player.setPosition(x, y);
      // Drive the follower's walk in lock-step with the player's step
      // progress. No-op when the follower isn't mid-step (hop is its
      // own Phaser tween; idle / send-out / dialogue do nothing).
      // This eliminates the parallel-tween timing drift that caused
      // the rubber-band lag on chained walks.
      this.follower?.updateWalkProgress(t);
      return 0;
    }
    // Finished — snap to target and fire side effects.
    this.player.setPosition(this.stepTargetX, this.stepTargetY);
    const overshoot = this.stepElapsedMs - duration;
    this.moving = false;
    this.hopActive = false;
    // Follower step/hop tween completes in lock-step with the player's
    // (Follower.beginWalkTween uses the same 140ms / 280ms durations).
    // Notify so FollowerState commits targetTile → tileX/tileY for the
    // next observation cycle.
    this.follower?.onPlayerStepComplete();
    // Seamless-world Step 3b: exterior region cross detection. Updates
    // currentRegion + mapData shim + music BEFORE checkTriggers etc. so
    // the side-effect checks below run against the new region's identity.
    // No-op in interior mode.
    this.detectRegionCross();
    this.updateStatusText();
    this.checkTriggers();
    // Notify the COMMAND-mode pathfinder so it can push the next
    // tile's direction into intentRouter *before* tryProcessMovement
    // reads it on the same tick (eliminates the one-frame gap that
    // would otherwise show up as a stutter every tile).
    this.pathfinder?.onStepComplete(this.playerTileX, this.playerTileY);
    // Warp check first — if the landing tile is a door / staircase, transition.
    // Trainer LOS check second — landing in a trainer's facing cone fires
    // before grass encounters so a wild Pokémon can't pre-empt a trainer
    // who was watching that very tile. Encounter check runs only if neither
    // warp nor trainer fired (otherwise we'd race a battle launch against
    // a scene transition or a trainer encounter).
    if (this.checkWarp()) return overshoot;
    // PR 13a (H5): coord-event triggers fire BEFORE trainer LOS + grass
    // encounter — cartridge cutscene priority. Oak intercept on Route 1
    // (PR 13b) is the v0.1 consumer; future cutscenes hook here without
    // touching the rest of advanceStep. Handlers self-gate one-shot via
    // their own FLAG_*_DONE flags.
    if (this.checkCoordTrigger()) return overshoot;
    if (this.checkTrainerLOS()) return overshoot;
    return overshoot;
  }

  /** PR 13a (H5): dispatch a coord-event trigger when the player's
   *  landing tile matches one. Returns true when a trigger fired so
   *  callers can short-circuit downstream side effects (LOS, encounter
   *  rolls) — the cinematic gets exclusive control for its duration.
   *  Gated by `transitioning` like checkWarp — a trigger that lands
   *  mid-transition is a runtime contradiction; bail. */
  private checkCoordTrigger(): boolean {
    if (this.transitioning) return false;
    const triggers = this.mapData?.events?.triggers;
    if (!triggers || triggers.length === 0) return false;
    const t = findTriggerAtTile(
      { x: this.playerTileX, y: this.playerTileY },
      triggers,
      // Cartridge-default semantic: unset var reads as 0. runState.getVar
      // returns `string | undefined`; coerce to number with 0 fallback.
      (name) => {
        const raw = runState.getVar(name);
        return raw === undefined ? 0 : Number(raw);
      },
    );
    if (!t) return false;
    // Hand off to scriptRunner — handler self-gates on the appropriate
    // FLAG_*_DONE flag and self-clears it on completion.
    dispatchScript(t.scriptRef, this.scriptCtx());
    return true;
  }

  /**
   * If the player's landing tile is a warp, kick a transition to its
   * destination map + warp position. Returns true when a warp was fired
   * (so callers can short-circuit other side effects). Maps not yet
   * registered in MAP_ID_TO_NAME silently no-op (e.g., warps to interiors
   * we haven't converted yet).
   *
   * Pass the player's current facing as `entryFacing` so the destination
   * scene can auto-step one tile out of the doorway (cartridge behavior:
   * you exit a building south of the door, not standing on it; you enter
   * a building one tile north of the rug, not standing on it). Without
   * this, you spawn on the door / rug tile itself and the side walls
   * block left/right movement until you step away.
   */
  private checkWarp(context: 'intent' | 'landing' = 'landing'): boolean {
    if (this.transitioning) return false;
    const w = this.mapData.warps.find((w) => w.x === this.playerTileX && w.y === this.playerTileY);
    if (!w) return false;
    // Cartridge semantics: fire only when the tile's METATILE BEHAVIOR is
    // a warp behavior. Tiles in `warps[]` with MB_NORMAL behavior are
    // leniency / spawn metadata, NOT triggers — without this gate,
    // sidestepping onto the tiles flanking a doormat would exit the
    // building.
    //
    // Arrow warps additionally fire on step INTENT (player presses the
    // arrow's direction while standing on the rug) and NOT on landing —
    // walking onto a rug doesn't exit by itself; you walk OFF it in the
    // arrow direction. Non-arrow warps (stairs / escalators / regular
    // warps / cave doors) still fire on landing in any direction, since
    // their cartridge semantic is "the moment you step on it." That
    // split is the only behavioral difference between the two contexts.
    const idx = this.playerTileY * this.mapData.width + this.playerTileX;
    const behavior = this.mapData.behaviors[idx] ?? 0;
    if (context === 'landing' && isArrowWarp(behavior)) return false;
    if (!behaviorFiresWarp(behavior, this.facing)) return false;
    const destName = MAP_ID_TO_NAME[w.destinationMap];
    if (!destName) return false;
    const destData = this.cache.json.get(`map:${destName}`) as RuntimeMap | undefined;
    if (!destData) return false;
    const destWarp = destData.warps[w.destinationWarp];
    if (!destWarp) return false;
    void this.transitionToMap(destName, destWarp.x, destWarp.y, this.facing);
    return true;
  }

  /**
   * Begin a 2-tile ledge hop landing at (landX, landY). The player skips
   * the ledge tile entirely (logical position jumps straight to the
   * landing tile), so triggers / encounters fire on the landing tile only
   * — matching pokeemerald, where you never "stand" on a ledge.
   *
   * The sprite's frame is forced to walkA (leg-extended) for the hop's
   * duration so it visibly looks like a leap, instead of cycling through
   * the normal walk frames during the hop.
   */
  private hopTo(landX: number, landY: number): void {
    // Follower hook BEFORE updating playerTileX/Y so the wrapper sees
    // the pre-hop tile as oldTile. Sets hopPending — the 1-step delay
    // means the follower's hop animation fires on the NEXT
    // onPlayerStepStart (cartridge HGSS convention).
    this.follower?.onPlayerHopStart(
      { x: this.playerTileX, y: this.playerTileY },
      { x: landX, y: landY },
    );

    this.playerTileX = landX;
    this.playerTileY = landY;
    this.persistPlayerPosition();
    this.moving = true;
    this.hopActive = true;
    const t = this.mapData.tileSize;
    this.stepStartX = this.player.x;
    this.stepStartY = this.player.y;
    this.stepTargetX = (landX + 0.5) * t;
    this.stepTargetY = (landY + 0.5) * t;
    this.stepElapsedMs = 0;
    // Lock onto a single leg-extended frame for the leap (don't cycle).
    const walkBase =
      this.facing === 'down' ? 3 : this.facing === 'up' ? 5 : 7;
    this.player.setFrame(walkBase);
    this.player.setFlipX(this.facing === 'right');
  }

  /**
   * Phase 12 M4: trainer line-of-sight check. After every step, scan all
   * non-defeated trainers on the current map; if the player's tile sits
   * inside any trainer's facing cone (extending sight-range tiles in their
   * facing direction, stopping at collision), launch the trainer encounter.
   *
   * Returns true when an encounter fired so the caller can short-circuit
   * grass encounter rolls (avoids racing two battle launches).
   *
   * v0.1 honors only static facers (FACE_DOWN/UP/LEFT/RIGHT + the paired
   * FACE_DOWN_AND_UP variant). Wandering trainers (MOVEMENT_TYPE_WANDER_*)
   * don't fire LOS — their facing changes per tick and modeling that
   * faithfully isn't worth the complexity for v0.1. They can still be
   * battled by walking up and pressing A.
   */
  private checkTrainerLOS(): boolean {
    if (
      this.transitioning ||
      this.encounterPending ||
      this.dialogueOpen ||
      this.moving
    )
      return false;

    for (const npc of this.mapData.events.npcs) {
      // Same defensive read as loadMap — clone-type entries lack
      // trainerType/flag/movementType. They're tree clones / item clones,
      // not trainers, but a missing field would crash the iteration.
      if (!npc.trainerType || npc.trainerType === 'TRAINER_TYPE_NONE') continue;
      if (!npc.scriptRef) continue;
      const trainer = getTrainer(npc.scriptRef);
      if (!trainer) continue; // No party data; skip until M4.1+ rosters them
      if (runState.hasDefeatedTrainer(npc.scriptRef)) continue;
      if (npcIsHidden(npc)) continue;

      const facing = facingFromMovementType(npc.movementType);
      if (!facing) continue;

      const sightRange = npc.trainerSightOrBerryTreeId ?? 0;
      if (sightRange <= 0) continue;

      // Trainer's owning region in exterior mode — used to clamp the LOS
      // raycast at the region's AABB. Per the design doc P3 NPC-region
      // binding: a trainer cone does NOT extend into a neighboring region
      // even if the geometry would otherwise allow it. (Cartridge has no
      // analog because per-map LOS was capped at the map edge for free.)
      let trainerRegion: Region | null = null;
      if (this.mode === 'exterior' && this.world) {
        for (const r of this.world.regions) {
          if (
            npc.x >= r.x && npc.x < r.x + r.w &&
            npc.y >= r.y && npc.y < r.y + r.h
          ) {
            trainerRegion = r;
            break;
          }
        }
      }

      // Cast the cone one tile at a time. Stop at first impassable cell.
      // canStep checks collision + impassable behaviors + NPC tiles, so a
      // wall, water, or another NPC standing in the cone breaks LOS.
      let dx = 0,
        dy = 0;
      switch (facing) {
        case 'up': dy = -1; break;
        case 'down': dy = 1; break;
        case 'left': dx = -1; break;
        case 'right': dx = 1; break;
      }
      for (let step = 1; step <= sightRange; step++) {
        const tx = npc.x + dx * step;
        const ty = npc.y + dy * step;
        if (tx < 0 || ty < 0 || tx >= this.mapData.width || ty >= this.mapData.height) break;
        // Region-bound guard: in exterior mode, terminate the raycast at
        // the trainer's owning region's AABB boundary. Half-open.
        if (
          trainerRegion &&
          (tx < trainerRegion.x ||
            tx >= trainerRegion.x + trainerRegion.w ||
            ty < trainerRegion.y ||
            ty >= trainerRegion.y + trainerRegion.h)
        ) {
          break;
        }
        if (tx === this.playerTileX && ty === this.playerTileY) {
          // Cartridge run-up: the trainer closes the gap to the tile
          // adjacent to the player before the intro line. Every cone
          // tile up to here passed canStep, so the lane is clear.
          const runupTo =
            step > 1 ? { x: tx - dx, y: ty - dy } : undefined;
          void this.launchTrainerEncounter(npc, trainer, { runupTo });
          return true;
        }
        if (!this.canStep(tx, ty)) break;
      }
    }
    return false;
  }

  /**
   * Trainer encounter flow:
   *   1. Lock movement (set encounterPending)
   *   2. Open dialogue with trainer's intro line
   *   3. On dialogue close, launch BattleScene with trainer party
   *
   * LOS trips pass `sighting` and get the full cartridge grammar:
   * '!' emote pop above the head (SE_PIN ping) → beat → RUN the gap
   * (`sighting.runupTo`, when not already adjacent) → intro line.
   * Interact-launched trainers (Brock) pass no sighting — you walked
   * up to THEM; no '!' and no run, straight to the intro line.
   * Playtest rounds 1+2, 2026-08-02.
   */
  private async launchTrainerEncounter(
    npc: RuntimeNpc,
    trainer: TrainerData,
    sighting?: { runupTo?: { x: number; y: number } },
  ): Promise<void> {
    this.encounterPending = true;
    if (sighting) {
      // The player hears the '!' and turns to meet the challenge —
      // cartridge beat, and it sets up the face-off framing (owner
      // ask 2026-08-03).
      this.facing = this.directionFor(
        Math.sign(npc.x - this.playerTileX),
        Math.sign(npc.y - this.playerTileY),
      );
      this.player.setFlipX(this.facing === 'right');
      this.setPlayerSpriteFrame('idle');
      await this.playTrainerAlertEmote(npc);
    }
    let runupTo = sighting?.runupTo;
    if (runupTo && IN_WORLD_DUEL_ENABLED) {
      // In-world duels: a distant challenger stops THREE tiles out —
      // enough corridor for both leads to step between you — instead
      // of marching all the way up (owner ask 2026-08-03). Closer
      // sightings keep their distance (side-by-side staging covers
      // adjacency); the run-up only ever shortens, never lengthens.
      const dx = Math.sign(npc.x - this.playerTileX);
      const dy = Math.sign(npc.y - this.playerTileY);
      const gap =
        Math.abs(npc.x - this.playerTileX) + Math.abs(npc.y - this.playerTileY);
      if (gap > 3) {
        runupTo = {
          x: this.playerTileX + dx * 3,
          y: this.playerTileY + dy * 3,
        };
      } else {
        runupTo = undefined; // already close enough — stand and fight
      }
    }
    if (runupTo && (runupTo.x !== npc.x || runupTo.y !== npc.y)) {
      const key = npc.localId ?? `__anon_${npc.x}_${npc.y}`;
      const sprite = this.npcSprites.get(key);
      if (sprite instanceof Phaser.GameObjects.Sprite) {
        try {
          await this.npcWalkAlongPath({
            spriteKey: sprite.texture.key, // ignored with existingSprite
            existingSprite: sprite,
            startTile: { x: npc.x, y: npc.y },
            waypoints: [runupTo],
            pixelsPerSecond: 128, // trainers RUN — 4 tiles/s
          });
          // Durable-in-session position: npcTiles (blocking + pathfinder
          // avoid points) and any sprite rebuild see the approach tile.
          // Resets on map reload — cartridge keeps them at their post
          // across re-entries too, but in-session-only is the cheap 95%.
          this.npcTiles.delete(npc.y * this.mapData.width + npc.x);
          npc.x = runupTo.x;
          npc.y = runupTo.y;
          this.npcTiles.add(npc.y * this.mapData.width + npc.x);
        } catch {
          // Walk failed (shape drift, sprite yanked) — the battle still
          // fires from the original spot; run-up is presentation only.
        }
      }
    }
    this.showDialogue([trainer.introLine], `__trainer__:${npc.scriptRef}`);
    // Dialogue close fires runDialogueAction with this special prefix; we
    // route to launchTrainerBattle from there to keep the dialogue-then-
    // action pattern unified with the nurse heal path.
  }

  /** The cartridge trainer-sighted beat: '!' pops above the trainer's
   *  head (sGfx_Emoticons frames 0→1→2, the authentic two-step pop-in)
   *  with the SE_PIN ping, holds, then vanishes before the run starts.
   *  The sprite joins handlerOwnedSprites so the UI camera ignores it
   *  (the f132523 double-render fix path) and bracket cleanup could
   *  never leak it. Reduced motion: frame 2 immediately, shorter hold. */
  private async playTrainerAlertEmote(npc: RuntimeNpc): Promise<void> {
    const ts = this.mapData.tileSize;
    const reduced = effectsReduced();
    const emote = this.add
      .sprite((npc.x + 0.5) * ts, (npc.y - 0.9) * ts, 'emote_exclamation', reduced ? 2 : 0)
      .setOrigin(0.5, 0.5)
      .setDepth(6);
    this.handlerOwnedSprites.add(emote);
    emote.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.handlerOwnedSprites.delete(emote);
    });
    this.syncUiCameraIgnores();
    audio.playSfx('alert');
    const holdMs = reduced ? EMOTE_HOLD_REDUCED_MS : EMOTE_HOLD_MS;
    await new Promise<void>((resolve) => {
      const finish = (): void => {
        emote.destroy();
        resolve();
      };
      if (reduced) {
        this.time.delayedCall(holdMs, finish);
        return;
      }
      // Cartridge pop cadence: 4 GBA frames (~67ms) per step, then hold.
      this.time.delayedCall(67, () => emote.setFrame(1));
      this.time.delayedCall(134, () => emote.setFrame(2));
      this.time.delayedCall(134 + holdMs, finish);
    });
  }

  /**
   * pokeemerald 9-frame walking-strip layout (per
   * data/object_events/object_event_anims.h, gObjectEventImageAnimTable_StandardFour):
   *
   *   0 = stand south (down idle / passing)
   *   1 = stand north (up idle / passing)
   *   2 = stand west  (left idle / passing; right uses this with hFlip)
   *   3, 4 = walk south  (down — left-leg / right-leg forward)
   *   5, 6 = walk north  (up)
   *   7, 8 = walk west   (left; right flips horizontally)
   *
   * pokeemerald walk cycle (e.g. gObjectEventImageAnim_GoWest):
   *   walk-leg-A → idle/passing → walk-leg-B → idle/passing → repeat
   *
   * The "passing" pose is the feet-together stance you pass through between
   * leg extensions. For down/up walking the two leg-extension frames look
   * visibly different so skipping the passing pose still reads OK, but the
   * side-walk frames (7 and 8) are subtle mirrors of each other — without
   * the passing frame in between they look like the legs are wiggling rather
   * than walking. So we cycle through the full 4-frame pattern for every
   * direction, matching the original game.
   *
   * stepCount % 4 sequence: idle, walkA, idle, walkB → first step shows walkA.
   */
  private setPlayerSpriteFrame(state: 'idle' | 'walk'): void {
    if (!this.player) return;
    const idle = this.facing === 'down' ? 0 : this.facing === 'up' ? 1 : 2;
    let frame: number;
    if (state === 'idle') {
      frame = idle;
    } else {
      const walkBase =
        this.facing === 'down' ? 3 : this.facing === 'up' ? 5 : 7;
      const cycle = [idle, walkBase, idle, walkBase + 1];
      frame = cycle[this.stepCount % 4]!;
    }
    this.player.setFrame(frame);
    // West frames (idle 2 + walks 7,8) face *left*; we flip horizontally to
    // turn them into east-facing. Down/up frames don't need flipping.
    this.player.setFlipX(this.facing === 'right');
  }

  /**
   * Persist the player's current tile + facing to runState (the resume-
   * on-reload source of truth). In exterior mode the on-screen
   * playerTileX/Y are GLOBAL mega-map coords (set by enterExteriorRegion
   * as `region.x + localX`); the save must store LOCAL coords so that
   * enterExteriorRegion's add-back on resume doesn't double the offset
   * and drop the player into the off-region void (the "border woods"
   * regression — caught when the user resumed and reported their avatar
   * invisible in unwalkable territory). In interior mode there's no
   * region offset to subtract; playerTileX/Y are already map-local.
   *
   * No-op when currentMapName isn't set yet (pre-loadMap).
   */
  private persistPlayerPosition(): void {
    if (!this.currentMapName) return;
    let tileX = this.playerTileX;
    let tileY = this.playerTileY;
    if (this.mode === 'exterior' && this.currentRegion) {
      tileX -= this.currentRegion.x;
      tileY -= this.currentRegion.y;
    }
    runState.setPosition({
      mapName: this.currentMapName,
      tileX,
      tileY,
      facing: this.facing,
    });
  }

  /**
   * After every successful step, log any coord_events whose (x, y) match the
   * player's new tile. Phase 4 only logs — real trigger behavior (state-gated
   * cutscenes, route-draft on first grass step, etc.) lands in Phase 9+.
   *
   * Multiple triggers can sit on the same tile, gated by var/var_value. We
   * print all matches so the eventual dispatcher (which will check the var
   * state at fire time) has a clear picture of what's available.
   */
  /**
   * Phase 9 M2 dispatch — also wired to the V debug key.
   *
   * Empty party: launch the starter draft (one-shot for v0.1; the wider
   * route-draft system in Phase 9 M3 fires per-route on first entry).
   * Otherwise: launch a wild battle with party[0] vs a placeholder
   * Wild Poochyena Lv 4 (matches Phase 0b spike (a)'s opponent).
   *
   * scene.sleep (not pause) so the world camera stops rendering while the
   * other scene is up — pause halts update() only, leaving the overworld
   * composited under the new scene.
   */
  /**
   * Roam (v16): populate the current region's roamer cast if it needs
   * one. Called on exterior entry + every region cross; all gates live
   * here so call sites stay one-liners.
   *
   * Visit bookkeeping: a genuine entry bumps runState.spawnVisits and
   * derives a fresh cast; the boot-resume entry (roamerResumeRegion)
   * reuses the persisted count so a reload re-derives the SAME cast.
   * A surviving live group short-circuits everything — border hops
   * keep continuity; a fully consumed group refills on re-entry.
   */
  private maybePopulateRoamers(): void {
    if (!this.roamers) return;
    if (this.mode !== 'exterior' || !this.currentRegion) return;
    // Pre-starter grass is empty: nothing to fight with. The first
    // Route 1 walk pre-starter bounces at onMapEntered anyway;
    // post-starter entries populate normally.
    if (!runState.hasStarter()) return;
    const region = this.currentRegion;
    // Table-less regions (towns) never spawn AND never bump — keeps
    // spawnVisits meaningful as "visits that rolled a cast."
    if (!ENCOUNTER_TABLES[region.name]) return;
    if (this.roamers.hasLiveGroup(region.name)) return;

    const isResume = this.roamerResumeRegion === region.name;
    this.roamerResumeRegion = null;
    const persisted = runState.getSpawnVisit(region.name);
    const visit =
      isResume && persisted > 0
        ? persisted
        : runState.bumpSpawnVisit(region.name);
    this.roamers.populate(
      region.name,
      { x: region.x, y: region.y, w: region.w, h: region.h },
      visit,
      runState.getSpawnConsumed(region.name),
    );
  }

  /** World px → screen/CSS px for the main (zoomed, scrolling) camera
   *  — the inverse of getWorldPoint. Zone-2 DOM anchored to sprites
   *  (floating wild HP, damage numbers over a duel) positions with
   *  this; canvas px == #game CSS px by the Scale.RESIZE contract. */
  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    const cam = this.cameras.main;
    // cam.worldView is the visible world rect Phaser computes in
    // preRender — the only zoom-safe origin for projection (scrollX
    // alone is wrong under zoom ≠ 1; that bug put the first duel's
    // damage floats 1700px below the viewport).
    return worldToScreen(wx, wy, {
      viewLeft: cam.worldView.x,
      viewTop: cam.worldView.y,
      zoom: cam.zoom,
    });
  }

  /**
   * M2 in-world duel bracket: staging intro → WorldBattleHost (the
   * full battle over the awake world) → outro, with the follower
   * hidden for the duration ("out fighting" is semantically a sleep)
   * and the duel input regime active. The wild's sprite stays owned
   * by RoamerSystem, frozen on its tile by the encounterPending gate.
   */
  private async runInWorldDuel(roamer: Roamer): Promise<boolean> {
    const lead = runState.getParty()[0];
    if (!lead) return true; // nothing to stage OR battle — swallow, matching the old no-op
    // Plan BEFORE any duel dressing (Cornered ladder, 2026-08-03): a
    // 'modal' verdict must leave the world untouched so the caller
    // can fall through to the classic frozen-modal battle — no stray
    // sting, no hidden follower, no stuck duel input regime.
    const blocked = (x: number, y: number): boolean =>
      !this.canStep(x, y) || Boolean(this.roamers?.roamerBlockingAt(x, y));
    const plan = planWildStaging(
      { x: this.playerTileX, y: this.playerTileY },
      { x: roamer.x, y: roamer.y },
      {
        isBlocked: blocked,
        // Matches startleBack's own execution checks exactly
        // (canStartleTo is the shared single source) so the plan
        // can't promise a hop the hop then refuses.
        canHop: (x, y) =>
          Boolean(
            this.roamers?.canStartleTo(roamer, x, y, (bx, by) => !this.canStep(bx, by)),
          ),
        isPlayerSafe: (x, y) => this.isDuelSafePlayerTile(x, y),
      },
    );
    if (plan.kind === 'modal') return false;
    this.setDuelActive(true);
    // The encounter sting marks the engagement — the frozen-modal
    // path plays it inside launchWildBattle; the duel path bypasses
    // that, so it fires here, once, as the theater starts.
    audio.playSfx('encounter');
    const f = this.follower?.snapshot;
    const fromTile = f?.visible ? { x: f.tileX, y: f.tileY } : null;
    this.follower?.onSleep();
    if (plan.wildHop) {
      // Rung 1 / ledge staging: the wild startle-hops back to open
      // the gap; the lead takes its vacated tile.
      await this.roamers?.startleBack(
        roamer,
        { x: plan.wildHop.x - roamer.x, y: plan.wildHop.y - roamer.y },
        (x, y) => !this.canStep(x, y),
      );
    } else {
      // Cornered / distant stagings: the startle beat still plays —
      // in place (whirl + hop with nowhere to flee), and it settles a
      // mid-lerp frozen sprite onto its committed tile. Without a
      // visible startle, the player's own give-ground step reads as
      // rubber-banding (theater critic, 2026-08-03).
      await this.roamers?.startleInPlace(roamer, {
        x: this.playerTileX,
        y: this.playerTileY,
      });
    }
    if (plan.playerStep) {
      // Rungs 2–3: the player gives ground / steps aside, eyes still
      // on the wild (facing held). stopFollow first — the punch tween
      // owns the camera next, and a follow-drag during the back-step
      // would bounce the shot (seams critic).
      this.cameras.main.stopFollow();
      await this.duelPlayerStep(plan.playerStep);
    }
    // Every engagement path: the wild ends up facing its OPPONENT's
    // tile (the slot) — sneaking up from behind must not leave it
    // fighting the wrong way (phone dogfood 2026-08-03).
    this.roamers?.faceToward(roamer, plan.slot);
    const stage = new DuelStage(this, this.mapData.tileSize, this.player);
    this.duelStage = stage;
    let endedInEndRun = false;
    let pinch: DuelPinchHandle | null = null;
    let hdToggle: DuelHdToggleHandle | null = null;
    // The whole-scene filter supersedes the per-sprite HD swap — when it's
    // on, battlers stay plain GBA and the shader smooths them.
    let hdOn =
      !DUEL_SCENE_FILTER_ENABLED && DUEL_HD_BATTLERS_ENABLED && hdPreferred();
    try {
      const introPromise = stage.intro({
        playerTile: { x: this.playerTileX, y: this.playerTileY },
        wildTile: { x: roamer.x, y: roamer.y },
        leadSpecies: lead.species,
        // Player-moved stagings materialize the battler ON its slot
        // (the just-vacated player tile — empty by construction):
        // walking it out of the follower tile would cross through the
        // retreating player (seams critic finding 6).
        fromTile: plan.playerStep ? plan.slot : fromTile,
        mySlot: plan.slot,
        isBlocked: blocked,
      });
      // Battler sprite exists synchronously after intro() is invoked —
      // sync the UI-camera ignore list NOW, not after the 450ms punch,
      // or the battler double-renders through the zoom-1 camera for
      // the whole walk-in (the cinematic-walker bug class).
      this.syncUiCameraIgnores();
      // HD taste-gate: skin both battlers per the stored pref BEFORE
      // the punch lands — at base zoom the swap is invisible (the HD
      // sheet NEAREST-decimates ~2:1 to the original art) and the
      // detail resolves as the camera approaches.
      const skinDuelSprites = (on: boolean): void =>
        this.skinDuelBattlers([stage.getBattler(), roamer.sprite], on);
      if (hdOn) skinDuelSprites(true);
      await introPromise;
      pinch = mountDuelPinch(stage);
      // The scaler cycler is NOT mounted here any more (2026-08-04) — it is
      // scene-lifetime, already up from create(), and stays up when the duel
      // ends. Only the HD sprite-swap toggle is still duel-scoped.
      if (!DUEL_SCENE_FILTER_ENABLED && DUEL_HD_BATTLERS_ENABLED) {
        hdToggle = mountDuelHdToggle({
          initial: hdOn,
          onToggle: (next) => {
            hdOn = next;
            setHdPreferred(next);
            skinDuelSprites(next);
          },
        });
      }
      // M2: the battle happens HERE — world awake, camera punched in,
      // the wild still standing on its tile. Setup construction is
      // shared byte-identically with the frozen-modal path.
      const { setup, opponentDexId, playerDexId } =
        await this.buildWildBattleSetup(roamer.species, roamer.level);
      const host = new WorldBattleHost({
        worldScene: this,
        battler: stage.getBattler()!,
        wildSprite: roamer.sprite,
        slotTile: stage.slotTile!,
        wildTile: { x: roamer.x, y: roamer.y },
        tileSize: this.mapData.tileSize,
        setup,
        opponentDexId,
        playerDexId,
        battleZone: this.mapData.name,
        // Mixin-born world-space FX (hit-particle emitters spawned
        // inside shared battle code) get reported so the zoom-1 UI
        // camera ignores them — same double-render failure mode
        // syncUiCameraIgnores guards for every named sprite family.
        onWorldObjectSpawned: (obj) => this.uiCamera.ignore(obj),
        hdSkins: () => hdOn,
      });
      await host.run();
      endedInEndRun = host.endedInEndRun;
      // Flee rule (owner, 2026-08-03): running away leaves the wild in
      // the world — you walked off, it watches you go. Only a FAINT
      // consumes the spawn (opponentFaints is the authoritative
      // signal: wilds are single-mon, so any entry means it went
      // down). Marking after resolution keeps the tab-close contract.
      if (host.opponentFaints.length > 0) {
        this.roamers?.consume(roamer);
        runState.markSpawnConsumed(roamer.region, roamer.spawnIndex);
      }
      if (!endedInEndRun) {
        // Restore the map's own theme over the battle/victory music —
        // mirrors the WAKE handler's restore on the frozen-modal path
        // (playMusic no-ops when the key is already current).
        const musicKey = MAP_MUSIC[this.currentMapName];
        if (musicKey) void audio.playMusic(this, musicKey);
      }
    } finally {
      pinch?.destroy();
      hdToggle?.destroy();
      // The scaler filter deliberately SURVIVES the duel now (2026-08-04) —
      // it is the whole game's look, not the battle's. Torn down on SLEEP /
      // SHUTDOWN instead.
      // The roamer's sprite is SHARED with the world — whatever
      // happened (flee, win, throw), it never wears HD art outside
      // the duel. consume() destroys it on wins (applyOwSkin's active
      // guard no-ops); on flees the revert waits for the camera to
      // ease home first — at base zoom the swap is invisible, at
      // punched zoom it would pop. Applied at the END of this finally.
      const restoreRoamerSkin = (): void =>
        this.skinDuelBattlers([roamer.sprite], false);
      if (endedInEndRun) {
        // Full wipe routed to EndRunScene over the now-SLEEPING world:
        // a sleeping scene's tween clock never ticks, so awaiting
        // outro() here would hang forever. Release the battler without
        // theater — EndRunScene's Reset Run reloads the page.
        stage.dispose();
        this.duelStage = null;
      } else {
        // Player-moved stagings recall the battler in place — its
        // walk-home target IS the player's post-move tile (Cornered
        // rungs 2–3), and marching into them re-creates the overlap.
        await stage.outro({ recallBattlerInPlace: Boolean(plan.playerStep) });
        stage.dispose();
        this.duelStage = null;
        this.follower?.onWake(
          { x: this.playerTileX, y: this.playerTileY },
          this.facing,
          // Blocked set: after a rung-2/3 player move, "behind the
          // player" is a tile nothing else ever validated — without
          // this the follower can re-materialize inside a wall
          // (seams critic, 2026-08-03).
          this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
          this.followerHomeAfterDuel(fromTile, Boolean(plan.playerStep)),
        );
        this.setDuelActive(false);
      }
      restoreRoamerSkin();
    }
    return true;
  }

  /** Apply the GBA⇄HD skin to live duel battlers. Species derives
   *  from each sprite's texture key, so the toggle keeps working
   *  across mid-battle force-switch re-skins. Any in-flight SCALE
   *  tween (the level-up pulse) is completed first — a running tween
   *  would overwrite the swap's setScale on its next tick and finish
   *  at the stale variant's base, stranding an HD battler 4× too big
   *  (adversarial review, 2026-08-03). Position/alpha tweens (lunges,
   *  faints) are left alone. */
  private skinDuelBattlers(
    sprites: Array<Phaser.GameObjects.Sprite | null>,
    on: boolean,
  ): void {
    for (const s of sprites) {
      if (!s || !s.active) continue;
      for (const t of this.tweens.getTweensOf(s)) {
        // data is TweenData[] | Tween[] (chains); only TweenData has key.
        const tweensScale = t.data?.some(
          (d) =>
            'key' in d &&
            (d.key === 'scale' || d.key === 'scaleX' || d.key === 'scaleY'),
        );
        if (tweensScale) t.complete();
      }
      const species = speciesFromKey(s.texture.key);
      if (species) applyOwSkin(this.textures, s, species, on);
    }
  }

  /** Attach/detach the whole-scene smoothing PostFX to the world camera.
   *  idx 0 (off) detaches → zero GPU cost and byte-identical to today.
   *  No-op on a Canvas renderer (post pipelines are WebGL-only). */
  private setDuelFilterAlgo(idx: number): void {
    const webgl =
      this.game.renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer;
    if (!webgl) return;
    if (idx === 0) {
      this.detachDuelFilter();
      return;
    }
    const cam = this.cameras.main;
    if (!this.duelFilterPipe) {
      cam.setPostPipeline(DUEL_FILTER_PIPELINE_KEY);
      const p = cam.getPostPipeline(DUEL_FILTER_PIPELINE_KEY);
      this.duelFilterPipe = (Array.isArray(p) ? p[0] : p) as
        | DuelSceneFilterPipeline
        | null;
    }
    if (this.duelFilterPipe) this.duelFilterPipe.algo = idx;
  }

  private detachDuelFilter(): void {
    if (!this.duelFilterPipe) return;
    this.cameras.main.resetPostPipeline();
    this.duelFilterPipe = null;
  }

  /** Hand the canvas→device upscale back to the compositor — the "second
   *  pass that would smooth it" the owner guessed at (ask 2026-08-04),
   *  implemented by the compositor for zero GPU cost.
   *
   *  Phaser stamps `image-rendering: pixelated` INLINE on the canvas element
   *  at boot (CreateRenderer.js:86 → CanvasInterpolation.setCrisp, because
   *  `pixelArt: true` forces antialias off), and the backing store is CSS px
   *  with no DPR multiplier (ScaleManager.js:861 — Phaser 3 has no DPR path
   *  at all). So on a DPR-3 phone every framebuffer pixel is a hard 3×3
   *  device-pixel block and NOTHING in the frame has ever been antialiased.
   *  Flipping to `auto` gives every edge — scaler sub-texel seams, block
   *  seams, sprite outlines alike — a phase-perfect ~1-framebuffer-px ramp,
   *  and it COMPOSES with the scaler's detail instead of replacing it (unlike
   *  a blur it cannot invent or erase a colour: it only interpolates between
   *  pixels the scaler already chose).
   *
   *  Real on any DPR > 1, so both the phone and a retina laptop; a genuine
   *  no-op on a 1x external monitor, which is the one thing an in-shader AA
   *  pass would buy over this — deliberately deferred until the owner says he
   *  wants this look at all (see duelSceneFilter's REJECTED note).
   *
   *  Scene-scoped since 2026-08-04: restored on SLEEP + SHUTDOWN, not at the
   *  end of a duel. Leaving the overworld smoothed is now a legitimate A/B
   *  position rather than a bug — but it must never outlive WorldScene, or a
   *  modal/title screen inherits a soft canvas nobody asked for. */
  private setDuelSmoothing(on: boolean): void {
    const canvas = this.game.canvas;
    if (!canvas) return;
    if (on) {
      if (this.duelPrevImageRendering === null) {
        this.duelPrevImageRendering = canvas.style.imageRendering;
      }
      canvas.style.imageRendering = 'auto';
      return;
    }
    if (this.duelPrevImageRendering !== null) {
      canvas.style.imageRendering = this.duelPrevImageRendering;
      this.duelPrevImageRendering = null;
    }
  }

  /** Mount the live scaler cycler + its delivery toggle, applying both stored
   *  prefs immediately so a reload resumes the tuned pair.
   *
   *  WHOLE-GAME as of 2026-08-04 (owner ask: "cycle through all these filters
   *  not just for combat but for the whole game"). Called from create() and
   *  from WAKE, torn down on SLEEP + SHUTDOWN — NOT from the duel flow, which
   *  used to own it. Idempotent: a second call with the pill already up is a
   *  no-op, so the WAKE handler cannot stack duplicates onto #game. */
  private mountDuelFilter(): void {
    if (!DUEL_SCENE_FILTER_ENABLED) return; // owner ruling: no world/scene filter — guard covers create() + WAKE + any call site
    if (this.filterCycler) return;
    const pref = filterAlgoPref();
    this.setDuelFilterAlgo(pref);
    this.filterCycler = mountDuelFilterCycler({
      initial: pref,
      initialSmooth: filterSmoothPref(),
      onChange: (i) => this.setDuelFilterAlgo(i),
      onSmooth: (on) => this.setDuelSmoothing(on),
    });
  }

  /** Tear down the cycler + its canvas-side effect. Detaching the PIPELINE is
   *  deliberately left to SHUTDOWN: a SLEEP (modal, Pokédex) keeps the camera
   *  object alive, and re-attaching a PostFX on every wake churns a render
   *  target for no visual gain. */
  private unmountDuelFilter(): void {
    this.filterCycler?.destroy();
    this.filterCycler = null;
  }

  /** Enter/exit the in-world duel input regime. Idempotent. On enter:
   *  joystick pointer zone disabled, any COMMAND path cancelled, all
   *  held intents cleared (a thumb resting on the stick must not
   *  buffer steps into the duel's end). On exit: joystick re-enabled;
   *  intents re-assert themselves from live input next frame. */
  private setDuelActive(on: boolean): void {
    if (this.duelActive === on) return;
    this.duelActive = on;
    this.joystickHandle?.setEnabled(!on);
    if (on) {
      this.pathfinder?.cancel();
      intentRouter.clearAll();
    }
  }

  /** Whether (x, y) is a legal PLAYER destination for a Cornered
   *  rung-2/3 give-ground step. The step is a raw positioning tween
   *  outside the step pipeline — no warp, no coord trigger, no
   *  region-cross detection fires — so every tile that would need
   *  those is excluded rather than handled. */
  private isDuelSafePlayerTile(x: number, y: number): boolean {
    // In-bounds first: canStep's flat-index read wraps at row edges
    // (tryProcessMovement convention — bounds before canStep).
    if (x < 0 || y < 0 || x >= this.mapData.width || y >= this.mapData.height) {
      return false;
    }
    if (!this.canStep(x, y)) return false;
    // roamerBlockingAt, not committed-only: a neighbor frozen
    // mid-lerp by the engagement gate straddles both tiles for the
    // whole duel (geometry critic, 2026-08-03).
    if (this.roamers?.roamerBlockingAt(x, y)) return false;
    // Region seam guard: a cross-region step would leave
    // currentRegion/mapName/music/spawn bookkeeping stale for the
    // whole duel (verified reachable at the Route1/Pallet boundary).
    if (this.mode === 'exterior' && this.currentRegion) {
      const r = this.currentRegion;
      if (x < r.x || x >= r.x + r.w || y < r.y || y >= r.y + r.h) return false;
    }
    if (this.mapData.warps.some((w) => w.x === x && w.y === y)) return false;
    const triggers = this.mapData?.events?.triggers;
    if (triggers?.some((tr) => tr.x === x && tr.y === y)) return false;
    return true;
  }

  /** Execute a planned rung-2/3 player move: 160ms tween (the
   *  startle-hop envelope), facing HELD — you back off with your eyes
   *  on the opponent; a facing flip would read as wandering off.
   *  Commits tiles immediately (stepTo semantics) and persists after
   *  landing. Deliberately bypasses the step pipeline — see
   *  isDuelSafePlayerTile for why that's sound. */
  private async duelPlayerStep(tile: { x: number; y: number }): Promise<void> {
    const t = this.mapData.tileSize;
    this.playerTileX = tile.x;
    this.playerTileY = tile.y;
    await new Promise<void>((resolve) => {
      this.tweens.add({
        targets: this.player,
        x: (tile.x + 0.5) * t,
        y: (tile.y + 0.5) * t,
        duration: 160,
        ease: 'Quad.easeOut',
        onComplete: () => resolve(),
      });
    });
    this.persistPlayerPosition();
  }

  /** Execute a planned trainer back-step (corridor-retreat staging):
   *  relative 160ms tween, facing held toward the duel, with the
   *  npcTiles bookkeeping the LOS run-up established. The trainer
   *  keeps their new post like the run-up does — in-session only. */
  private async duelTrainerStep(
    npc: RuntimeNpc,
    tile: { x: number; y: number },
  ): Promise<void> {
    const sprite = this.npcSpriteAtTile(npc.x, npc.y);
    const ts = this.mapData.tileSize;
    const dx = (tile.x - npc.x) * ts;
    const dy = (tile.y - npc.y) * ts;
    this.npcTiles.delete(npc.y * this.mapData.width + npc.x);
    npc.x = tile.x;
    npc.y = tile.y;
    this.npcTiles.add(npc.y * this.mapData.width + npc.x);
    if (!sprite) return;
    await new Promise<void>((resolve) => {
      this.tweens.add({
        targets: sprite,
        x: sprite.x + dx,
        y: sprite.y + dy,
        duration: 160,
        ease: 'Quad.easeOut',
        onComplete: () => resolve(),
      });
    });
  }

  /**
   * Roam contact → battle. The player never moves onto the roamer's
   * tile (bump/interact both leave them adjacent, cartridge-style);
   * the roamer is consumed at launch and its patch refills only on
   * genuine region re-entry. Reuses launchWildBattle verbatim — same
   * 12-draw battle seed shape, same BattleScene, same EXP loop as a
   * classic encounter with this species/level.
   */
  private async engageRoamer(roamer: Roamer): Promise<void> {
    // `moving` included in the gate set: a keyboard interact mid-step
    // would otherwise launch a battle relative to
    // the player's committed tile while the sprite is still lerping
    // (adversarial review bug 8c).
    if (this.dialogueOpen || this.moving || this.transitioning || this.encounterPending) return;
    // Look at what you're fighting BEFORE any of it stages. The two
    // walk-contact paths (bump + ledge hop) turn `facing` at the top of
    // tryProcessMovement and then intercept here, skipping the
    // setPlayerSpriteFrame calls at the bottom — so the player sprite
    // held its PREVIOUS direction's frame for the entire duel and only
    // snapped round on the first idle tick after the battle
    // (phone dogfood, 2026-08-03). Doing it here covers every
    // engagement path at once — bump, ledge hop, A-button, tap,
    // COMMAND arrival, and the rung-4 fall-through to the frozen modal.
    // Ahead of the starter gate on purpose: a bump that engages nothing
    // still turns you into it, cartridge-style (same rule as bumping a
    // wall).
    this.facePlayerAtTile(roamer.x, roamer.y);
    if (!runState.hasStarter()) return;
    this.encounterPending = true;
    try {
      if (IN_WORLD_DUEL_ENABLED) {
        // M2: the battle happens HERE — world awake, camera punched
        // in, the wild still standing on its tile. Consumption is
        // decided inside (flee = the wild STAYS; only a faint
        // consumes it — owner ruling 2026-08-03, "if i run from a
        // wild pokemon, can it still stick around?").
        const staged = await this.runInWorldDuel(roamer);
        if (staged) return; // finally clears encounterPending
        // Cornered rung 4: planWildStaging found NO in-world geometry
        // (sealed pocket / walled corridor). The startle beat still
        // opens the encounter — same first 200ms as every staged
        // rung — then we cut to the classic battle screen below.
        await this.roamers?.startleInPlace(roamer, {
          x: this.playerTileX,
          y: this.playerTileY,
        });
      }
      // Battle FIRST, consume AFTER: the frozen-backdrop snapshot is
      // captured inside launchWildBattle, and the creature you
      // engaged must still be standing in the frame — destroying it
      // pre-launch opened every roam battle over empty grass where
      // the wild stood a frame ago (litigation cross-examination,
      // 2026-08-02). The world is asleep for the whole await, so the
      // sprite is frozen in place until the return, then removed:
      // it fought, it's gone.
      await this.launchWildBattle(roamer.species, roamer.level);
      // launchFrozenModal resolves at scene LAUNCH, not at wake — so
      // without this wait, consume + mark ran while the battle was
      // still being fought, and the pagehide auto-save could persist
      // a consumed mark for an unresolved fight (seams critic,
      // 2026-08-03 — the comment below finally matches the code).
      if (this.scene.isSleeping()) {
        await new Promise<void>((resolve) =>
          this.events.once(Phaser.Scenes.Events.WAKE, () => resolve()),
        );
      }
      this.roamers?.consume(roamer);
      // Mark consumption AFTER the battle resolves (win/loss/flee all
      // count — the wild dispersed either way). A tab-close mid-battle
      // leaves the ordinal unmarked, so an unresolved battle's roamer
      // returns on reload — the encounter never happened. Battle-end
      // auto-save persists the mark.
      runState.markSpawnConsumed(roamer.region, roamer.spawnIndex);
    } finally {
      this.encounterPending = false;
    }
  }

  /**
   * Shared wild-battle setup constructor — the frozen-modal path
   * (launchWildBattle) and the M2 in-world duel host both build the
   * SAME battle from it: same 12-draw battle-domain seed shape (3×
   * rollBattleSeed in this exact literal order), same startingHp /
   * startingStatus snapshot, same recordSeenWild tally. Split out of
   * launchWildBattle with its behavior byte-identical.
   */
  private async buildWildBattleSetup(
    opponentSpecies: string,
    opponentLevel: number,
  ): Promise<{ setup: BattleSetup; opponentDexId: number; playerDexId: number }> {
    const party = runState.getParty();
    const playerSpecies = party[0]!.species;
    const playerDexId = SPECIES_TO_DEX[playerSpecies] ?? 263;
    const opponentDexId = SPECIES_TO_DEX[opponentSpecies] ?? 1;

    // Pokédex Step 6: tally the wild sighting BEFORE launching the
    // battle. The player has seen the species by the time the
    // encounter triggers; recording at battle-launch time vs battle-
    // end time is identical in v0.1 (no flee option), and doing it
    // here keeps it adjacent to the launch rather than racing the
    // scene.wake handler after BattleScene exits. Roam keeps the
    // same semantics: contact = seen (eyeballing a distant roamer
    // deliberately does NOT tally — deferred decision, 2026-08-02
    // review).
    runState.recordSeenWild(opponentSpecies, opponentLevel);

    // Resolve the wild Pokémon's level-appropriate moves before launching
    // BattleScene. Cached after the first call (loadGen3 caches the dex
    // Promise); subsequent encounters resolve in <1 ms.
    const wildSet = await wildPokemonAsync(opponentSpecies, opponentLevel);

    // Phase 12 M1: build startingHp from runState's stored party HP so
    // wounded Pokémon enter sim's battle at their preserved current HP.
    // Sim's PokemonSet has no current-HP field; runInteractiveBattle
    // mutates sim's internal Pokemon.hp directly using this map.
    const startingHp: Record<string, number> = {};
    for (const m of party) startingHp[m.species] = m.hp.current;
    // PR 3 EXP 5c: same pattern for persistent status — sim-slot
    // ordered (party order matches sim's pokemon[] order).
    const startingStatus = party.map((m) => m.status);

    const setup: BattleSetup = {
      seed: rollBattleSeed(),
      p1: {
        name: 'Player',
        team: teamFromParty(party),
        aiSeed: rollBattleSeed(),
        startingHp,
        startingStatus,
      },
      p2: {
        name: 'Opponent',
        team: [wildSet],
        aiSeed: rollBattleSeed(),
      },
    };

    return { setup, opponentDexId, playerDexId };
  }

  /**
   * Species/level-parameterized wild battle launch — the shared tail of
   * classic step-roll encounters (launchWildEncounter, which rolls the
   * species from the 'encounter' domain) and roam-mode contact
   * engagement (roamers arrive with their species/level pre-rolled at
   * spawn time). Caller owns the encounterPending flag; the battle
   * itself is identical either way — same 12-draw battle-domain seed
   * shape, same BattleScene, same EXP loop.
   */
  private async launchWildBattle(
    opponentSpecies: string,
    opponentLevel: number,
  ): Promise<void> {
    const { setup, opponentDexId, playerDexId } =
      await this.buildWildBattleSetup(opponentSpecies, opponentLevel);

    // Follower: resolve pending hops / in-flight send-outs so onSleep
    // (about to fire via this.scene.sleep) has a stable home tile to
    // snap to. Without this, a SLEEP during a mid-hop state would
    // strand the follower at the pre-hop tile.
    this.follower?.flushPending();

    audio.playSfx('encounter');
    await this.launchFrozenModal('BattleScene', {
      setup,
      opponentDexId,
      playerDexId,
      battleZone: this.mapData.name,
    });
  }

  private checkTriggers(): void {
    for (const t of this.mapData.events.triggers) {
      if (t.x === this.playerTileX && t.y === this.playerTileY) {
        console.log(
          `[trigger] tile=(${t.x},${t.y}) ${t.var}=${t.varValue} script=${t.scriptRef}`,
        );
      }
    }
  }

  /** Single handler for keyboard Space/Enter + the A button. Open dialogue
   *  advances to the next page (or closes on the last page); otherwise look
   *  at the tile in front and try to interact with it. */
  private handleInteract(): void {
    if (this.duelActive) return; // duel owns A — battle chrome handles input
    if (this.dialogueOpen) {
      this.advanceDialogue();
      return;
    }
    this.tryInteract();
  }

  /** Cancel/back: keyboard Escape (Phase 7+) and the B button. Closes any
   *  open dialogue regardless of page. */
  private handleCancel(): void {
    if (this.dialogueOpen) this.closeDialogue();
  }


  private tryInteract(): void {
    // Compute the tile the player is facing, then apply the counter-skip
    // (see MB_COUNTER above) so nurses / mart clerks behind a desk are
    // reachable. dx/dy are captured up-front so the skip can reuse them.
    let dx = 0;
    let dy = 0;
    switch (this.facing) {
      case 'up': dy = -1; break;
      case 'down': dy = 1; break;
      case 'left': dx = -1; break;
      case 'right': dx = 1; break;
    }
    let tx = this.playerTileX + dx;
    let ty = this.playerTileY + dy;
    const w = this.mapData.width;

    // Roam: interacting while facing a roamer engages it — the A-button
    // / adjacent-tap / COMMAND-approach-arrival path, complementing the
    // walk-bump intercept in tryProcessMovement. Checked pre-counter-
    // skip (roamers live in grass, never behind counters).
    const facedRoamer = this.roamers?.roamerAt(tx, ty);
    if (facedRoamer) {
      void this.engageRoamer(facedRoamer);
      return;
    }

    if (tx >= 0 && tx < w && ty >= 0 && ty < this.mapData.height) {
      const behavior = this.mapData.behaviors[ty * w + tx] ?? 0;
      if (behavior === MB_COUNTER) {
        tx += dx;
        ty += dy;
      }
    }

    const npc = this.mapData.events.npcs.find((n) => n.x === tx && n.y === ty);
    if (npc) {
      // Cloned NPCs (cartridge `type: "clone"` — tree clones, item-ball
      // clones across maps) lack a scriptRef. Show a fallback ellipsis
      // page rather than nothing so the player gets some feedback.
      const ref = npc.scriptRef ?? '';
      // One-tap Pokémon Centers (first-friend playtest 2026-08-02):
      // heal spots dispatch straight to the heal handler — no greeting
      // page between the tap and the fade. The handler shows the
      // healed ack itself (or a greeting when the party is empty).
      if (ref && HEAL_SPOT_SCRIPT_REFS.has(ref)) {
        dispatchScript(ref, this.scriptCtx());
        return;
      }
      // Talk-to-battle: the cartridge launches script-driven trainers
      // (Brock!) from their interaction script, not from sight — but
      // only the LOS path ever shipped, so Brock (TRAINER_TYPE_NONE,
      // sight 0) was unreachable and the win condition dead (first-
      // friend playtest 2026-08-02). Any undefeated rostered trainer
      // now battles on interact; defeated trainers fall through to
      // the dialogue table (post-defeat flavor).
      const trainer = ref ? getTrainer(ref) : undefined;
      if (trainer && !runState.hasDefeatedTrainer(ref) && !npcIsHidden(npc)) {
        void this.launchTrainerEncounter(npc, trainer);
        return;
      }
      this.showDialogue(getDialogue(ref), ref || null);
      return;
    }

    const sign = this.mapData.events.signs.find((s) => s.x === tx && s.y === ty);
    if (sign) {
      this.showDialogue(getDialogue(sign.scriptRef), sign.scriptRef);
      return;
    }
    // No interactable in front — silent. Phase 5 may add a soft "huh?" cue.
  }

  /**
   * Tap-on-thing dispatcher. Called from two paths that both end up
   * here:
   *
   *   1. this.input.on('pointerup') — tap that landed on empty Phaser
   *      world (no interactive hit). currentlyOver === [] gates this.
   *   2. The TAP_PASSTHROUGH_EVENT emitted by the joystick zone's
   *      capture-phase click detector (DIRECT mode taps in lower 60%
   *      that nipplejs would otherwise eat).
   *
   * Both supply both screen coords (for dialogue-rect hit-test, since
   * the dialogue lives in UI camera = screen pixels at zoom 1) and
   * world coords (for tile-tap routing). Two responsibilities:
   *
   *   - Dialogue open: tap *inside* the dialogue rect → advance;
   *     anywhere outside → close. The bg's own pointerdown handler
   *     covers path 1 inside-tap natively, but path 2 needs the
   *     explicit rect check.
   *   - World tap: NPC (1 × 2 column — foot + head, since sprites
   *     render origin (0.5, 0.75)) or sign at the tapped tile.
   *     DIRECT / Classic fire only when target is orthogonally
   *     adjacent (Manhattan = 1); diagonals require walking closer
   *     first. COMMAND-mode pathfind lives in Step 7; today it
   *     degrades to the same Manhattan=1 rule.
   *
   * Encounter / battle gating mirrors handleInteract — refuse during
   * transitions, mid-step, or while another scene launch is pending.
   */
  private handleTap(opts: {
    screenX: number;
    screenY: number;
    worldX: number;
    worldY: number;
  }): void {
    if (this.dialogueOpen) {
      // Dialogue overlay (DOM glass) captures taps inside its bounds via
      // gestureRouter.onTap to advance pages. Any tap that reaches
      // handleTap landed OUTSIDE the overlay (the canvas), so treat it
      // as a skip-to-close — same UX as the legacy Phaser-side dialogue.
      // 350 ms grace prevents the tail of the gesture that triggered
      // showDialogue from immediately closing the dialogue we just
      // opened (iOS Safari's synthetic click delay is ~300 ms).
      if (performance.now() - this.dialogueOpenedAt < 350) return;
      this.closeDialogue();
      return;
    }
    // Note: this.moving is intentionally NOT a bail condition — a tap
    // received mid-step can update the pathfinder (which replans
    // safely from the step's destination tile, since playerTileX/Y
    // advances at step start). transitioning + encounterPending still
    // bail because those actively own the scene.
    if (this.transitioning || this.encounterPending) return;

    const ts = this.mapData.tileSize;
    const tileX = Math.floor(opts.worldX / ts);
    const tileY = Math.floor(opts.worldY / ts);

    // DIRECT/Classic in-front shortcut (Bug 3): when the player is
    // already facing an NPC/sign, ANY tap that isn't aimed at a
    // *different* adjacent NPC/sign fires the front interact. The
    // adjacency check preserves the existing "tap the left NPC to
    // talk to the left NPC" behavior when multiple adjacents exist;
    // taps on empty / far / off-map / far-NPC all collapse to "talk
    // to whoever I'm facing." COMMAND keeps its tap-to-walk semantics.
    if (
      this.controlMode !== 'command' &&
      this.hasInteractableInFront() &&
      !this.tapHitAdjacentInteractable(tileX, tileY)
    ) {
      this.tryInteract();
      return;
    }

    if (
      tileX < 0 || tileY < 0 ||
      tileX >= this.mapData.width || tileY >= this.mapData.height
    ) {
      // Off-map tap. In COMMAND, route to an edge-connection cross if
      // there's a connection in that direction; otherwise silent no-op.
      // DIRECT / Classic don't tap-to-walk so off-map taps are always
      // no-ops there.
      if (this.controlMode === 'command') {
        this.tryCrossEdgeFromOffMapTap(tileX, tileY);
      }
      return;
    }

    // NPC 1×2 column hit: tapped tile OR the tile below it (head row).
    // Foot tile is canonical (matches mapData.events.npcs[].y), head
    // tile is foot - 1. So if the tap is at (tileX, tileY), the NPC's
    // foot is at (tileX, tileY) OR (tileX, tileY + 1).
    let npc = this.mapData.events.npcs.find((n) => n.x === tileX && n.y === tileY);
    if (!npc) {
      npc = this.mapData.events.npcs.find(
        (n) => n.x === tileX && n.y === tileY + 1,
      );
    }
    if (npc) {
      if (npcIsHidden(npc)) return;
      this.routeInteractableTap({ x: npc.x, y: npc.y });
      return;
    }

    const sign = this.mapData.events.signs.find((s) => s.x === tileX && s.y === tileY);
    if (sign) {
      this.routeInteractableTap({ x: sign.x, y: sign.y });
      return;
    }

    // Roam: tapped a roamer (foot tile, or head row — same 1×2 column
    // hit as NPCs; the 32px sprite's side overhang is visual only).
    // Routes through the interactable-approach flow: DIRECT/Classic
    // engage at Manhattan ≤ 1; COMMAND pathfinds to an adjacent
    // approach tile and engages on arrival via tryInteract. If the
    // roamer ambles away before arrival, the arrival interact finds
    // nothing and no-ops — tap again (halt-and-replan family).
    const tappedRoamer =
      this.roamers?.roamerAt(tileX, tileY) ??
      this.roamers?.roamerAt(tileX, tileY + 1);
    if (tappedRoamer) {
      this.routeInteractableTap({ x: tappedRoamer.x, y: tappedRoamer.y });
      return;
    }

    // Tapped tile has no NPC / sign. In COMMAND, if it's walkable,
    // pathfind there. DIRECT / Classic are silent on empty-world
    // taps — they always have a movement input (joystick or D-pad).
    if (this.controlMode === 'command') {
      // Tap on the player's own tile: short-circuit pathfinding. If the
      // tile sits on an edge with a matching connection, fire the cross
      // directly (covers "just walked into a new zone, want to walk
      // back" — your current tile IS the edge tile). Otherwise no-op.
      if (tileX === this.playerTileX && tileY === this.playerTileY) {
        const crossEdge = this.edgeCrossDirOfTile(tileX, tileY);
        if (crossEdge) this.onPathArrival({ crossEdge });
        return;
      }
      if (this.isPathWalkable(tileX, tileY)) {
        // If the tap landed on an edge tile whose direction matches a
        // connection (and isn't ambiguous between two connections),
        // queue a crossEdge intent so the path continues into the next
        // map on arrival. Covers the camera-clamped case where the
        // user can't tap *past* the edge from the viewport.
        const crossEdge = this.edgeCrossDirOfTile(tileX, tileY);
        this.pathfinder?.findPath(
          this.playerTileX,
          this.playerTileY,
          { x: tileX, y: tileY },
          crossEdge ? { crossEdge } : undefined,
        );
      }
    }
  }

  /** Branch tap-on-interactable by control mode. DIRECT / Classic
   *  enforce Manhattan ≤ 1 (existing behavior — user walks closer
   *  first). COMMAND pathfinds to an adjacent approach tile then
   *  fires interact on arrival via the pathfinder's onArrival hook. */
  private routeInteractableTap(target: TileXY): void {
    const dx = Math.abs(target.x - this.playerTileX);
    const dy = Math.abs(target.y - this.playerTileY);
    const manhattan = dx + dy;
    if (manhattan === 1) {
      // Already adjacent — same flow as DIRECT.
      this.tryReachInteractable(target.x, target.y);
      return;
    }
    if (this.controlMode !== 'command') {
      // DIRECT / Classic: silent no-op — walk closer first. The
      // tap-anywhere-when-in-front shortcut in handleTap covers the
      // ergonomic case where the user has already aimed by walking.
      return;
    }
    if (manhattan === 0) {
      // Tapped the player's own tile (shouldn't happen, but guard).
      return;
    }
    // Pick a walkable approach tile + pathfind there with an
    // interact-on-arrival hook.
    const approach = this.pickApproachTile(target);
    if (!approach) return; // No reachable adjacent — silent fail.
    this.pathfinder?.findPath(
      this.playerTileX,
      this.playerTileY,
      approach,
      { faceTile: target },
    );
  }

  /** True iff the tile directly in front of the player (per current
   *  facing) is occupied by an interactable NPC or sign. Powers the
   *  DIRECT/Classic "tap anywhere when in front = interact" shortcut:
   *  the user doesn't have to aim at the NPC if they're already
   *  facing one. Flag-hidden NPCs (FLAG_HIDE_*) don't count — they
   *  also skip rendering + npcTile occupancy. */
  private hasInteractableInFront(): boolean {
    // Mirror tryInteract's counter-skip so the tap-anywhere-when-in-front
    // shortcut treats "facing a counter desk with a nurse behind it" as
    // an interactable.
    let dx = 0;
    let dy = 0;
    switch (this.facing) {
      case 'up': dy = -1; break;
      case 'down': dy = 1; break;
      case 'left': dx = -1; break;
      case 'right': dx = 1; break;
    }
    let tx = this.playerTileX + dx;
    let ty = this.playerTileY + dy;
    const w = this.mapData.width;
    // Roam: a roamer directly in front counts — the tap-anywhere
    // shortcut engages it (pre-counter-skip tile; roamers live in
    // grass, never behind counters).
    if (this.roamers?.roamerAt(tx, ty)) return true;
    if (tx >= 0 && tx < w && ty >= 0 && ty < this.mapData.height) {
      const behavior = this.mapData.behaviors[ty * w + tx] ?? 0;
      if (behavior === MB_COUNTER) {
        tx += dx;
        ty += dy;
      }
    }
    const npc = this.mapData.events.npcs.find((n) => n.x === tx && n.y === ty);
    if (npc && !npcIsHidden(npc)) return true;
    const sign = this.mapData.events.signs.find((s) => s.x === tx && s.y === ty);
    return Boolean(sign);
  }

  /** True iff the tap at (tileX, tileY) landed on an NPC/sign tile
   *  whose foot is Manhattan 1 from the player (i.e., a tile the
   *  existing per-tile router would face + interact with). Used by
   *  the in-front shortcut to preserve "tap the specific adjacent
   *  NPC" behavior — without this, the shortcut would override an
   *  explicit tap on a side-adjacent NPC and talk to the front one. */
  private tapHitAdjacentInteractable(tileX: number, tileY: number): boolean {
    const manhattanToPlayer = (x: number, y: number): number =>
      Math.abs(x - this.playerTileX) + Math.abs(y - this.playerTileY);
    for (const n of this.mapData.events.npcs) {
      if (npcIsHidden(n)) continue;
      // NPC renders as a 1×2 column; tap may land on the foot tile
      // OR the head tile above it. Adjacency is measured to the foot.
      const hitFoot = n.x === tileX && n.y === tileY;
      const hitHead = n.x === tileX && n.y === tileY + 1;
      if ((hitFoot || hitHead) && manhattanToPlayer(n.x, n.y) === 1) return true;
    }
    for (const s of this.mapData.events.signs) {
      if (s.x === tileX && s.y === tileY && manhattanToPlayer(s.x, s.y) === 1) return true;
    }
    // Roam: an adjacent roamer counts as an aimed tap target too —
    // without this, facing a sign with a roamer beside you hijacks the
    // roamer tap into reading the sign (adversarial review bug 7).
    // Same 1×2 column hit as NPCs.
    const roamer =
      this.roamers?.roamerAt(tileX, tileY) ??
      this.roamers?.roamerAt(tileX, tileY + 1);
    if (roamer && manhattanToPlayer(roamer.x, roamer.y) === 1) return true;
    return false;
  }

  /** Detect whether an in-bounds tile sits on an edge that has a
   *  matching connection — used to auto-cross on a tap-on-edge-tile in
   *  COMMAND mode (Phaser's camera clamps at map bounds, so off-map
   *  taps aren't always reachable from the viewport). Returns null
   *  when the tile isn't on any edge, when no connection matches, or
   *  when the tile sits on two edges with both having connections
   *  (ambiguous corner — fall through to plain walk-and-stop). */
  private edgeCrossDirOfTile(tileX: number, tileY: number): ConnectionDir | null {
    const w = this.mapData.width;
    const h = this.mapData.height;
    const candidates: ConnectionDir[] = [];
    if (tileY === 0 && this.mapData.connections.some((c) => c.direction === 'up')) {
      candidates.push('up');
    }
    if (tileY === h - 1 && this.mapData.connections.some((c) => c.direction === 'down')) {
      candidates.push('down');
    }
    if (tileX === 0 && this.mapData.connections.some((c) => c.direction === 'left')) {
      candidates.push('left');
    }
    if (tileX === w - 1 && this.mapData.connections.some((c) => c.direction === 'right')) {
      candidates.push('right');
    }
    return candidates.length === 1 ? candidates[0]! : null;
  }

  /** Off-map tap → walk to the nearest in-bounds edge tile and cross
   *  the connection. The tap's parallel axis pins to the edge row or
   *  column; the perpendicular axis is clamped to [0, max). Bails on
   *  corner taps (both axes off — direction is ambiguous), on missing
   *  connections, and on unwalkable edge tiles. */
  private tryCrossEdgeFromOffMapTap(tileX: number, tileY: number): void {
    const w = this.mapData.width;
    const h = this.mapData.height;
    const offX: ConnectionDir | null =
      tileX < 0 ? 'left' : tileX >= w ? 'right' : null;
    const offY: ConnectionDir | null =
      tileY < 0 ? 'up' : tileY >= h ? 'down' : null;
    if (offX && offY) return;
    const dir = offX ?? offY;
    if (!dir) return;

    const conn = this.mapData.connections.find((c) => c.direction === dir);
    if (!conn) return;

    let targetX = tileX;
    let targetY = tileY;
    switch (dir) {
      case 'up':    targetY = 0; break;
      case 'down':  targetY = h - 1; break;
      case 'left':  targetX = 0; break;
      case 'right': targetX = w - 1; break;
    }
    targetX = Math.max(0, Math.min(w - 1, targetX));
    targetY = Math.max(0, Math.min(h - 1, targetY));

    if (!this.isPathWalkable(targetX, targetY)) return;

    this.pathfinder?.findPath(
      this.playerTileX,
      this.playerTileY,
      { x: targetX, y: targetY },
      { crossEdge: dir },
    );
  }

  /** Pick a walkable adjacent tile for the player to stop on, sorted
   *  by Manhattan from the player so the chosen approach minimizes
   *  walking. Returns null if every neighbor is blocked or out of
   *  bounds. */
  private pickApproachTile(target: TileXY): TileXY | null {
    // C6: cartridge approach-from-front convention. If the target is an
    // NPC with a determined facing (MOVEMENT_TYPE_FACE_*), prefer the
    // tile directly in front of it — the player ends up looking at the
    // NPC's face, which is the cartridge UX. Wandering / look-around
    // NPCs (no canonical facing) fall back to closest-walkable.
    const npc = this.mapData.events.npcs.find(
      (n) => n.x === target.x && n.y === target.y,
    );
    const npcFacing = npc ? facingFromMovementType(npc.movementType) : null;
    const frontTile = frontTileOf(target, npcFacing);
    if (frontTile && this.isPathWalkable(frontTile.x, frontTile.y)) {
      return frontTile;
    }
    // Fallback: closest-walkable cardinal neighbor.
    const cardinals: TileXY[] = [
      { x: target.x, y: target.y - 1 },
      { x: target.x, y: target.y + 1 },
      { x: target.x - 1, y: target.y },
      { x: target.x + 1, y: target.y },
    ];
    const candidates = cardinals
      .filter((c) => this.isPathWalkable(c.x, c.y))
      .map((c) => ({
        ...c,
        dist:
          Math.abs(c.x - this.playerTileX) +
          Math.abs(c.y - this.playerTileY),
      }))
      .sort((a, b) => a.dist - b.dist);
    return candidates[0] ?? null;
  }

  /** Mirror of buildPathfinderGrid's walkability rule (kept inline
   *  so handleTap can decide without rebuilding a grid). Warp tiles
   *  override their collision=1 since the cartridge fires the warp
   *  the moment you step in. NPC-occupied tiles are excluded so
   *  pickApproachTile + edge-cross tap don't pick destinations the
   *  pathfinder's request-time avoid points would refuse to route
   *  to (Bug 5). */
  private isPathWalkable(x: number, y: number): boolean {
    if (
      x < 0 || y < 0 ||
      x >= this.mapData.width || y >= this.mapData.height
    ) return false;
    const idx = y * this.mapData.width + x;
    if (this.npcTiles.has(idx)) return false;
    // Roamer tiles are pathfinder avoid points, so they must fail the
    // same pre-check npcTiles does — otherwise pickApproachTile /
    // edge-cross taps hand findPath a destination it will refuse (the
    // Bug 5 class, re-opened for roamers by the adversarial review).
    if (this.roamers?.roamerBlockingAt(x, y)) return false;
    if (this.mapData.warps.some((w) => w.x === x && w.y === y)) return true;
    if ((this.mapData.collision[idx] ?? 1) !== 0) return false;
    if (IMPASSABLE_BEHAVIORS.has(this.mapData.behaviors[idx] ?? 0)) return false;
    return true;
  }

  /** Fired by the pathfinder once it finishes a path that carried an
   *  arrival intent. Two flavors: `faceTile` (tap-on-NPC / tap-on-sign
   *  in COMMAND — face the recorded target + run tryInteract); or
   *  `crossEdge` (tap off-map in COMMAND — push one step in the cross
   *  direction so tryProcessMovement fires the connection transition,
   *  then clear the intent so the player doesn't keep walking on the
   *  destination map). */
  private onPathArrival(intent: PathArrivalIntent | undefined): void {
    if (!intent) return;

    if (intent.crossEdge) {
      const dir = intent.crossEdge;
      this.facing = dir;
      this.setPlayerSpriteFrame('idle');
      intentRouter.setIntent(dir, true, 'tap-step');
      this.tryProcessMovement();
      // Clear immediately so a held tap-step intent doesn't bleed into
      // the destination map after the transition fade completes. The
      // attemptConnectionTransition above set this.transitioning = true
      // synchronously, so the next tryProcessMovement on the dest map
      // bails until the fade callback flips it back — but we still
      // want the slate clean.
      intentRouter.setIntent(dir, false, 'tap-step');
      return;
    }

    if (!intent.faceTile) return;
    const dx = intent.faceTile.x - this.playerTileX;
    const dy = intent.faceTile.y - this.playerTileY;
    if (Math.abs(dx) + Math.abs(dy) !== 1) return;
    if (dy < 0) this.facing = 'up';
    else if (dy > 0) this.facing = 'down';
    else if (dx < 0) this.facing = 'left';
    else this.facing = 'right';
    this.setPlayerSpriteFrame('idle');
    this.persistPlayerPosition();
    this.tryInteract();
  }

  /** Helper for handleTap: face the target tile (if orthogonally
   *  adjacent) and fire tryInteract through the existing flow.
   *  Diagonal targets (Chebyshev 1 but Manhattan 2) and far targets are
   *  silent no-ops in DIRECT / Classic — the user walks closer first.
   *  Step 7 will pathfind for COMMAND mode. */
  private tryReachInteractable(targetX: number, targetY: number): void {
    const dx = targetX - this.playerTileX;
    const dy = targetY - this.playerTileY;
    const manhattan = Math.abs(dx) + Math.abs(dy);
    if (manhattan !== 1) {
      // Diagonal or distant — must be orthogonally adjacent (Manhattan 1)
      // to face + interact. COMMAND mode pathfinds via tryReachByPath
      // before reaching this gate.
      return;
    }
    // Orthogonal neighbor — face toward the target then interact.
    if (dy < 0) this.facing = 'up';
    else if (dy > 0) this.facing = 'down';
    else if (dx < 0) this.facing = 'left';
    else if (dx > 0) this.facing = 'right';
    this.setPlayerSpriteFrame('idle');
    this.persistPlayerPosition();
    this.tryInteract();
  }

  /**
   * D-pad + A/B buttons. Translucent overlays sitting on top of the world
   * so the game canvas reads as edge-to-edge on mobile (the user explicitly
   * wanted full-screen game with mostly-transparent controls).
   *
   * Sizing scales up on portrait (thumb-friendly, ≥64px primary controls)
   * and tightens on landscape/desktop where mouse precision is fine. D-pad
   * lives bottom-left (left thumb), A/B bottom-right (right thumb).
   *
   * Reposition closures re-read this.scale.{width,height} on every resize,
   * so the controls track the visible viewport even when the URL bar slides
   * or the device rotates.
   */
  private createTouchControls(): void {
    this.touchControls = this.add.container(0, 0).setDepth(15).setScrollFactor(0);

    const isPortrait = this.scale.height > this.scale.width;
    const cell = isPortrait ? 64 : 56;
    const aSize = isPortrait ? 76 : 64;
    const bSize = isPortrait ? 60 : 52;
    // Generous margins on mobile so the controls don't crowd the gesture-
    // navigation strip at the bottom of modern phones.
    const margin = isPortrait ? 24 : 16;

    const dpadOffsets: Record<ConnectionDir, [number, number]> = {
      up: [cell, 0],
      left: [0, cell],
      right: [cell * 2, cell],
      down: [cell, cell * 2],
    };
    const dpadGlyphs: Record<ConnectionDir, string> = {
      up: '▲',
      down: '▼',
      left: '◀',
      right: '▶',
    };

    // Classic D-pad palette per DESIGN.md "Classic D-pad" component vocab
    // (added 2026-05-13 /plan-design-review). Pre-Future-Cartridge this
    // was a paper-fill + ink-stroke + coral-A + peach-B aesthetic; now
    // all surfaces use glass tokens. A button is the "comfortably loud"
    // beat (glass-fill-thick + 2px specular-edge-emphasis) vs B's lighter
    // glass-fill-thin + 1px specular-edge — non-Pokémon CTAs use specular
    // emphasis for hierarchy, never type color.
    for (const dir of ['up', 'down', 'left', 'right'] as const) {
      this.dpadButtons[dir] = this.makeTouchButton({
        size: cell - 4,
        fill: GLASS_FILL_THIN.hex,
        fillAlpha: GLASS_FILL_THIN.alpha,
        strokeColor: SPECULAR_EDGE.hex,
        strokeAlpha: SPECULAR_EDGE.alpha,
        labelColor: TEXT_PRIMARY.rgba,
        labelText: dpadGlyphs[dir],
        labelFontSize: isPortrait ? '28px' : '24px',
        reposition: () => {
          const baseX = margin;
          const baseY = this.scale.height - cell * 3 - margin;
          const [ox, oy] = dpadOffsets[dir];
          return [baseX + ox + 2, baseY + oy + 2];
        },
        onDown: () => {
          intentRouter.setIntent(dir, true, 'classic-dpad');
        },
        onUp: () => {
          intentRouter.setIntent(dir, false, 'classic-dpad');
        },
      });
    }

    this.aButton = this.makeTouchButton({
      size: aSize,
      fill: GLASS_FILL_THICK.hex,
      fillAlpha: GLASS_FILL_THICK.alpha,
      strokeColor: SPECULAR_EDGE_EMPHASIS.hex,
      strokeAlpha: SPECULAR_EDGE_EMPHASIS.alpha,
      strokeWidth: 2,
      labelColor: TEXT_PRIMARY.rgba,
      labelText: 'A',
      labelFontSize: isPortrait ? '30px' : '24px',
      labelBold: true,
      reposition: () => [
        this.scale.width - margin - aSize,
        this.scale.height - margin - aSize - cell / 2,
      ],
      onDown: () => this.handleInteract(),
    });

    this.bButton = this.makeTouchButton({
      size: bSize,
      fill: GLASS_FILL_THIN.hex,
      fillAlpha: GLASS_FILL_THIN.alpha,
      strokeColor: SPECULAR_EDGE.hex,
      strokeAlpha: SPECULAR_EDGE.alpha,
      labelColor: TEXT_PRIMARY.rgba,
      labelText: 'B',
      labelFontSize: isPortrait ? '24px' : '20px',
      labelBold: true,
      reposition: () => [
        this.scale.width - margin - aSize - bSize - 8,
        this.scale.height - margin - bSize,
      ],
      onDown: () => this.handleCancel(),
    });

    // Top-right ☰ menu button removed in orb-chrome step 7c — the
    // Pokéball glyph + popover are the canonical menu entry now.
  }

  private makeTouchButton(opts: {
    size: number;
    fill: number;
    fillAlpha: number;
    strokeColor: number;
    strokeAlpha: number;
    /** Stroke width in px. Defaults to 1 (DESIGN.md Classic D-pad spec
     *  for cells + B button); A button overrides to 2 for the
     *  "comfortably loud" emphasis tier. */
    strokeWidth?: number;
    labelColor: string;
    labelText: string;
    labelFontSize: string;
    labelBold?: boolean;
    reposition: () => [number, number];
    onDown: () => void;
    onUp?: () => void;
  }): TouchButton {
    const rect = this.add
      .rectangle(0, 0, opts.size, opts.size, opts.fill, opts.fillAlpha)
      .setOrigin(0, 0)
      .setStrokeStyle(opts.strokeWidth ?? 1, opts.strokeColor, opts.strokeAlpha)
      .setInteractive({ useHandCursor: true });
    // Press feedback: darken the bg slightly so taps feel responsive even
    // through the translucency. Reset on release / cancel.
    const pressedAlpha = Math.min(1, opts.fillAlpha + 0.3);
    rect.on('pointerdown', () => {
      rect.setFillStyle(opts.fill, pressedAlpha);
      opts.onDown();
    });
    const release = (): void => {
      rect.setFillStyle(opts.fill, opts.fillAlpha);
      opts.onUp?.();
    };
    rect.on('pointerup', release);
    rect.on('pointerout', release);
    rect.on('pointerupoutside', release);
    const label = this.add
      .text(0, 0, opts.labelText, {
        color: opts.labelColor,
        fontSize: opts.labelFontSize,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontStyle: opts.labelBold ? 'bold' : 'normal',
      })
      .setOrigin(0.5);
    this.touchControls.add([rect, label]);

    const reposition = (): void => {
      const [x, y] = opts.reposition();
      rect.setPosition(x, y);
      label.setPosition(x + opts.size / 2, y + opts.size / 2);
    };
    const setVisible = (v: boolean): void => {
      rect.setVisible(v);
      label.setVisible(v);
      // Hidden buttons must also stop receiving pointer events; setVisible
      // alone leaves the hit area live and can swallow taps that should
      // go to the canvas (or the joystick zone).
      if (v) rect.setInteractive({ useHandCursor: true });
      else rect.disableInteractive();
    };
    return { rect, label, reposition, setVisible };
  }

  private repositionTouchControls(): void {
    if (!this.touchControls) return;
    for (const dir of ['up', 'down', 'left', 'right'] as const) {
      this.dpadButtons[dir]?.reposition();
    }
    this.aButton?.reposition();
    this.bButton?.reposition();
  }

  /** Show/hide the classic D-pad + A/B button cluster based on the
   *  active control mode. MENU is mode-independent — always visible.
   *  Called on create() after createTouchControls + on every
   *  setControlMode flip. */
  private applyControlModeVisibility(): void {
    const showClassic = shouldShowClassicDpad(this.controlMode);
    for (const dir of ['up', 'down', 'left', 'right'] as const) {
      this.dpadButtons[dir]?.setVisible(showClassic);
    }
    this.aButton?.setVisible(showClassic);
    this.bButton?.setVisible(showClassic);
  }

  /** Switch the active control mode at runtime. Step 4's MenuScene
   *  radios call this when the user changes their selection. Tears
   *  down the outgoing mode's input layer + brings up the incoming
   *  one without a scene reload. */
  setControlMode(mode: ControlMode): void {
    if (mode === this.controlMode) return;
    if (this.joystickHandle) {
      this.joystickHandle.destroy();
      this.joystickHandle = null;
    }
    // Drop any in-flight pathfind when leaving command mode so a
    // half-walked path doesn't keep nudging intent forward in
    // direct / classic.
    if (this.controlMode === 'command') this.pathfinder?.reset();
    this.controlMode = mode;
    this.applyControlModeVisibility();
    if (mode === 'direct') {
      this.joystickHandle = mountJoystick(this);
    }
  }

  /**
   * Mark a transparent modal overlay open / closed for a given source.
   * Callers: PartySheetScene (source='partySheet') today; pokeballMenu
   * will add 'settings' / 'pokedex' / 'bag' in PR 3b. Public because
   * cross-scene reach is required — design doc chose this API over a
   * generic event bus on runState (CLAUDE.md "runState is NOT a generic
   * event bus" lock).
   *
   * Side effects fire only on the open↔closed TRANSITION (set size
   * 0↔non-zero). Opening a second source while one is already open is a
   * no-op for the side-effect chain; closing one while another remains
   * open is likewise a no-op. Mirrors the prior `setPartySheetOpen`'s
   * `if (partySheetOpen === open) return` early exit, generalized.
   *
   * Side effects on transition:
   *   - Orb chrome dims (50% opacity) + per-orb pointer-events off
   *   - Joystick zone div pointer-events off (applyJoystickGate)
   *   - Dialogue overlay DOM suppresses (display: none) while open;
   *     restores on close
   *
   * NOT a side-effect: WorldScene is never put to sleep. The transparent-
   * overlay contract is the load-bearing piece of the orb-chrome design.
   */
  setModalOverlayOpen(open: boolean, source: ModalOverlaySource): void {
    const transition = applyModalOverlay(this.openModalSources, open, source);
    this.openModalSources = transition.sources;
    if (!transition.transitioned) return;
    this.partyOrbs?.setInteractionsDisabled(transition.isOpen);
    this.dialogueOverlayHandle?.setExternallySuppressed(transition.isOpen);
    // The scaler pills join the suppress chain rather than the SLEEP path,
    // because the DEFAULT modal route is the LIVE overlay (pokeballMenu's
    // launchOverlay — WorldScene stays awake behind a transparent scene), so
    // SLEEP never fires for Pokédex/Settings unless motion is reduced. Hidden
    // rather than destroyed: the modal is transient and the pills' state is
    // worth keeping across it.
    this.filterCycler?.setVisible(!transition.isOpen);
    // Open transition: clear any in-flight movement intent so a held
    // thumbstick / pending tap-walk doesn't fire through into the just-
    // opened overlay. Gated on !dialogueOpen so a trailing dialogue
    // close action (heal NPC dispatch, trainer encounter pickup, etc.)
    // isn't truncated. Eng-locked #2.
    if (transition.isOpen && !this.dialogueOpen) {
      intentRouter.clearAll();
    }
    this.applyJoystickGate();
  }

  /**
   * Mark an orb drag-to-reorder gesture in flight. PartyOrbs calls this
   * on long-press fire and again on drag end (true → false). The
   * joystick is disabled while EITHER partySheetOpen OR dragInProgress
   * is true — single shared "joystick disabled" state per the eng
   * review's Issue 4 contract.
   */
  setDragInProgress(active: boolean): void {
    if (this.dragInProgress === active) return;
    this.dragInProgress = active;
    this.applyJoystickGate();
  }

  /** OR-gate the joystick on the suppression flags. Modal overlays + drag
   *  + dialogue + frozen-modal launch window all suppress so a held
   *  thumbstick can't auto-walk into a just-opened modal. */
  private applyJoystickGate(): void {
    const suppress =
      this.openModalSources.size > 0 ||
      this.dragInProgress ||
      this.dialogueOpen ||
      this.isLaunchingModal;
    this.joystickHandle?.setEnabled(!suppress);
  }

  /**
   * Capture the world into a global DynamicTexture, sleep WorldScene,
   * and launch the named modal scene with the snapshot handle attached
   * to the data payload. The modal scene's `create()` (or `init()` for
   * Battle→EndRun) calls `handle.transferOwnership(this)` as its first
   * line, moving the Image (and shutdown listener) onto itself; the
   * DynamicTexture survives in the global TextureManager regardless.
   *
   * Synchronous input-lock runs BEFORE the await (strategy-locked
   * outside-voice §3): isLaunchingModal flips on, joystick suppresses,
   * intentRouter clears (gated on !dialogueOpen so trailing dialogue
   * actions still dispatch). Without this, a stray tap during the
   * ≤100ms captureFrom window could fire a second encounter.
   *
   * If captureFrom resolves to null (timeout / display-list rejected /
   * GPU error), the modal launches anyway with no snapshot — the
   * receiver falls back to its prior `#0E1014` backdrop. Locked
   * decision #2.
   *
   * `waitForFadeIn: true` defers capture until the camera fade-in
   * completes — needed at the pending-draft resume site (onMapEntered
   * fires during the post-warp fade-in window). Locked decision #13.
   */
  async launchFrozenModal(
    sceneKey:
      | 'BattleScene'
      | 'DraftScene'
      | 'PokedexScene'
      | 'SettingsScene'
      | 'BagScene',
    data: Record<string, unknown>,
    opts?: { waitForFadeIn?: boolean },
  ): Promise<void> {
    this.isLaunchingModal = true;
    if (!this.dialogueOpen) {
      intentRouter.clearAll();
    }
    this.applyJoystickGate();

    let handle: SnapshotHandle | null = null;
    try {
      if (opts?.waitForFadeIn) {
        // Pending-draft resume fires inside onMapEntered, before the
        // map's fade-in completes. Capturing before the fade would
        // snapshot a half-faded world. 500ms safety timeout in case
        // the fade is already done (the once-listener wouldn't fire).
        await new Promise<void>((resolve) => {
          const cam = this.cameras.main;
          const timer = setTimeout(resolve, 500);
          cam.once('camerafadeincomplete', () => {
            clearTimeout(timer);
            resolve();
          });
        });
      }

      handle = await captureFrom(this);
      this.scene.sleep();
      try {
        this.scene.launch(sceneKey, { ...data, snapshotHandle: handle });
      } catch (err) {
        // Synchronous launch failure (e.g. bad scene key). The handle
        // is orphaned on this scene; dispose so we don't leak the
        // DynamicTexture. Eng-locked #4 caller-owns-handle-until-create.
        console.error('[launchFrozenModal] scene.launch failed:', err);
        handle?.dispose();
        this.scene.wake();
        throw err;
      }
    } finally {
      this.isLaunchingModal = false;
      this.applyJoystickGate();
    }
  }

  /**
   * Bump wakeAt past `time.now` right before a popover launches a scene
   * that sleeps WorldScene. Defends against the stale-pointerup race:
   * the popover row's pointerdown fires BEFORE the launch (so before
   * WorldScene goes to sleep), then pointerup arrives AFTER WorldScene
   * wakes back up — without this bump, the existing wakeAt guard
   * (`pointer.downTime < this.wakeAt`) wouldn't reject it and the tap
   * would COMMAND-walk to the row's screen coords. Same fix the
   * popover-launched scenes (Pokédex / Settings / Bag) need; the
   * orb-tap path doesn't because PartySheetScene is a transparent
   * overlay (never puts WorldScene to sleep). Public so the popover
   * module can call it cleanly without reaching for a private field.
   */
  markPendingOverlay(): void {
    this.wakeAt = this.time.now + 1;
  }

  /** Open the dialogue overlay with one or more pages. scriptRef (when
   *  non-null) is stashed for post-close dispatch — heal NPCs, trainer
   *  encounter launches, etc. land via runDialogueAction once the
   *  overlay's exit animation completes (see onDialogueClosed).
   *
   *  Region name passes to the overlay so the dialog-luma-* body class
   *  resolves to the correct glass tier (light/mid/dark) for the
   *  current tilemap. */
  private showDialogue(pages: string[], scriptRef: string | null = null): void {
    if (!this.dialogueOverlayHandle) return;
    // Follower: resolve pending hops / in-flight send-outs BEFORE the
    // overlay mounts so the follower is at a stable home tile while
    // text is up. Then freeze the walk tween (send-out plays through
    // per spec — dialogue doesn't pause animations, only the player).
    this.follower?.flushPending();
    this.follower?.onDialogueOpen();
    this.dialogueScriptRef = scriptRef;
    this.dialogueOpenedAt = performance.now();
    const region = this.currentRegion?.name ?? this.mapData?.name;
    this.dialogueOverlayHandle.show(pages, region ? { region } : undefined);
    // Joystick: suppress while the overlay is up. dialogueOpen flips
    // true synchronously on overlay.show(), so applyJoystickGate sees
    // it on the next call.
    this.applyJoystickGate();
  }

  /** Advance to the next dialogue page; on the last page, this triggers
   *  the overlay's close flow. Wired to A button + Space/Enter through
   *  handleInteract() and to the overlay's own gestureRouter.onTap. */
  private advanceDialogue(): void {
    if (!this.dialogueOverlayHandle) return;
    audio.playSfx('select');
    this.dialogueOverlayHandle.advance();
  }

  /** Close the dialogue overlay immediately. The exit animation runs
   *  for 150 ms; onDialogueClosed (subscribed in create()) fires when
   *  it completes and handles post-dialogue actions + the follower's
   *  dialogue-frozen flag. */
  private closeDialogue(): void {
    this.dialogueOverlayHandle?.close();
  }

  /** Fires AFTER the overlay's exit animation completes (subscribed via
   *  dialogueOverlayHandle.onClosed in create()). Clears the follower's
   *  dialogue-frozen flag and dispatches the stashed scriptRef through
   *  runDialogueAction so heal NPCs, trainer encounters, and the Pallet-
   *  bounce flow all fire at the correct point in the timeline. */
  private onDialogueClosed(): void {
    this.follower?.onDialogueClose();
    // Re-enable joystick now that the exit animation has resolved —
    // dialogueOpen is false again, so applyJoystickGate clears the
    // suppression unless something else (partySheet, drag) is active.
    this.applyJoystickGate();
    // Capture-then-clear so the action handler sees the right scriptRef
    // even if it triggers another dialogue (rare but possible).
    const ref = this.dialogueScriptRef;
    this.dialogueScriptRef = null;
    if (ref) this.runDialogueAction(ref);
  }

  /**
   * Dispatch a post-dialogue action keyed by scriptRef.
   *
   * As of MVP PR 3 (eng-review decisions 13 + 21), routing lives in
   * scripts/scriptRunner.ts. WorldScene exposes a narrow ScriptHostScene
   * view via `scriptCtx()` and hands the ref to dispatchScript.
   *
   * Three layers:
   *   1. Synthetic prefixes (allowlisted): `__trainer__:<ref>` launches
   *      a trainer battle; `__pallet_bounce__` bounces north-exit to
   *      Pallet's south edge.
   *   2. Heal-spot membership: nurse scriptRefs fade-out → healAll →
   *      save → fade-in → ack dialogue.
   *   3. Explicit registry: future PRs (Oak intercept, rival cinematic,
   *      item-ball pickups) register handlers here.
   */
  private runDialogueAction(scriptRef: string): void {
    dispatchScript(scriptRef, this.scriptCtx());
  }

  /** Build the ScriptCtx handed to handlers. The `worldScene` field is a
   *  narrow ScriptHostScene view so handlers can't reach into Phaser
   *  internals (per decision 13). */
  private scriptCtx(): ScriptCtx {
    return {
      worldScene: this as unknown as ScriptHostScene,
      runState,
      audio,
      FLAGS,
    };
  }

  // ─── ScriptHostScene surface ────────────────────────────────────
  //
  // Methods exposed to scriptRunner handlers via ScriptHostScene. Kept
  // narrow on purpose — handlers should compose these instead of
  // reaching for Phaser primitives.

  /** Direct setter for the transitioning flag — used by handlers that
   *  fence off camera/input during fades. Cinematic handlers prefer
   *  `runHandlerBracket` (below); this is the escape hatch for the
   *  existing nurse-heal flow. */
  setTransitioning(value: boolean): void {
    this.transitioning = value;
  }

  /** Look up trainer data by ref + launch the battle. The synthetic
   *  `__trainer__:<ref>` prefix handler in scriptRunner unwraps the
   *  prefix and calls this. */
  launchTrainerBattleByRef(realRef: string): void {
    const trainer = getTrainer(realRef);
    if (trainer) void this.launchTrainerBattle(realRef, trainer);
  }

  /** Bounce the player back to Pallet's matching south-edge tile and
   *  step them one tile south (autoStepOut via entryFacing='down') so
   *  they end up off the boundary and visibly facing into Pallet. */
  bouncePalletConnection(): void {
    const back = this.mapData.connections.find(
      (c) => c.direction === 'down' && c.map === 'MAP_PALLET_TOWN',
    );
    if (!back) return;
    const entryX = this.playerTileX - back.offset;
    void this.transitionToMap('PalletTown', entryX, 0, 'down');
  }

  /** Bracket primitive for cinematic handlers (Oak intercept, rival
   *  cinematic — ship in PR 13-15). Owns transitioning=true for the
   *  duration so detectRegionCross() can't rewrite mapData mid-tween
   *  (per cross-model finding 4). Camera-bounds locking layers in when
   *  C5's scripted-walking primitive lands. State always restored on
   *  resolve OR reject.
   *
   *  PR A5: also tracks handler-owned sprites created during the bracket
   *  and destroys any survivors in `finally`. Snapshot-on-entry / delta-
   *  on-exit pattern: sprites created BEFORE the bracket are untouched;
   *  sprites created DURING the bracket that weren't explicitly destroyed
   *  by the handler get cleaned up. Belt-and-suspenders against the
   *  "mini-rival sprite leak" regression class — even if a handler errors
   *  mid-walk or forgets a destroy() on some path, the bracket catches it. */
  async runHandlerBracket<T>(
    fn: () => Promise<T>,
    opts?: { recallFollower?: boolean; restoreFollower?: boolean },
  ): Promise<T> {
    const wasTransitioning = this.transitioning;
    this.transitioning = true;
    // Snapshot handler-owned sprites at entry. Anything in this set at
    // bracket-end that ISN'T in the snapshot was created during the
    // bracket and is a leak candidate. Using a Set (not array) so the
    // delta computation is O(n) instead of O(n²).
    const spritesAtEntry = new Set(this.handlerOwnedSprites);
    // Round-5 HGSS convention, OPT-IN per bracket: recall the follower
    // into its ball for cinematics whose walk geometry would cross its
    // tile (the rival post-battle exit). labPostPick deliberately does
    // NOT opt in — the just-picked starter stands at the player's right
    // watching the rival take his ball. Depth-guarded so nested opt-in
    // brackets recall once and restore once; idempotent when no
    // follower is out (pre-starter Oak intercept → no-op).
    const wantsRecall = !!opts?.recallFollower;
    enterBracketRecall(this.bracketRecall, wantsRecall, this.bracketRecallHooks());
    try {
      return await fn();
    } finally {
      this.transitioning = wasTransitioning;
      for (const sprite of this.handlerOwnedSprites) {
        if (!spritesAtEntry.has(sprite) && sprite.active) {
          sprite.destroy();
        }
      }
      // Runs in finally so a thrown handler still restores the follower.
      // restoreFollower: false = battle-launching brackets (rival
      // pre-battle challenge) — recall lands the follower in the same
      // onSleep() state the battle's SLEEP produces, and the post-
      // battle WAKE re-derive is the restore; a pop here would race
      // the battle fade. Bookkeeping (depth + species) still balances.
      exitBracketRecall(
        this.bracketRecall,
        wantsRecall,
        this.bracketRecallHooks(),
        opts?.restoreFollower !== false,
      );
    }
  }

  /** Depth + species bookkeeping for the bracket-level follower recall
   *  (choreography is the pure scripts/bracketRecall module). */
  private bracketRecall = { depth: 0, species: null as string | null };

  private bracketRecallHooks() {
    return {
      isOut: () => !!this.follower?.isOut,
      leadSpecies: () => runState.getParty()[0]?.species ?? null,
      recall: () => this.follower?.recall(effectsReduced()),
      restore: (species: string) =>
        this.follower?.spawn(
          { x: this.playerTileX, y: this.playerTileY },
          this.facing,
          species,
          this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
        ),
    };
  }

  /** Round-5: move the static rival npc entry to his battle-era tile
   *  when the fled-rival fingerprint holds (pure predicate in
   *  rivalLab.ts). In-session only — see fledRivalRearmTile's docs for
   *  why map-reload reversion to (5,4) is canon-coherent. Idempotent:
   *  same-tile calls no-op, and the fingerprint dissolves the moment
   *  the battle resolves or the hide flag returns. */
  private repositionFledRivalNpc(): void {
    if (this.currentMapName !== 'PalletTown_ProfessorOaksLab') return;
    const tile = fledRivalRearmTile({
      labState: getLabSceneState(),
      resolved: runState.hasFlag(FLAGS.RIVAL_BATTLE_RESOLVED),
      postBattleDone: runState.hasFlag(FLAGS.RIVAL_POST_BATTLE_DONE),
      hidden: runState.hasFlag(FLAGS.HIDE_RIVAL_IN_LAB),
      lastTileRaw: runState.getVar(VAR_RIVAL_LAST_TILE),
    });
    if (!tile) return;
    const npc = this.mapData.events.npcs.find(
      (n) => n.localId === RIVAL_LOCAL_ID,
    );
    if (!npc || (npc.x === tile.x && npc.y === tile.y)) return;
    this.npcTiles.delete(npc.y * this.mapData.width + npc.x);
    npc.x = tile.x;
    npc.y = tile.y;
    this.npcTiles.add(npc.y * this.mapData.width + npc.x);
    // Facing: authored MOVEMENT_TYPE_FACE_DOWN already faces the room.
  }

  /** Round-5 lab refinement: the post-pick starter spawns at the
   *  player's RIGHT-hand tile instead of behind. Behind OAK_FRONT
   *  (6,4) is (6,5) — exactly the rival's row-5 table corridor, which
   *  is how the friend's starter got walked over twice. The east tile
   *  sits clear of both table legs (pinned in labPostPickPath.test.ts)
   *  and reads better anyway: your new partner stands beside you
   *  watching the rival grab his ball. Lab-only; everywhere else the
   *  spawn keeps the behind-the-player default. */
  private firstSpawnPreferredTile(): { x: number; y: number } | undefined {
    if (this.currentMapName !== 'PalletTown_ProfessorOaksLab') return undefined;
    return { x: this.playerTileX + 1, y: this.playerTileY };
  }

  /** Persist runState to localStorage. Per decision 20, handlers call
   *  this explicitly at handler-end — setFlag does NOT autosave. */
  save(): void {
    saveManager.save();
  }

  /** PR 13b: returns when the dialogue overlay has closed. Used by
   *  cinematic handlers that fire `showDialogue` and need to resume
   *  after the player taps to dismiss. Polls at 50 ms; the dialogue
   *  overlay's `isOpen` getter is the source of truth. */
  async waitForDialogueClose(): Promise<void> {
    while (this.dialogueOpen) {
      await new Promise<void>((resolve) =>
        this.time.delayedCall(50, () => resolve()),
      );
    }
  }

  /** PR 13b: read-only snapshot of the player's tile. Cinematic
   *  handlers (e.g. Oak intercept) compute waypoint coords relative to
   *  the player's position at trigger-fire. */
  getPlayerTile(): { x: number; y: number } {
    return { x: this.playerTileX, y: this.playerTileY };
  }

  /** PR 14.5: cinematic-drive the player sprite along a cardinal path.
   *  Sister to npcWalkAlongPath but operates on `this.player`.
   *
   *  Bypasses the normal input + advanceStep pipeline (which is gated
   *  on !transitioning, so it freezes inside a runHandlerBracket).
   *  Tweens player.x/y directly, updates playerTileX/Y synchronously at
   *  the START of each step (so subsequent code sees the new tile),
   *  drives the follower's walk-progress in lockstep, cycles the
   *  player's walk frames via stepCount + setPlayerSpriteFrame.
   *
   *  Designed for cinematic handlers (Oak intercept lab walk) where
   *  the player auto-follows an NPC. Not for general player movement —
   *  the input + checkWarp + LOS pipeline does not fire during this
   *  walk. Handlers calling this MUST be inside
   *  runHandlerBracket so transitioning=true gates other systems. */
  async walkPlayerAlongPath(args: {
    waypoints: readonly { x: number; y: number }[];
    pixelsPerSecond?: number;
    /** Map the waypoints were authored for. If the player is somewhere
     *  else when the walk fires (a cinematic geometry assumption
     *  failed), the walk aborts with a warn instead of tweening the
     *  sprite across the wrong map's coordinate space — degrade to
     *  ugly-but-stationary, never teleport-spin (round-4 playtest). */
    expectedMap?: string;
  }): Promise<void> {
    if (!playerWalkAllowed(args.expectedMap, this.currentMapName)) {
      console.warn(
        '[cinematic] walkPlayerAlongPath aborted — expected map',
        args.expectedMap,
        'but player is on',
        this.currentMapName,
      );
      return;
    }
    const ts = this.mapData.tileSize;
    const speed = args.pixelsPerSecond ?? 64;
    const msPerTile = (ts / speed) * 1000;
    const steps = expandWaypoints(
      { x: this.playerTileX, y: this.playerTileY },
      args.waypoints,
    );

    for (const step of steps) {
      // Update facing + sprite for THIS step.
      this.facing = step.facing;
      // Follower hook BEFORE updating playerTileX/Y (matches the
      // existing tryProcessMovement pattern at ~line 1525 — the
      // follower's onPlayerStepStart reads the OLD tile as its `from`).
      this.follower?.onPlayerStepStart(
        { x: this.playerTileX, y: this.playerTileY },
        { x: step.tile.x, y: step.tile.y },
        step.facing,
        this.time.now,
      );
      this.playerTileX = step.tile.x;
      this.playerTileY = step.tile.y;
      this.stepCount++;
      this.setPlayerSpriteFrame('walk');

      // Tween the visual. Follower walks in lockstep via the existing
      // updateWalkProgress(t) hook (same pattern as advanceStep's
      // in-flight lerp).
      await new Promise<void>((resolve) => {
        const tween = this.tweens.add({
          targets: this.player,
          x: (step.tile.x + 0.5) * ts,
          y: (step.tile.y + 0.5) * ts,
          duration: msPerTile,
          ease: 'Linear',
          onUpdate: () => {
            this.follower?.updateWalkProgress(tween.progress);
          },
          onComplete: () => resolve(),
        });
      });
    }

    // Path complete — idle frame in the final facing direction.
    this.setPlayerSpriteFrame('idle');
  }

  /** Turn the player in place to face the given direction. Cinematic
   *  handlers use this to pivot the player toward an NPC for dialogue
   *  without committing a move (e.g. Oak intercept beat 3 — player turns
   *  south to face Oak before "It's unsafe!"). Updates the follower's
   *  facing too so its sprite frame doesn't desync. */
  setPlayerFacing(dir: ConnectionDir): void {
    if (this.facing === dir) return;
    this.facing = dir;
    this.setPlayerSpriteFrame('idle');
    this.follower?.onPlayerFacingChange(dir);
  }

  /** Turn the player to look at (x, y) — dominant axis, horizontal on
   *  ties (facingTowardTile shares findDuelSlot's convention so the
   *  turn agrees with the corridor the lead steps into). Unlike
   *  setPlayerFacing this ALWAYS re-asserts the sprite frame, because
   *  its callers include paths that already advanced `facing` but
   *  bailed before the frame was applied. Persists — a facing change is
   *  part of the resume-on-reload snapshot. */
  private facePlayerAtTile(x: number, y: number): void {
    const dir = facingTowardTile(
      { x: this.playerTileX, y: this.playerTileY },
      { x, y },
    );
    if (!dir) return; // same tile — nothing to look at
    const changed = this.facing !== dir;
    this.facing = dir;
    this.setPlayerSpriteFrame('idle');
    if (changed) this.follower?.onPlayerFacingChange(dir);
    if (this.currentMapName) this.persistPlayerPosition();
  }

  /** PR A1: set a specific NPC sprite's frame by `localId`. Lookup is
   *  O(1) via the npcSprites Map. No-op if the NPC isn't currently
   *  mounted — hidden via flag, on a different map, or a Rectangle
   *  fallback (no setFrame support). The Rectangle guard is defensive:
   *  callers should only use this on directional-9 NPCs with real
   *  spritesheets, but the no-op fallback keeps the cinematic from
   *  crashing if the data happens to drop the sprite asset. */
  setNpcFrame(localId: string, frameIndex: number): void {
    const sprite = this.npcSprites.get(localId);
    if (sprite && sprite instanceof Phaser.GameObjects.Sprite) {
      sprite.setFrame(frameIndex);
    }
  }

  /** PR A2-prep: cinematic delay primitive. Wraps Phaser's
   *  time.delayedCall so handlers can `await ctx.worldScene.delay(60)`
   *  between beats. Used by oakIntercept for the cartridge inter-beat
   *  pauses (60 frames after warp before "Fed up!", 30 frames between
   *  Oak's monologue and the rival react). */
  delay(ms: number): Promise<void> {
    return new Promise((resolve) =>
      this.time.delayedCall(ms, () => resolve()),
    );
  }

  /** PR 13d: launch the starter DraftScene with offers from the cached
   *  pendingDraft, optionally pre-flipping the matching species' card.
   *  Offers are sorted into STARTER_PEDESTAL_ORDER below so the modal's
   *  card positions correspond to the lab pedestals' left→right layout.
   *  The 3 ball-tap handlers call this with their species; an absent
   *  pendingDraft (e.g. flag-state corruption — STARTER_PICKED unset but
   *  pendingDraft cleared) is a defensive no-op rather than a crash.
   *
   *  Mirrors the rollOffers+launchFrozenModal path that used to live in
   *  onMapEntered. The pool title comes from DRAFT_POOLS so the modal
   *  shows "Pick a starter" (not whatever zone label). */
  async launchStarterDraft(): Promise<void> {
    const pending = runState.getPendingDraft();
    if (!pending || pending.zoneId !== 'PalletTown_ProfessorOaksLab') return;
    if (runState.hasFlag(FLAGS.STARTER_PICKED)) return;
    const pool = DRAFT_POOLS['PalletTown_ProfessorOaksLab'];
    if (!pool) return;
    this.encounterPending = true;
    try {
      // Sort offers into the cartridge pedestal order so the modal's
      // left/center/right cards always match the left/center/right balls
      // on the table. The rolled offer order from rollOffers is rng-
      // shuffled. Sorting is display-only; offer identity (species +
      // IVs + ability + nature) is preserved, the commit path reads the
      // offer object not the slot index.
      const sortedOffers = [...pending.offers].sort(
        (a, b) =>
          STARTER_PEDESTAL_ORDER.indexOf(a.species) -
          STARTER_PEDESTAL_ORDER.indexOf(b.species),
      );
      await this.launchFrozenModal('DraftScene', {
        title: pool.title,
        offers: sortedOffers,
      });
    } finally {
      this.encounterPending = false;
    }
  }

  /** C5 scripted-walking primitive (decision 15 + cross-model finding 4).
   *
   *  Designed for cinematic handlers (Oak intercept on Route 1, rival
   *  walk into the lab, etc.) — paths are known in advance and don't
   *  need pathfinder integration. Cardinal tile-by-tile movement at
   *  cartridge speed.
   *
   *  Safety invariants:
   *  - Handler-owned `Phaser.GameObjects.Sprite` — created here, NOT
   *    pushed onto `npcSprites[]`. A `rebuildNpcSprites()` mid-walk (e.g.
   *    from a region-cross) does not destroy this sprite.
   *  - `transitioning = true` for the full duration. `detectRegionCross()`
   *    is gated on `!transitioning`, so a walk that straddles a region
   *    boundary (Route 1 → Pallet) won't trigger a `mapData` rewrite
   *    mid-tween.
   *  - Suspended per-tween when `dialogueOpen` becomes true; resumed on
   *    dialogue close. Polled at 16 ms.
   *  - Player on the next tile → NPC waits (50 ms poll). No phase-through.
   *  - State restored on resolve OR reject (try/finally). */
  async npcWalkAlongPath(args: NpcWalkArgs): Promise<NpcWalkHandle> {
    const ts = this.mapData.tileSize;
    const speed = args.pixelsPerSecond ?? 32;
    const msPerTile = (ts / speed) * 1000;

    // PR 14.5: accept an existing sprite for multi-leg cinematics. Same
    // safety invariants apply — sprite lives outside npcSprites so
    // rebuildNpcSprites can't yank it; caller owns lifetime.
    //
    // PR A5: when a NEW sprite is created here (no existingSprite), it
    // joins handlerOwnedSprites so runHandlerBracket's finally can clean
    // up if the cinematic errors mid-walk. The DESTROY listener removes
    // it from the set so an explicit caller-driven destroy() doesn't
    // leave a stale reference. existingSprite path skips registration —
    // that sprite was already tracked by the original create site.
    const sprite =
      args.existingSprite ??
      this.add
        .sprite(
          (args.startTile.x + 0.5) * ts,
          (args.startTile.y + 0.5) * ts,
          args.spriteKey,
          0,
        )
        .setOrigin(0.5, 0.75)
        .setDepth(4);
    if (!args.existingSprite) {
      this.handlerOwnedSprites.add(sprite);
      sprite.once(Phaser.GameObjects.Events.DESTROY, () => {
        this.handlerOwnedSprites.delete(sprite);
      });
      // The new sprite lives in world space; syncUiCameraIgnores spreads
      // handlerOwnedSprites into the UI-camera ignore list (since
      // 2026-08-02 — before that this call was a no-op for the walker
      // and it double-rendered top-left through the zoom-1 uiCamera).
      this.syncUiCameraIgnores();
    }

    const steps = expandWaypoints(args.startTile, args.waypoints);
    const wasTransitioning = this.transitioning;
    this.transitioning = true;
    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => this.time.delayedCall(ms, () => resolve()));

    // Per-step walk-cycle frame update. Without this the sprite keeps
    // whatever frame it had at creation (frame 0 = down-standing) for the
    // whole walk — sprite glides the wrong direction and never animates
    // its legs. walkCycleFrame mirrors the cartridge pattern [standing,
    // walkA, standing, walkB] for directional-9 layouts; directional-3
    // and static return the standing frame each step (correct facing, no
    // leg animation).
    const layoutKey = sprite.texture.key as keyof typeof SPRITE_MANIFEST;
    const layout = SPRITE_MANIFEST[layoutKey]?.layout ?? 'static';

    try {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i]!;
        // Hold the step only while dialogue is open (cinematic pacing
        // beat). The walker deliberately does NOT wait for the player's
        // tile: cartridge applymovement ignores collision, and during a
        // handler bracket the player is input-locked and can never
        // vacate — the previous wait-for-clear deadlocked the lab
        // post-pick cinematic whenever the rival's return path crossed
        // the tile the player picked from (game froze, 2026-08-01).
        // A momentary sprite overlap is cartridge-normal.
        while (this.dialogueOpen) {
          await sleep(50);
        }

        // Pick the right frame + flipX for this step.
        const { flipX } = frameForFacing(layout, step.facing);
        sprite.setFrame(walkCycleFrame(layout, step.facing, i));
        sprite.setFlipX(flipX);

        await new Promise<void>((resolve) => {
          const tween = this.tweens.add({
            targets: sprite,
            x: (step.tile.x + 0.5) * ts,
            y: (step.tile.y + 0.5) * ts,
            duration: msPerTile,
            ease: 'Linear',
            onComplete: () => {
              watchTimer.remove();
              resolve();
            },
          });
          // Pause/resume the tween if dialogue opens DURING the tile-step
          // (rare — most cinematics don't trigger dialogue mid-walk —
          // but the spec calls for it). 16 ms tick matches frame rate.
          const watchTimer = this.time.addEvent({
            delay: 16,
            loop: true,
            callback: () => {
              if (this.dialogueOpen && tween.isPlaying()) tween.pause();
              else if (!this.dialogueOpen && tween.paused) tween.resume();
            },
          });
        });
      }

      // Settle on the standing frame in the final facing so a parked
      // sprite isn't frozen mid-stride.
      const last = steps[steps.length - 1];
      if (last) {
        const { frame, flipX } = frameForFacing(layout, last.facing);
        sprite.setFrame(frame);
        sprite.setFlipX(flipX);
      }
    } finally {
      this.transitioning = wasTransitioning;
      if (args.destroyOnComplete) {
        sprite.destroy();
      } else {
        // Sprite persists — re-sync the UI camera ignore since the
        // handler-owned sprite is now a permanent member of world space.
        this.syncUiCameraIgnores();
      }
    }
    return { sprite };
  }

  /**
   * Build a trainer-mode BattleSetup and hand control to BattleScene.
   * Mirrors launchWildEncounter's setup but uses the trainer's hand-
   * curated party for p2 + plumbs the trainer scriptRef so BattleScene
   * can mark the trainer as defeated on win (M4.4).
   *
   * Pre-resolves party movesets the same way wildPokemonAsync does so
   * sim's team build sees cartridge-accurate moves on first turn.
   */
  private async launchTrainerBattle(
    scriptRef: string,
    trainer: TrainerData,
  ): Promise<void> {
    if (!runState.hasStarter()) {
      // Defensive: a trainer LOS triggered with no party. Shouldn't be
      // reachable (Pewter is past Oak's), but guard anyway.
      this.encounterPending = false;
      return;
    }
    try {
      const party = runState.getParty();
      const playerSpecies = party[0]!.species;
      const playerDexId = SPECIES_TO_DEX[playerSpecies] ?? 263;

      const startingHp: Record<string, number> = {};
      for (const m of party) startingHp[m.species] = m.hp.current;
      const startingStatus = party.map((m) => m.status);

      // Build trainer's PokemonSet[] via the shared opponent builder —
      // fair 15-IV block + Hardy (stats-integrity 2026-08-02; the old
      // inline builder played secret perfect 31s), moves straight from
      // data/trainerParties.ts. Still synchronous on first call.
      const trainerTeam = trainer.party.map((m) =>
        trainerPokemonSet(m.species, m.level, m.moves),
      );

      const setup: BattleSetup = {
        seed: rollBattleSeed(),
        p1: {
          name: 'Player',
          team: teamFromParty(party),
          aiSeed: rollBattleSeed(),
          startingHp,
          startingStatus,
        },
        p2: {
          name: trainer.name,
          team: trainerTeam,
          aiSeed: rollBattleSeed(),
        },
      };

      // Follower: same flushPending pattern as the wild encounter path
      // (the trainer-LOS hook fires from advanceStep, so a mid-step
      // hopPending could land here).
      this.follower?.flushPending();

      if (IN_WORLD_DUEL_ENABLED) {
        // #26: trainer battles stage in the world too — the trainer
        // stays standing at their tile, watching their lead fight.
        const staged = await this.runInWorldTrainerDuel(
          scriptRef,
          trainer,
          setup,
          playerDexId,
        );
        if (staged) return; // finally clears encounterPending
        // Staging declined (walled corridor both ends — Cornered
        // trainer ruling): cut to the classic modal, cartridge-style.
      }

      audio.playSfx('encounter');
      await this.launchFrozenModal('BattleScene', {
        setup,
        opponentDexId: trainer.leadDexId,
        playerDexId,
        trainerScriptRef: scriptRef,
        battleZone: this.mapData.name,
      });
    } finally {
      this.encounterPending = false;
    }
  }

  /** Resolve the mounted sprite standing on tile (x, y). Trainers are
   *  anonymous NPCs whose npc.x/y mutate on the LOS run-up, so the
   *  npcSprites key (baked from ORIGINAL coords) can't be used —
   *  position matching is the reliable lookup. */
  private npcSpriteAtTile(x: number, y: number): Phaser.GameObjects.Sprite | null {
    const ts = this.mapData.tileSize;
    const cx = (x + 0.5) * ts;
    const cy = (y + 0.5) * ts;
    for (const s of this.npcSprites.values()) {
      if (
        s instanceof Phaser.GameObjects.Sprite &&
        Math.abs(s.x - cx) < ts / 2 &&
        Math.abs(s.y - cy) < ts
      ) {
        return s;
      }
    }
    return null;
  }

  /** Turn a trainer NPC's sprite to look at `target` (tile coords).
   *  Spectator rule for in-world duels: people watch the fight —
   *  without this, the trainer keeps the facing fossilized from the
   *  LOS conversation and stares at the player all battle (phone
   *  dogfood, 2026-08-03). */
  private faceNpcToward(
    npc: { x: number; y: number; graphicsId: string; localId?: string | null },
    target: { x: number; y: number },
  ): void {
    const sprite = this.npcSpriteAtTile(npc.x, npc.y);
    if (!sprite) return;
    const spriteKey =
      npc.graphicsId === 'OBJ_EVENT_GFX_VAR_0' && npc.localId
        ? VAR_0_SPRITES[npc.localId]
        : GFX_TO_SPRITE[npc.graphicsId];
    const layout =
      SPRITE_MANIFEST[spriteKey as keyof typeof SPRITE_MANIFEST]?.layout ?? 'static';
    const dx = target.x - npc.x;
    const dy = target.y - npc.y;
    const facing =
      Math.abs(dx) >= Math.abs(dy)
        ? dx >= 0
          ? 'right'
          : 'left'
        : dy > 0
          ? 'down'
          : 'up';
    const { frame, flipX } = frameForFacing(layout, facing);
    sprite.setFrame(frame);
    sprite.setFlipX(flipX);
  }

  /**
   * #26: in-world trainer duel. Mirrors runInWorldDuel's bracket with
   * trainer staging: BOTH leads are duel-owned battlers — mine walks
   * out from the follower tile, theirs from the trainer's tile — into
   * findTrainerDuelSlots' geometry (corridor when there's a gap,
   * side-by-side when the LOS run-up left them adjacent). The shared
   * mixins run every trainer branch (defeat flags, rival sparring,
   * victory music, Brock → EndRun win) via params.trainerScriptRef.
   */
  private async runInWorldTrainerDuel(
    scriptRef: string,
    trainer: TrainerData,
    setup: BattleSetup,
    playerDexId: number,
  ): Promise<boolean> {
    const lead = runState.getParty()[0];
    if (!lead) return true; // nothing to stage OR battle — swallow, matching the old no-op
    const npc = this.mapData.events.npcs.find((n) => n.scriptRef === scriptRef);
    const trainerTile = npc
      ? { x: npc.x, y: npc.y }
      : { x: this.playerTileX, y: this.playerTileY - 1 }; // defensive
    // Plan BEFORE any duel dressing — a 'modal' verdict must leave
    // the world untouched so the caller falls through cleanly (the
    // old order soft-locked duelActive on a decline — seams critic
    // blocker, 2026-08-03). Corridor-retreat: when the side-by-side
    // geometry is walled, BOTH owners back up and the leads square
    // off on the vacated tiles (owner ruling, "why can't either of
    // us back up?").
    const plan = planTrainerStaging(
      { x: this.playerTileX, y: this.playerTileY },
      trainerTile,
      {
        isBlocked: (x, y) =>
          !this.canStep(x, y) || Boolean(this.roamers?.roamerBlockingAt(x, y)),
        isPlayerSafe: (x, y) => this.isDuelSafePlayerTile(x, y),
        isTrainerSafe: (x, y) => this.isDuelSafePlayerTile(x, y),
      },
    );
    if (plan.kind === 'modal') return false;
    this.setDuelActive(true);
    audio.playSfx('encounter');
    const f = this.follower?.snapshot;
    const fromTile = f?.visible ? { x: f.tileX, y: f.tileY } : null;
    this.follower?.onSleep();
    if (plan.playerStep || (plan.trainerStep && npc)) {
      // Corridor-retreat: both give ground simultaneously — boxers
      // returning to their corners. Facings held on both.
      const moves: Promise<void>[] = [];
      if (plan.playerStep) {
        this.cameras.main.stopFollow();
        moves.push(this.duelPlayerStep(plan.playerStep));
      }
      if (plan.trainerStep && npc) {
        moves.push(this.duelTrainerStep(npc, plan.trainerStep));
      }
      await Promise.all(moves);
    }
    // Spectators watch the fight: the trainer turns toward their
    // lead's slot, the player toward theirs. Both were facing each
    // other from the LOS conversation. (Computed AFTER any retreat
    // steps — the signs are relative to final positions.)
    if (npc) this.faceNpcToward(npc, plan.opp);
    this.facing = this.directionFor(
      Math.sign(plan.mine.x - this.playerTileX),
      Math.sign(plan.mine.y - this.playerTileY),
    );
    this.player.setFlipX(this.facing === 'right');
    this.setPlayerSpriteFrame('idle');
    const stage = new DuelStage(this, this.mapData.tileSize, this.player);
    this.duelStage = stage;
    let endedInEndRun = false;
    let pinch: DuelPinchHandle | null = null;
    let hdToggle: DuelHdToggleHandle | null = null;
    let hdOn =
      !DUEL_SCENE_FILTER_ENABLED && DUEL_HD_BATTLERS_ENABLED && hdPreferred();
    try {
      const opponentSpecies = trainer.party[0]!.species;
      const introPromise = stage.intro({
        playerTile: { x: this.playerTileX, y: this.playerTileY },
        wildTile: plan.opp,
        leadSpecies: lead.species,
        // Retreat stagings materialize each lead ON its slot (the
        // vacated tile — empty by construction) instead of walking it
        // through its retreating owner.
        fromTile: plan.playerStep ? plan.mine : fromTile,
        isBlocked: (x, y) => !this.canStep(x, y),
        mySlot: plan.mine,
        opponent: {
          species: opponentSpecies,
          fromTile: plan.trainerStep ? plan.opp : trainerTile,
          slot: plan.opp,
        },
      });
      this.syncUiCameraIgnores();
      // HD taste-gate — mirrors runInWorldDuel. Both battlers here are
      // duel-owned (destroyed at outro/dispose), so no restore needed.
      const skinDuelSprites = (on: boolean): void =>
        this.skinDuelBattlers(
          [stage.getBattler(), stage.getOpponentBattler()],
          on,
        );
      if (hdOn) skinDuelSprites(true);
      await introPromise;
      pinch = mountDuelPinch(stage);
      // The scaler cycler is NOT mounted here any more (2026-08-04) — it is
      // scene-lifetime, already up from create(), and stays up when the duel
      // ends. Only the HD sprite-swap toggle is still duel-scoped.
      if (!DUEL_SCENE_FILTER_ENABLED && DUEL_HD_BATTLERS_ENABLED) {
        hdToggle = mountDuelHdToggle({
          initial: hdOn,
          onToggle: (next) => {
            hdOn = next;
            setHdPreferred(next);
            skinDuelSprites(next);
          },
        });
      }
      const host = new WorldBattleHost({
        worldScene: this,
        battler: stage.getBattler()!,
        wildSprite: stage.getOpponentBattler()!,
        slotTile: stage.slotTile!,
        wildTile: plan.opp,
        tileSize: this.mapData.tileSize,
        setup,
        opponentDexId: trainer.leadDexId,
        playerDexId,
        battleZone: this.mapData.name,
        trainerScriptRef: scriptRef,
        onWorldObjectSpawned: (obj) => this.uiCamera.ignore(obj),
        hdSkins: () => hdOn,
      });
      await host.run();
      endedInEndRun = host.endedInEndRun;
      if (!endedInEndRun) {
        const musicKey = MAP_MUSIC[this.currentMapName];
        if (musicKey) void audio.playMusic(this, musicKey);
      }
    } finally {
      pinch?.destroy();
      hdToggle?.destroy();
      // The scaler filter deliberately SURVIVES the duel now (2026-08-04) —
      // it is the whole game's look, not the battle's. Torn down on SLEEP /
      // SHUTDOWN instead.
      // Same teardown split as runInWorldDuel: EndRun = world asleep,
      // no outro theater possible; otherwise full restore.
      if (endedInEndRun) {
        stage.dispose();
        this.duelStage = null;
      } else {
        await stage.outro({ recallBattlerInPlace: Boolean(plan.playerStep) });
        stage.dispose();
        this.duelStage = null;
        // The beaten (or victorious) trainer turns back to the player
        // — the cartridge concession beat.
        if (npc) {
          this.faceNpcToward(npc, {
            x: this.playerTileX,
            y: this.playerTileY,
          });
        }
        this.follower?.onWake(
          { x: this.playerTileX, y: this.playerTileY },
          this.facing,
          this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
          this.followerHomeAfterDuel(fromTile, Boolean(plan.playerStep)),
        );
        this.setDuelActive(false);
      }
    }
    return true;
  }

  /**
   * Where the follower re-materializes when a duel ends. Both duel
   * paths re-aim the player's facing at their opponent for the fight,
   * so onWake's default "one tile behind the player" derivation points
   * somewhere the follower never stood — it teleported across the
   * player the instant the world came back, right after the outro had
   * walked the battler home to the tile it LEFT from (owner report,
   * 2026-08-03: "he snaps back to a position behind the way I was
   * facing"). Hand back that same departure tile so the creature simply
   * steps into its own footprints.
   *
   * Two cases keep the derived placement: a rung-2/3 player move (the
   * departure tile may be the one the player is now standing on, and
   * that battler was recalled in place rather than walked home), and a
   * surviving wild parked on it after a flee.
   */
  private followerHomeAfterDuel(
    fromTile: { x: number; y: number } | null,
    playerMoved: boolean,
  ): { x: number; y: number } | undefined {
    if (!fromTile || playerMoved) return undefined;
    if (this.roamers?.roamerBlockingAt(fromTile.x, fromTile.y)) return undefined;
    return fromTile;
  }

  private directionFor(dx: number, dy: number): ConnectionDir {
    if (dy < 0) return 'up';
    if (dy > 0) return 'down';
    if (dx < 0) return 'left';
    return 'right';
  }

  private canStep(x: number, y: number): boolean {
    // Warp tiles override collision. Doors in pokefirered have collision=1
    // (you "can't stand on them") but the cartridge fires the warp the
    // moment you step in. Without this, doors read as locked walls.
    if (this.mapData.warps.some((w) => w.x === x && w.y === y)) return true;
    const idx = y * this.mapData.width + x;
    const c = this.mapData.collision[idx] ?? 1;
    if (c !== 0) return false;
    // Water / waterfall / puddle tiles have collision=0 in pokefirered map
    // data because the cartridge gates them via behavior, not collision.
    // We mirror that here so the player can't walk on water.
    const behavior = this.mapData.behaviors[idx] ?? 0;
    if (IMPASSABLE_BEHAVIORS.has(behavior)) return false;
    // NPC occupancy. Built per-map in loadMap.
    if (this.npcTiles.has(idx)) return false;
    return true;
  }

  private attemptConnectionTransition(
    conn: RuntimeMap['connections'][number],
    offX: number,
    offY: number,
  ): void {
    const destName = MAP_ID_TO_NAME[conn.map];
    if (!destName) {
      // Connected to a map we haven't converted yet (e.g. Oldale in Phase 3). No-op.
      return;
    }
    const destData = this.cache.json.get(`map:${destName}`) as RuntimeMap;
    let entryX = offX;
    let entryY = offY;
    // Wrap perpendicular to the direction; clamp parallel to the dest's edge.
    switch (conn.direction) {
      case 'up':
        entryX = offX - conn.offset;
        entryY = destData.height - 1;
        break;
      case 'down':
        entryX = offX - conn.offset;
        entryY = 0;
        break;
      case 'left':
        entryX = destData.width - 1;
        entryY = offY - conn.offset;
        break;
      case 'right':
        entryX = 0;
        entryY = offY - conn.offset;
        break;
    }
    // Refuse to enter if the entry tile is blocked or out-of-bounds for the dest.
    if (
      entryX < 0 ||
      entryY < 0 ||
      entryX >= destData.width ||
      entryY >= destData.height ||
      (destData.collision[entryY * destData.width + entryX] ?? 1) !== 0
    ) {
      return;
    }
    void this.transitionToMap(destName, entryX, entryY);
  }

  /** PR 13b: public via ScriptHostScene so cinematic handlers (Oak
   *  intercept, rival cinematic) can place the player in a new map at
   *  the end of a sequence. Includes the standard fade-out → swap →
   *  fade-in. Handlers passing a known MapName get the type-safe path;
   *  the string overload is for ScriptHostScene callers that don't
   *  import MapName. */
  async transitionToMap(
    name: MapName,
    entryX: number,
    entryY: number,
    entryFacing?: ConnectionDir,
  ): Promise<void> {
    // Cartridge-pure door transition: fade to black → swap → fade up.
    // doorTransition owns the transitioning lifecycle.
    await this.doorTransition(() => {
      // Step 3b: dispatch to mega-map exterior path when the destination
      // is a region in the loaded world; fall back to per-map loadMap.
      this.dispatchEnterMap(name, entryX, entryY);
      // Cartridge "step out of the doorway" beat: warp callers pass the
      // direction the player was moving when they hit the warp, and we
      // advance them one tile in that direction so they land in front
      // of the door (or above the rug) instead of on the warp tile
      // itself. Edge connections don't pass a facing — they spawn on
      // the dest's matching edge tile, which is intentional + correct.
      if (entryFacing !== undefined) {
        this.facing = entryFacing;
        this.autoStepOut(entryFacing);
      }
      // Follower: re-derive AFTER autoStepOut so the home tile is
      // computed from the post-step-out player position. Sends out the
      // current lead species during the fade-in window (cartridge-canon
      // HGSS — the Pokéball-pop completes ~640 ms into the new map's
      // visible window). Early-returns inside FollowerState if no
      // species (empty party after Reset Run wiping the lead).
      if (this.follower && runState.hasPartyMember()) {
        this.follower.onMapLoad(
          { x: this.playerTileX, y: this.playerTileY },
          this.facing,
          this.buildBlockedNearPlayer(this.playerTileX, this.playerTileY),
        );
      }
    });
    this.onMapEntered();
  }

  /**
   * Doorframe transition (`motion-threshold` token, 440 ms cartridge-pure
   * black fade — FireRed doors don't pan or zoom, just blackout-and-swap).
   *
   * Three phases:
   *   1. 180 ms fade out to black (`DOOR_FADE_RGB`).
   *   2. 80 ms black hold during which the tilemap swap fires (Phaser's
   *      fade overlay stays opaque between fadeOut and fadeIn, so the
   *      swap is hidden).
   *   3. 180 ms fade in from black.
   *
   * Reduced motion: INSTANT CUT. tilemapSwap runs, transitioning toggles
   * true→false on the next tick. Lifecycle identical to the full path.
   *
   * Lifecycle: `transitioning = true` on entry, cleared on resolution.
   * Callers don't need to manage it (mirrors the old `fade()` contract).
   */
  private async doorTransition(tilemapSwap: () => void): Promise<void> {
    // Restore-PRIOR, never unconditional false: when a cinematic bracket
    // (runHandlerBracket) already holds transitioning=true, a mid-
    // cinematic warp must not clobber the hold. Round-4 playtest: the
    // Oak-intercept's post-warp delay beats went input-live at lab
    // state 1 (leave-guard triggers arm at state 2), the player could
    // step south out the door, and the walk-up beat then drove the
    // sprite against the wrong map's coordinate space — "randomly spin
    // across the map." Normal door use enters with transitioning=false
    // and restores false — behavior unchanged there.
    const outerHold = this.transitioning;
    this.transitioning = true;

    if (effectsReduced()) {
      tilemapSwap();
      // Resolve on next tick so the caller's `await` sees the restored
      // transitioning state before any post-transition side effects.
      await new Promise<void>((resolve) => this.time.delayedCall(0, resolve));
      this.transitioning = outerHold;
      return;
    }

    const cam = this.cameras.main;
    const [r, g, b] = DOOR_FADE_RGB;

    // Phase 1: fade to black.
    await new Promise<void>((resolve) => {
      cam.once('camerafadeoutcomplete', () => resolve());
      cam.fadeOut(DOOR_PHASE1_MS, r, g, b);
    });

    // Phase 2: tilemap swap during the black hold. Phaser's fade overlay
    // remains at full opacity until fadeIn is called, so we can swap +
    // delay safely. tilemapSwap calls applyCameraBounds → setZoom for
    // the new map; the cartridge fade doesn't tween zoom so we don't
    // need to capture/restore peak zoom like the old zoom cinematic did.
    tilemapSwap();
    await new Promise<void>((resolve) => {
      this.time.delayedCall(DOOR_PHASE2_MS, () => resolve());
    });

    // Phase 3: fade in from black.
    await new Promise<void>((resolve) => {
      cam.once('camerafadeincomplete', () => resolve());
      cam.fadeIn(DOOR_PHASE3_MS, r, g, b);
    });

    this.transitioning = outerHold;
  }

  /**
   * Advance the player one tile in `dir` if that tile is in-bounds and
   * passable; otherwise leave them on the warp tile. We deliberately skip
   * checkWarp on this auto-step — it's a positioning
   * fix-up, not a real player step. The next real input is what re-engages
   * those side effects.
   */
  private autoStepOut(dir: ConnectionDir): void {
    let nx = this.playerTileX;
    let ny = this.playerTileY;
    switch (dir) {
      case 'up': ny -= 1; break;
      case 'down': ny += 1; break;
      case 'left': nx -= 1; break;
      case 'right': nx += 1; break;
    }
    if (
      nx < 0 ||
      ny < 0 ||
      nx >= this.mapData.width ||
      ny >= this.mapData.height
    ) {
      return;
    }
    if (!this.canStep(nx, ny)) return;
    this.playerTileX = nx;
    this.playerTileY = ny;
    this.persistPlayerPosition();
    this.positionPlayer();
    // Update sprite to face the entry direction. loadMap defaults facing to
    // 'down' and forces the sprite frame; without this the player would
    // visually face down even after we walked them out of a north-facing
    // doorway.
    this.player.setFlipX(this.facing === 'right');
    this.setPlayerSpriteFrame('idle');
  }

  /**
   * Hook fired once a transition into a new map has fully resolved (both
   * fade halves done, transitioning flag cleared). Used for first-visit
   * triggers — starter draft on entering Oak's Lab with an empty party,
   * future per-zone drafts (M4c), etc. Boot-time loadMap doesn't go
   * through transitionToMap so this hook doesn't fire on first paint —
   * which is correct: we don't want a draft modal in the player's
   * bedroom.
   */
  private onMapEntered(): void {
    // (Map music switch lives in loadMap so it fires on every load,
    // not just transitions that go through onMapEntered.)

    // Pallet exit gate: leaving Pallet Town northward without a starter
    // shows the "visit Oak first" dialogue ON Route 1 (the transition
    // already completed), then on dialogue close bounces the player back
    // to Pallet facing south. The bounce action lives in runDialogueAction
    // under the '__pallet_bounce__' scriptRef.
    //
    // Route 1 is the only route reachable pre-starter, so this single
    // map-id check covers all egress paths today. If a future map adds
    // another pre-starter exit, mirror this block there.
    if (this.mapData.name === 'Route1' && !runState.hasStarter()) {
      this.showDialogue(
        [
          'You should visit PROF. OAK first.',
          'Pick up your starter at his lab.',
        ],
        '__pallet_bounce__',
      );
      return;
    }

    // v8: pending-draft restoration. If a previous run wrote a pending
    // draft for this map (tab close mid-modal), re-launch DraftScene
    // with the SAME offers — no re-roll, no rng draw. Same identity
    // (uuid) carries through, so the picked offer transfers cleanly
    // into the party. Step 3 of the Pokédex implementation. Post-seamless-
    // world rekey: pending.zoneId is the short region name (e.g. "Route1"),
    // same key DRAFT_POOLS uses.
    //
    // Oak's Lab exception, redesigned round-4: pendingDraft caches at
    // the end of the Oak intercept (rollOffers runs during the warp-in
    // pass), so it's present on EVERY lab entry until the pick. Under
    // the draft-at-Oak flow the modal COMING TO YOU is the design — so
    // a lab re-entry with the intro done but no starter picked is by
    // definition an interrupted intro (escaped mid-monologue under the
    // pre-hold-fix prod build, tab-closed mid-modal, pathological poll
    // timeout). Rescue: promote the state machine to PRE_PICK if the
    // escape happened mid-monologue, then re-present the cached offers
    // (same uuids, no re-roll) after the fade settles. The monologue
    // is not replayed — they saw it or fled it; the pick is what the
    // run needs.
    const pending = runState.getPendingDraft();
    if (pending && pending.zoneId === this.mapData.name) {
      const isOakLab = this.mapData.name === 'PalletTown_ProfessorOaksLab';
      if (isOakLab) {
        if (
          runState.hasFlag(FLAGS.OAK_INTRO_DONE) &&
          !runState.hasFlag(FLAGS.STARTER_PICKED)
        ) {
          if (getLabSceneState() < LAB_SCENE_STATES.PRE_PICK) {
            setLabSceneState(LAB_SCENE_STATES.PRE_PICK);
          }
          this.time.delayedCall(400, () => {
            void this.launchStarterDraft();
          });
        }
      }
      if (!isOakLab) {
        const pool = DRAFT_POOLS[pending.zoneId];
        this.encounterPending = true;
        void this.launchFrozenModal(
          'DraftScene',
          { title: pool?.title ?? 'Draft', offers: pending.offers },
          { waitForFadeIn: true },
        ).finally(() => {
          this.encounterPending = false;
        });
      }
      return;
    }

    // Starter draft (Oak's Lab) and per-zone drafts now flow through
    // the same rollOffers path. Gating differs:
    //   - Oak's Lab fires when FLAG_OAK_INTRO_DONE is SET (cinematic
    //     finished) AND FLAG_STARTER_PICKED is unset (H8, PR 11). The
    //     OAK_INTRO_DONE half is cartridge-faithful empty-lab posture:
    //     if the player wanders into the lab before triggering the
    //     intercept, no offers roll, no pendingDraft caches, balls are
    //     hidden so no ball-tap can launch the modal. Reset Run clears
    //     both flags, so post-reset the cinematic must re-run before a
    //     re-pick is possible.
    //   - Zone drafts fire on the once-per-run hasDrafted(mapName) gate.
    // Both share the soft hint for pre-starter players entering a zone.
    const pool = DRAFT_POOLS[this.mapData.name];
    if (!pool) return;
    const isOakLab = this.mapData.name === 'PalletTown_ProfessorOaksLab';
    const shouldFire = isOakLab
      ? runState.hasFlag(FLAGS.OAK_INTRO_DONE) && !runState.hasFlag(FLAGS.STARTER_PICKED)
      : !runState.hasDrafted(this.mapData.name);
    if (!shouldFire) return;

    if (!isOakLab && !runState.hasStarter()) {
      // Soft hint instead of silent no-op so a fresh post-reset player
      // understands why nothing happened. Defer one tick so it lands
      // after the fade-in, not during the transition.
      this.time.delayedCall(50, () => {
        this.showDialogue([
          'You should visit PROF. OAK first.',
          'Pick up your starter at his lab.',
        ]);
      });
      return;
    }

    // Mark drafted BEFORE rolling so a re-entry during the modal can't
    // double-fire the rollOffers stream (extra rng draws would shift
    // the canonical order).
    if (!isOakLab) {
      runState.markDrafted(this.mapData.name);
    }
    this.encounterPending = true;

    // Glass-Over-World: WorldScene stays AWAKE during rollOffers so the
    // captureFrom inside launchFrozenModal can snapshot a live (not
    // frozen-black) frame. encounterPending already gates movement;
    // setBackgroundColor and the screen-stays-dark moment that the
    // previous immediate-sleep produced are gone. Sleep happens inside
    // launchFrozenModal after rollOffers + captureFrom complete.
    //
    // Defensive try/catch (added 2026-05-11 after user-reported mobile
    // blank-page on Oak's Lab entry): if rollOffers throws (slow dex
    // chunk fetch, network blip, anything) we MUST recover — log the
    // error, clear the encounter-pending gate, and surface the failure
    // in-game so the player can at least walk around / re-trigger the
    // draft by leaving + re-entering the map.
    const zoneId = this.mapData.name;
    void (async () => {
      try {
        const excludedSpecies = new Set<string>([
          ...runState.getDraftedSpecies(),
          ...runState.getParty().map((m) => m.species),
        ]);
        // v9: caughtAt on every DraftOffer is non-nullable; draft modals
        // fire downstream of map load so position is set by invariant.
        // Asserting at the call site keeps the precondition explicit
        // instead of an implicit-everywhere-it-might-fire convention.
        const position = runState.getPosition();
        if (!position) {
          throw new Error(
            '[onMapEntered] rollOffers called with null position — precondition violation',
          );
        }
        const caughtAt = {
          mapName: position.mapName,
          tileX: position.tileX,
          tileY: position.tileY,
          when: Date.now(),
        };
        const offers = await rollOffers(
          zoneId,
          seedStore.rngFor('draft'),
          seedStore.rngFor('ivs'),
          excludedSpecies,
          caughtAt,
        );
        // Persist pendingDraft BEFORE launching so a tab close during
        // the modal restores to the SAME offers (not a re-roll, which
        // would burn extra rng draws and change every downstream
        // outcome).
        runState.setPendingDraft({ zoneId, offers });
        saveManager.save();
        // PR 13c (Ship 2): Oak's Lab no longer auto-launches DraftScene
        // on entry. The 3 starter ball NPCs are tap-interactive
        // (PR 13d); the ball-tap handler reads this pendingDraft and
        // launches the modal with the tapped species pre-flipped.
        // Other zone-drafts (Route 1, Forest, etc.) still auto-launch
        // — there's no per-ball UX to tap-into there.
        const isOakLabDraft = zoneId === 'PalletTown_ProfessorOaksLab';
        if (!isOakLabDraft) {
          await this.launchFrozenModal('DraftScene', {
            title: pool.title,
            offers,
          });
        }
      } catch (err) {
        console.error('[onMapEntered] draft setup failed; recovering:', err);
        // Surface the failure in-game so a phone user (without
        // DevTools access) can SEE which path broke. Format:
        // "Draft failed: <Error name>: <Error message>". Truncate
        // long messages so the dialogue box stays readable.
        const e = err as Error;
        const eName = e?.name ?? 'Error';
        const eMsg = (e?.message ?? String(err)).slice(0, 120);
        const eStack = (e?.stack ?? '').split('\n').slice(0, 3).join(' | ').slice(0, 200);
        this.scene.wake();
        // Defer the dialogue to the next tick so the wake cycle
        // (PartyOrbs/joystick remount, etc.) completes first.
        this.time.delayedCall(50, () => {
          this.showDialogue([
            `Draft failed.`,
            `${eName}: ${eMsg}`,
            eStack ? eStack : 'No stack available.',
          ]);
        });
      } finally {
        this.encounterPending = false;
      }
    })();
  }

  private fade(direction: 'in' | 'out'): Promise<void> {
    return new Promise((resolve) => {
      const cam = this.cameras.main;
      const handler = (): void => {
        cam.off(direction === 'out' ? 'camerafadeoutcomplete' : 'camerafadeincomplete', handler);
        resolve();
      };
      cam.once(direction === 'out' ? 'camerafadeoutcomplete' : 'camerafadeincomplete', handler);
      if (direction === 'out') cam.fadeOut(TRANSITION_MS, 253, 244, 220);
      else cam.fadeIn(TRANSITION_MS, 253, 244, 220);
    });
  }

  private loadMap(name: MapName, entryX: number, entryY: number): void {
    const data = this.cache.json.get(`map:${name}`) as RuntimeMap;
    if (!data) throw new Error(`Map asset not loaded: ${name}`);

    // Tear down. If we're switching FROM exterior mode, the mega-map
    // tilemap is owned by this.world and must persist across mode
    // switches — just hide the layers. Otherwise (interior→interior or
    // first boot), destroy the previous per-map tilemap.
    if (this.mode === 'exterior' && this.world) {
      this.world.groundLayer.setVisible(false);
      this.world.aboveLayer.setVisible(false);
    } else if (this.tilemap) {
      this.tilemap.destroy();
    }
    if (this.collisionOverlay) {
      this.collisionOverlay.destroy();
      this.collisionOverlay = undefined;
    }
    for (const npc of this.npcSprites.values()) npc.destroy();
    this.npcSprites.clear();
    // Roam: interior maps have no roamers, and exterior sprites live at
    // world coords that would bleed over an interior tilemap. Clearing
    // here means every interior trip wipes the groups — so stepping
    // back outside is a genuine re-entry that repopulates (the
    // respawn-on-re-entry policy, deliberately).
    this.roamers?.clearAll();

    this.mode = 'interior';
    this.currentRegion = null;
    this.mapData = data;
    this.currentMapName = name;
    this.playerTileX = entryX;
    this.playerTileY = entryY;
    this.facing = 'down';
    // Resume-on-reload: write the new position so the next save flush
    // captures this map+tile. transitionToMap may overwrite facing right
    // after via autoStepOut; that path also calls setPosition then.
    // loadMap is the interior path (mode set to 'interior' above), so
    // persistPlayerPosition stores raw coords without region offset.
    this.persistPlayerPosition();

    // Rescue saves stranded mid-post-pick-cinematic (latch set, state
    // still 2 — the leave-guard would otherwise trap the player in the
    // lab forever; see repairStrandedLabPostPick). Runs BEFORE the NPC
    // mount below so rival/ball visibility resolves against the
    // repaired flags. saveManager flushes on the next auto-save point.
    if (name === 'PalletTown_ProfessorOaksLab' && repairStrandedLabPostPick()) {
      console.warn('[loadMap] repaired save stranded mid lab post-pick cinematic');
    }

    const grid: number[][] = [];
    for (let y = 0; y < data.height; y++) {
      const row: number[] = [];
      for (let x = 0; x < data.width; x++) {
        row.push(data.metatileIds[y * data.width + x]!);
      }
      grid.push(row);
    }

    const tilemap = this.make.tilemap({
      data: grid,
      tileWidth: data.tileSize,
      tileHeight: data.tileSize,
    });
    // margin=1, spacing=2 — mirrors ATLAS_MARGIN/ATLAS_SPACING in the
    // converter's buildAtlas.ts. Without this, Phaser samples the tile
    // at content-only positions and the renderer can pick up neighboring
    // tile colors at sub-pixel scroll offsets (the horizontal-line
    // artifact during camera scroll). With it, samples land in the
    // edge-extruded padding and stay stable.
    const tileset = tilemap.addTilesetImage(
      'atlas',
      `atlas:${name}`,
      data.tileSize,
      data.tileSize,
      1, // margin
      2, // spacing
    );
    if (!tileset) throw new Error(`Failed to addTilesetImage for ${name}`);
    const layer = tilemap.createLayer(0, tileset, 0, 0);
    if (!layer) throw new Error(`Failed to createLayer for ${name}`);
    this.tilemap = tilemap;
    this.layer = layer;
    layer.setDepth(0);

    // Above-sprite layer. Each cell in `aboveLayer` (built by the converter
    // from layer_type ≠ COVERED AND non-empty front plane) re-renders from
    // a SEPARATE front-only atlas where each metatile contains just its
    // front plane (subtile entries 4-7) with the rest transparent. Using
    // a front-only atlas (instead of the composite) is what stops grass
    // ground / tree-trunk shadows from drawing above the sprite — only
    // the canopy/blade/roof artwork covers the player.
    const tilesetAbove = tilemap.addTilesetImage(
      'atlas-above',
      `atlas-above:${name}`,
      data.tileSize,
      data.tileSize,
      1,
      2,
    );
    if (!tilesetAbove) throw new Error(`Failed to addTilesetImage (above) for ${name}`);
    const aboveTileLayer = tilemap.createBlankLayer(
      'above',
      tilesetAbove,
      0,
      0,
      data.width,
      data.height,
    );
    if (!aboveTileLayer) throw new Error(`Failed to createBlankLayer for ${name}`);
    for (let y = 0; y < data.height; y++) {
      for (let x = 0; x < data.width; x++) {
        const idx = y * data.width + x;
        if ((data.aboveLayer[idx] ?? 0) === 1) {
          aboveTileLayer.putTileAt(data.metatileIds[idx]!, x, y);
        }
      }
    }
    aboveTileLayer.setDepth(6); // player=5, NPCs=4
    this.aboveLayer = aboveTileLayer;

    // Build NPC occupancy set + sprites from the now-set this.mapData.
    // (Common path with enterExteriorRegion in mega-map mode.)
    this.rebuildNpcSprites();

    // Camera bounds + bg via applyCameraBounds — interior path expands
    // bounds when the map is smaller than the viewport so the player
    // stays centered (small interiors framed in black instead of walking
    // off-center to clamp at map edges).
    this.applyCameraBounds();

    this.positionPlayer();
    this.cameras.main.centerOn(this.player.x, this.player.y);
    this.syncUiCameraIgnores();
    this.repositionAll();
    this.updateStatusText();

    // Resume-on-reload checkpoint: every map transition is a natural
    // save point. The pagehide handler covers graceful close, but a
    // browser crash mid-walk would only restore to the last save event
    // (draft pick / battle end / nurse heal). Saving on every map
    // entry adds one localStorage write per door/edge crossing — well
    // worth the durability.
    saveManager.save();

    // Rebuild the pathfinder grid for this map. The grid is pure
    // terrain — NPC occupancy is supplied per findPath request via
    // getAvoidTiles, which reads this.npcTiles LIVE, so plans route
    // around NPCs at their current positions (Bug 5's "weird denial"
    // stays fixed) and a mid-scene visibility change (e.g. DraftScene
    // hiding the starter balls) is picked up without a grid rebuild.
    // Warp tiles stay walkable so doors / staircases can be pathfind
    // targets. Tall grass is walkable but high-cost so paths route
    // around it when reasonable detours exist (Bug 4).
    const pathGrid = buildPathfinderGrid(data, IMPASSABLE_BEHAVIORS, {
      highCostBehaviors: new Set<number>([MB_TALL_GRASS]),
    });
    this.pathfinder?.reset();
    this.pathfinder = new PathfinderManager(pathGrid, {
      onArrival: (intent) => this.onPathArrival(intent),
      // NPCs + roamers, both read LIVE at every findPath request (the
      // commit-6b6ccbb request-time contract). Roamer tiles as avoid
      // points make tapped-roamer tiles unreachable as walk targets —
      // handleTap routes those through the interactable-approach flow
      // instead, so COMMAND engages roamers rather than pathing into
      // them. A roamer crossing a planned path mid-walk halts the
      // walk (the bump intercept cancels the path in COMMAND mode —
      // never an unchosen battle); tap again to replan. Common now,
      // not rare.
      getAvoidTiles: () => [
        ...this.npcTileList(),
        ...(this.roamers?.tileList() ?? []),
      ],
    });

    // Phase 15a: switch to this map's music. Lives in loadMap (not
    // onMapEntered) so it fires on EVERY map load — boot from title,
    // resume-on-reload, warps, edge connections — not just transitions
    // that go through onMapEntered. Without this, title music persists
    // into Player's House on a fresh NEW RUN until the player crosses a
    // map boundary. playMusic is a no-op when the same key is already
    // current, so it's free on consecutive same-music maps.
    const musicKey = MAP_MUSIC[name];
    if (musicKey) void audio.playMusic(this, musicKey);

    // NOTE: follower.onMapLoad is NOT called here. transitionToMap
    // fires it after autoStepOut so the follower lands behind the
    // *post-step-out* player tile (warp tiles → player moves one tile
    // out of the doorway before the send-out anchors). create()'s
    // boot path uses follower.spawn() directly, which is strictly
    // more (sets species + places + send-out).
  }

  /** PR 12.5: public refresh hook for callers that mutate FLAG_HIDE_*
   *  flags and need the world to re-render NPCs against the new
   *  visibility state. Wraps rebuildNpcSprites + syncUiCameraIgnores —
   *  the same pair `loadMap` already runs. DraftScene.commit calls this
   *  after setting the 3 starter-ball-hide flags so the lab pedestal
   *  visually empties without a map reload.
   *
   *  Cheap to call (re-creates ~3-10 Phaser sprites depending on map);
   *  unnecessary for callers that already trigger a map transition (the
   *  transition's onMapEntered runs loadMap which rebuilds anyway). */
  refreshNpcVisibility(): void {
    if (!this.mapData) return;
    this.rebuildNpcSprites();
    this.syncUiCameraIgnores();
  }

  /** Current NPC-occupied tiles as coords — the pathfinder's
   *  getAvoidTiles source. Read at every findPath request, so a
   *  visibility refresh (starter balls picked, FLAG_HIDE_* mutations)
   *  is reflected in the very next plan without a grid rebuild. */
  private npcTileList(): TileXY[] {
    const w = this.mapData.width;
    return [...this.npcTiles].map((idx) => ({
      x: idx % w,
      y: Math.floor(idx / w),
    }));
  }

  /**
   * (Re)build npcTiles + npcSprites from this.mapData.events.npcs.
   * Called from loadMap (interior mode, sprites at local coords) and
   * enterExteriorRegion (exterior mode, sprites at GLOBAL coords — the
   * synthesized mapData carries every region's NPCs translated to global).
   *
   * FLAG_HIDE_* NPCs are filtered via npcIsHidden() — hidden iff the
   * flag is currently SET in runState. Cartridge defaults are seeded
   * by runState.reset()'s INITIAL_HIDDEN_FLAGS; handlers set/clear at
   * cinematic beats (Oak intercept clears OAK_IN_HIS_LAB, DraftScene
   * sets the 3 ball-hide flags on pick, etc.). The flag-optional-chain
   * in npcIsHidden defends against malformed runtime.json entries
   * (cartridge `type: "clone"` entries lack movement/flag/script).
   */
  private rebuildNpcSprites(): void {
    this.npcTiles.clear();
    for (const sprite of this.npcSprites.values()) {
      this.tweens.killTweensOf(sprite); // item-ball pulse (G4)
      sprite.destroy();
    }
    this.npcSprites.clear();

    const data = this.mapData;
    const ts = data.tileSize;
    for (const npc of data.events.npcs) {
      if (npcIsHidden(npc)) continue;
      this.npcTiles.add(npc.y * data.width + npc.x);
      const cx = (npc.x + 0.5) * ts;
      const cy = (npc.y + 0.5) * ts;
      const spriteKey =
        npc.graphicsId === 'OBJ_EVENT_GFX_VAR_0' && npc.localId
          ? VAR_0_SPRITES[npc.localId]
          : GFX_TO_SPRITE[npc.graphicsId];
      let sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Rectangle;
      if (spriteKey) {
        // 16×32 NPCs use origin (0.5, 0.75) so their feet sit at the tile
        // center and their head extends up into the tile above. Static
        // tile-sized sprites (item balls, etc.) are 16×16 and want a true
        // centered origin so they sit in their own tile.
        const originY = STATIC_TILE_SPRITES.has(spriteKey) ? 0.5 : 0.75;
        // C3: pick the initial frame + flipX from the NPC's movementType +
        // sprite layout. A face-up NURSE behind a counter, a face-right
        // youngster looking at a sign, etc. — all rendered correctly at
        // mount time. Wandering / unknown movementTypes fall back to the
        // down-facing frame.
        const layout = SPRITE_MANIFEST[spriteKey as keyof typeof SPRITE_MANIFEST]?.layout ?? 'static';
        const facing = facingFromMovementType(npc.movementType);
        const { frame, flipX } = frameForFacing(layout, facing);
        sprite = this.add
          .sprite(cx, cy, spriteKey, frame)
          .setOrigin(0.5, originY)
          .setDepth(4);
        if (flipX) sprite.setFlipX(true);
        // G4: item balls carry a subtle alpha pulse — the "tappable
        // collectible" affordance. Picked balls never reach here (their
        // FLAG_HIDE_* filters them out above), so the pulse trivially
        // respects the picked state. Static under reduced motion.
        if (spriteKey === 'item_ball' && !effectsReduced()) {
          this.tweens.add({
            targets: sprite,
            alpha: 0.7,
            duration: 900,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        }
      } else {
        // Defensive fallback for NPCs whose sprite-fetch entry is missing.
        // MVP Bucket B baked 15 NPC sprites; this path should be rare,
        // but a future NPC added without a sprite asset shouldn't crash —
        // it renders as a placeholder glass-tinted square instead of olive.
        sprite = this.add
          .rectangle(
            cx,
            cy,
            14,
            14,
            GLASS_FILL_MID.hex,
            GLASS_FILL_MID.alpha,
          )
          .setOrigin(0.5)
          .setStrokeStyle(1, SPECULAR_EDGE.hex, SPECULAR_EDGE.alpha)
          .setDepth(4);
      }
      // localId per cartridge runtime.json is unique per map; clone NPCs
      // (no localId) get a synthetic tile-derived key so they're still
      // individually addressable within the Map.
      const key = npc.localId ?? `__anon_${npc.x}_${npc.y}`;
      this.npcSprites.set(key, sprite);
    }
  }

  /** Build a Set<string> of "x,y" tile keys that the follower must NOT
   *  occupy — covers map collision (walls, water, ledges), behavior-
   *  based impassables, NPCs, and off-map tiles in a 5×5 area around
   *  (cx, cy). FollowerState's auto-shuffle searches the 8 tiles
   *  around the player or follower home; a 5×5 box more than covers
   *  the candidate set. */
  private buildBlockedNearPlayer(cx: number, cy: number): Set<string> {
    const blocked = new Set<string>();
    if (!this.mapData) return blocked;
    const w = this.mapData.width;
    const h = this.mapData.height;
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= w || y >= h) {
          blocked.add(`${x},${y}`);
          continue;
        }
        if (!this.canStep(x, y)) blocked.add(`${x},${y}`);
      }
    }
    return blocked;
  }

  /** UI camera should never render world-side objects. Called after every
   *  loadMap (npcSprites change) and after toggleCollisionOverlay. */
  private syncUiCameraIgnores(): void {
    if (!this.uiCamera) return;
    const worldObjs: Phaser.GameObjects.GameObject[] = [this.layer, this.player, ...this.npcSprites.values()];
    if (this.aboveLayer) worldObjs.push(this.aboveLayer);
    if (this.collisionOverlay) worldObjs.push(this.collisionOverlay);
    // Follower sprites live in world space (cameras.main). The UI
    // camera must ignore all three (Pokémon + Pokéball + flash) so
    // the send-out beats + idle BW filmstrip don't double-render on
    // top of dialogue / orb chrome. Pokéball + flash exist from
    // Follower construction; the Pokémon sprite is lazy (first
    // prepareSpecies). For the lazy case, the next syncUiCameraIgnores
    // fires on the next loadMap and picks it up — initial-spawn
    // window before that is ~1 frame, acceptable.
    if (this.follower) worldObjs.push(...this.follower.getAllSprites());
    // Roamer sprites live in world space like the follower's — same
    // double-render failure mode through the zoom-1 uiCamera without
    // this. Populate paths call syncUiCameraIgnores right after
    // mounting new sprites.
    if (this.roamers) worldObjs.push(...this.roamers.getSprites());
    // Duel battler lives in world space for the duration of an
    // in-world battle; runInWorldDuel syncs immediately on creation.
    if (this.duelStage) worldObjs.push(...this.duelStage.getSprites());
    // Handler-owned cinematic walkers (Oak intercept, rival legs) live
    // in world space too. Missing from this list until 2026-08-02 —
    // the walker double-rendered through the zoom-1 uiCamera at raw
    // world coords: the playtest's "small rival sprite in the top-left
    // corner over black." Safe to spread: the DESTROY listener removes
    // dead sprites from the set, so no destroyed refs land here.
    worldObjs.push(...this.handlerOwnedSprites);
    this.uiCamera.ignore(worldObjs);
  }

  private positionPlayer(): void {
    const t = this.mapData.tileSize;
    this.player.setPosition((this.playerTileX + 0.5) * t, (this.playerTileY + 0.5) * t);
  }

  /**
   * Camera zoom = how many screen pixels per world pixel.
   *
   * Portrait (mobile): zoom enough that the map fills *both* viewport axes
   * — no off-map cream BG showing under translucent overlay controls. Ceil
   * to an integer to keep pixel-art crisp.
   *
   * Landscape (desktop): aim for ~18 tiles across the width — roughly GBA's
   * 15-tile screen with breathing room. BUT if the map is smaller than that
   * target (small interiors like Player's House 1F), scale up to the fill
   * zoom so the map covers the canvas rather than leaving off-map space on
   * the right / bottom (the bug Pewter Center 1F surfaced on desktop).
   *
   * Min 2x so glyphs + sprite details stay readable on tiny viewports.
   */
  private computeZoom(): number {
    const w = this.scale.width;
    const h = this.scale.height;
    const tileSize = this.mapData.tileSize;
    const isPortrait = h > w;
    // Mode-agnostic baseline: player sprite renders at the SAME on-screen
    // size in both interior + exterior modes, so the zoom slider feels
    // consistent across maps + the cartridge "same camera scale
    // everywhere" framing is preserved. Was previously map-fill-zoom
    // for small interiors (would zoom to 6× inside Player's House and
    // 2× outside) which made the slider feel mode-specific. Tiny
    // interiors now render at the same scale; small interiors get black
    // margins where the room doesn't fill the viewport (applyCameraBounds
    // expands bounds so the player stays centered + fills off-map with
    // the camera's black bg — centered-composition framing vs cartridge's
    // edge-clamp on small maps).
    //
    // Portrait baseline 2× (matches the mega-map's natural fit on
    // ~414-px wide portrait phones). Landscape targets ~18 tiles
    // across at the canvas width (cartridge GBA's ~15-tile screen
    // with breathing room), floor for pixel-art crispness.
    let baseline: number;
    if (isPortrait) {
      baseline = 2;
    } else {
      baseline = Math.max(2, Math.floor(w / (18 * tileSize)));
    }
    // Player preference multiplier (TODOs #28 — camera-zoom slider).
    // Rounded to keep pixel-art rendering crisp; final zoom never
    // drops below 1× (a zoom of 0 would crash the camera).
    return applyZoomMultiplier(baseline, getZoomMultiplier());
  }

  /**
   * Public hook for SettingsScene's zoom slider. Persists the new
   * multiplier + re-applies via repositionAll() so the camera live-
   * updates without waiting for the next map transition. Called every
   * time the user moves the slider; idempotent.
   */
  applyCameraZoomPref(): void {
    this.repositionAll();
  }

  /**
   * Set camera bounds + background based on mode. Called on map load and
   * from repositionAll so resize / zoom-slider changes re-apply.
   *
   * - Exterior: clamp at mega-map edges (cartridge edge behavior — player
   *   walks off-center near unfinished edges). Camera bg transparent so
   *   the canvas bg shows through. Per-region environmental border tiles
   *   for unfinished edges tracked in TODOs #30 — deferred as self-
   *   deprecating work (every region stitched into the mega-map via
   *   TODOs #17 bucket 2 shrinks the off-map problem until it's gone).
   * - Interior: expand bounds beyond the map in any dimension where the
   *   map is smaller than the viewport so the camera always centers on
   *   the player (centered-composition framing vs cartridge's clamp-and-
   *   walk-off-center on small maps). Off-map area fills with black via
   *   the camera bg.
   */
  private applyCameraBounds(): void {
    const cam = this.cameras.main;
    if (this.mode === 'exterior' && this.world) {
      const worldPxW = this.world.worldWidth * this.world.tileSize;
      const worldPxH = this.world.worldHeight * this.world.tileSize;
      cam.setBounds(0, 0, worldPxW, worldPxH);
      cam.setBackgroundColor('rgba(0,0,0,0)');
    } else if (this.mapData) {
      const mapPxW = this.mapData.width * this.mapData.tileSize;
      const mapPxH = this.mapData.height * this.mapData.tileSize;
      const viewportPxW = this.scale.width / cam.zoom;
      const viewportPxH = this.scale.height / cam.zoom;
      const boundsW = Math.max(mapPxW, viewportPxW);
      const boundsH = Math.max(mapPxH, viewportPxH);
      const boundsX = (mapPxW - boundsW) / 2;
      const boundsY = (mapPxH - boundsH) / 2;
      cam.setBounds(boundsX, boundsY, boundsW, boundsH);
      cam.setBackgroundColor('#000000');
    }
  }

  private repositionAll(): void {
    if (!this.layer) return;
    this.cameras.main.setZoom(this.computeZoom());
    this.applyCameraBounds();
    if (this.uiCamera) this.uiCamera.setSize(this.scale.width, this.scale.height);
    this.repositionTouchControls();
  }

  private updateStatusText(): void {
    const idx = this.playerTileY * this.mapData.width + this.playerTileX;
    const c = this.mapData.collision[idx] ?? 0;
    const e = this.mapData.elevation[idx] ?? 0;
    this.statusText.setText(
      `${this.mapData.name}\n` +
        `tile (${this.playerTileX}, ${this.playerTileY})  collision ${c}  elevation ${e}`,
    );
  }

  private toggleCollisionOverlay(): void {
    if (this.collisionOverlay) {
      this.collisionOverlay.destroy();
      this.collisionOverlay = undefined;
      return;
    }
    // World-coord rects; camera zoom does the scaling for us.
    const container = this.add.container(0, 0).setDepth(4);
    for (let y = 0; y < this.mapData.height; y++) {
      for (let x = 0; x < this.mapData.width; x++) {
        const c = this.mapData.collision[y * this.mapData.width + x] ?? 0;
        if (c === 0) continue;
        const rect = this.add.rectangle(
          (x + 0.5) * this.mapData.tileSize,
          (y + 0.5) * this.mapData.tileSize,
          this.mapData.tileSize,
          this.mapData.tileSize,
          ERR_RED.hex,
          0.35,
        );
        container.add(rect);
      }
    }
    this.collisionOverlay = container;
    this.syncUiCameraIgnores();
  }
}
